import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Waves, Droplets, Gauge, Thermometer, Weight, CheckCircle2, AlertCircle,
  Info, ChevronRight, Save, RotateCcw, Microscope, FileText,
  TrendingUp, BarChart3, Zap, Cylinder, X,
  Factory, ShieldAlert, Shield, Beaker,
  ArrowDown, ArrowRight, ArrowRightLeft, Volume2, CircleDot, Timer, Filter,
  FlaskConical, Settings, Wind, FlaskConicalOff, TestTube, Cog,
  Database, Download, FlaskConical as FlaskIcon, Blend, Pipette
} from "lucide-react";
import { apiListInfluentSamples, apiSaveInfluentSample, apiSaveReport, hasPhpBackend, apiPumpModels } from '@/lib/phpApi';
import PageHeader from '@/components/PageHeader';
import { generateDAFReportPDF, buildRegulatoryReportNumber, type DAFReportParams, type DAFReportResult, type DAFReportOptions } from '@/lib/pdfGenerator';
import { useSession } from '@/context/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { getRecommendedPumps, getBestPump, verdictColors, type ScoredPump, type RawPumpModel } from '@/lib/pumpRecommendation';
import { getAllLocalSamples } from '@/lib/hybridStorage';
import { ALL_STANDARDS } from '@/types/standards';
import ValidationPrintReport from '@/components/ValidationPrintReport';
import type { ValidationSection } from '@/components/EquipmentAdminValidation';
import { InfluentIntelligencePanel } from '@/components/InfluentIntelligencePanel';
import RegulatoryNotice from '@/components/RegulatoryNotice';

/* ─── DAF Specification Page ───────────────────────────
   Dissolved Air Flotation — Full Physics/Chemistry Simulation
   per Reynolds Bauhm Smart Tool v2.0 Specification
   ──────────────────────────────────────────────────── */

export interface DAFParams {
  flowRate: number;       // m³/day (UI shows m³/h = flowRate/24)
  bod: number; cod: number; tss: number; og: number; ph: number; temp: number;
  alkalinity: number;     // mg/L as CaCO₃ — buffers pH drop from coagulant
  turbidity: number;      // NTU — correlates with TSS, affects removal efficiency
  conductivity: number;   // μS/cm — ionic strength, affects coagulant demand
  hardness: number;       // mg/L as CaCO₃ — affects coagulant demand & scaling
  nh3n: number;           // mg/L NH₃-N — nutrient loading indicator
  tp: number;             // mg/L total phosphorus — nutrient loading indicator
  // DAF Cell
  length: number; width: number; depth: number; freeboard: number;
  airPressure: number;    // bar (recycle pump head equiv)
  recycleRate: number;    // % of Q
  saturatorPressure: number; // bar
  saturatorEfficiency: number; // % (new per spec)
  // Flocculation Chamber
  flocTankVolume: number; // m³
  flocNumCompartments: number;
  flocRetentionTime: number; // minutes
  flocMotorPower: number;  // kW
  flocMotorSpeed: number;  // rpm
  flocImpellerDia: number; // m
  /** Impeller geometry determines Power Number (Np) used in P = Np·ρ·N³·D⁵ */
  flocImpellerType: 'paddle' | 'hydrofoil' | 'pitched' | 'rushton';
  // Dosing
  coagulantType: 'ferric' | 'alum' | 'pac' | 'none';
  coagulantDose: number;  // mg/L
  flocculantType: 'anionic' | 'cationic' | 'nonionic' | 'none';
  flocculantDose: number; // mg/L
  // Performance
  targetRemovalTSS: number; // %
  targetRemovalOG: number;  // %
  // Selected Pump
  selectedPumpId?: string;
  selectedPumpCode?: string;
  selectedPumpMaxFlowM3h?: number;
  selectedPumpMaxPressureBar?: number;
  selectedPumpMotorKw?: number;
}

export interface DAFSize {
  // Geometry & Hydraulics
  volume: number;         // m³ operating
  volumeWithFreeboard: number;
  surfaceArea: number;    // m²
  hlr: number;            // m³/m²·h
  slr: number;            // kg/m²·h
  olr: number;            // kg/m²·h
  actualRetention: number; // min
  // Removal
  tssRemoval: number;     // fraction 0-1
  ogRemoval: number;      // fraction 0-1
  removalEfficiency: number; // % (average)
  // Effluent
  effluentTSS: number;    // mg/L
  effluentOG: number;     // mg/L
  effluentPH: number;
  effluentBOD: number;    // mg/L
  effluentCOD: number;    // mg/L
  // Removal
  bodRemoval: number;     // fraction 0-1
  codRemoval: number;     // fraction 0-1
  // Air System
  airDemand: number;      // kg/day
  airToSolids: number;    // A/S ratio
  airSolubility: number;   // mg/L
  bubbleSize: number;      // μm
  bubbleRise: number;      // m/h
  bubbleCount: number;     // for visualisation
  // Flocculation
  flocRetentionTime: number; // min
  flocGValue: number;      // s⁻¹
  flocGt: number;          // dimensionless G×t (Camp criterion)
  flocVelocityGradient: number;
  flocPower: number;       // kW total
  flocPerCompartmentVol: number;
  flocMixerSpeedActual: number;
  flocTipSpeed: number;    // m/s
  flocMotorTorque: number; // Nm
  flocReynolds: number;    // impeller Re = ρ·N·D²/μ
  flocRegime: 'turbulent' | 'transitional' | 'laminar';
  flocNpUsed: number;      // actual Np applied (regime-adjusted)
  flocEffFactor: number;   // 0-1 efficiency modifier from G/Gt quality
  // Sludge
  sludgeTSS: number;      // kg/day
  sludgeOG: number;       // kg/day
  sludgeBOD: number;      // kg/day
  sludgeCOD: number;      // kg/day
  sludgeChem: number;     // kg/day (precipitate)
  sludgeTotal: number;    // kg/day
  sludgeVolume: number;   // m³/day
  blanketThickness: number; // mm
  blanketDensity: string;  // Light / Normal / Thick
  // Scraper System
  floatDS_pct: number;          // % dry solids in float
  floatDensity_kg_m3: number;   // kg/m³ (water - trapped air)
  floatVol_m3_h: number;        // m³/h wet float produced
  blanketAccumRate_mm_h: number; // mm/h float blanket rise
  scraperCount: number;         // number of scraper flights on chain
  scraperSpeedMpm: number;      // m/min travel speed
  scraperPassesPerHour: number; // full-length passes per hour
  scraperFrequencyMin: number;  // minutes between complete passes
  operatingBlanket_mm: number;  // steady-state blanket depth at scraper balance
  scraperBladeDepth_mm: number; // blade immersion depth (mm)
  scraperMotorPower_kW: number; // calculated motor power (kW)
  scraperStatus: 'optimal' | 'adequate' | 'marginal' | 'overloaded';
  scraperStatusDetail: string;
  // Dosing & Chemistry
  coagulantDemand: number;       // kg/day
  flocculantDemand: number;      // kg/day
  phDrop: number;
  coagPrecipitate: number;       // kg/day
  alkalinityConsumed: number;    // mg/L as CaCO₃ consumed by coagulant hydrolysis
  residualAlkalinity: number;    // mg/L as CaCO₃ after coagulation
  polymerChargeMatch: 'optimal' | 'good' | 'acceptable' | 'poor';
  polymerChargeNote: string;     // human-readable explanation
  // Water chemistry factors
  conductivityFactor: number;
  hardnessFactor: number;
  // Power
  pumpPower: number;      // kW
  compressorPower: number;// kW
  mixerPower: number;      // kW
  power: number;          // kW total
  // Status
  status: 'optimal' | 'acceptable' | 'undersized' | 'oversized' | 'overloaded';
  statusDetail: string;
  // Warnings
  asStatus: 'poor' | 'marginal' | 'optimal' | 'excessive';
  designNotes: string[];
}

const PROFILES = [
  { name: 'Dairy Effluent', description: 'High organic, moderate FOG, neutral pH', icon: Factory,
    defaults: { flowRate: 250, bod: 1850, cod: 3200, tss: 450, og: 120, ph: 6.8, temp: 25,
      alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      length: 6, width: 3, depth: 2.5, freeboard: 0.5,
      airPressure: 5.5, recycleRate: 30, saturatorPressure: 5, saturatorEfficiency: 85,
      flocTankVolume: 12, flocNumCompartments: 3, flocRetentionTime: 10,
      flocMotorPower: 3, flocMotorSpeed: 40, flocImpellerDia: 0.8, flocImpellerType: 'paddle' as const,
      coagulantType: 'ferric' as const, coagulantDose: 15,
      flocculantType: 'anionic' as const, flocculantDose: 0.5,
      targetRemovalTSS: 85, targetRemovalOG: 90 } },
  { name: 'Brewery Effluent', description: 'Very high COD/BOD, acidic, high TSS', icon: Factory,
    defaults: { flowRate: 350, bod: 2200, cod: 4100, tss: 620, og: 180, ph: 5.2, temp: 20,
      alkalinity: 80, turbidity: 550, conductivity: 4200, hardness: 350, nh3n: 85, tp: 18,
      length: 8, width: 4, depth: 3, freeboard: 0.5,
      airPressure: 6, recycleRate: 30, saturatorPressure: 5, saturatorEfficiency: 85,
      flocTankVolume: 18, flocNumCompartments: 3, flocRetentionTime: 10,
      flocMotorPower: 4, flocMotorSpeed: 35, flocImpellerDia: 1.0, flocImpellerType: 'paddle' as const,
      coagulantType: 'alum' as const, coagulantDose: 15,
      flocculantType: 'anionic' as const, flocculantDose: 0.5,
      targetRemovalTSS: 80, targetRemovalOG: 85 } },
  { name: 'Fish Processing', description: 'High TSS with blood and oil, moderate COD', icon: Factory,
    defaults: { flowRate: 150, bod: 1450, cod: 2650, tss: 520, og: 220, ph: 6.9, temp: 15,
      alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8,
      length: 5, width: 2.5, depth: 2.2, freeboard: 0.5,
      airPressure: 5, recycleRate: 30, saturatorPressure: 5, saturatorEfficiency: 85,
      flocTankVolume: 8, flocNumCompartments: 3, flocRetentionTime: 10,
      flocMotorPower: 2, flocMotorSpeed: 45, flocImpellerDia: 0.7, flocImpellerType: 'paddle' as const,
      coagulantType: 'ferric' as const, coagulantDose: 15,
      flocculantType: 'anionic' as const, flocculantDose: 0.5,
      targetRemovalTSS: 82, targetRemovalOG: 92 } },
];

export function calcDAF(p: DAFParams): DAFSize {
  const surfaceArea = p.length * p.width;
  const volume = surfaceArea * p.depth;
  const volumeWithFreeboard = surfaceArea * (p.depth + p.freeboard);

  const Q = p.flowRate / 24; // m³/h (per spec)
  const hlr = Q / surfaceArea; // m³/m²·h

  // ─── Water properties ───
  const T = p.temp;
  const tempK = T + 273.15;
  // Dynamic viscosity: μ = 0.001 × exp[-1.5 × (T-20)/(T+273.15)]  (Pa·s)
  const mu = 0.001 * Math.exp(-1.5 * (T - 20) / tempK);
  // Water density: ρ_w = 998 - 0.2 × (T - 20)  (kg/m³)
  const rhoWater = 998 - 0.2 * (T - 20);

  // ─── Input Validation ───
  const validationNotes: string[] = [];
  if (p.ph < 4.5 || p.ph > 9.5) validationNotes.push('⚠️ pH outside recommended range (4.5–9.5) for DAF operation.');
  if (p.temp < 5 || p.temp > 35) validationNotes.push('⚠️ Temperature outside range (5–35°C). Viscosity and bubble dynamics may be inaccurate.');
  if (p.bod / Math.max(p.cod, 1) > 0.85) validationNotes.push('⚠️ BOD/COD ratio >0.85 — unusual for wastewater. Check data accuracy.');
  if (p.bod / Math.max(p.cod, 1) < 0.15) validationNotes.push('ℹ️ BOD/COD ratio <0.15 — indicates highly recalcitrant or industrial effluent.');
  if (p.tss > 3000) validationNotes.push('⚠️ Extremely high TSS (>3000 mg/L). DAF may be overloaded — consider pre-screening.');
  if (p.og > 1000) validationNotes.push('⚠️ Very high O&G (>1000 mg/L). Risk of emulsion formation — may need pre-treatment.');
  if (p.alkalinity < 50 && p.coagulantDose > 10) validationNotes.push('⚠️ Low alkalinity with high coagulant dose — severe pH drop expected. Add alkali.');
  if (p.conductivity > 8000) validationNotes.push('ℹ️ High conductivity — ionic strength may reduce coagulant efficiency.');
  if (p.hardness > 500) validationNotes.push('ℹ️ High hardness (>500 mg/L CaCO₃) — scaling risk in saturator and pipework.');
  if (p.nh3n > 100) validationNotes.push('⚠️ Very high NH₃-N (>100 mg/L) — nutrient loading may require biological post-treatment.');
  if (p.tp > 20) validationNotes.push('ℹ️ High total phosphorus (>20 mg/L) — enhanced coagulation or tertiary P-removal may be needed.');

  // ─── Conductivity & Hardness Effects on Coagulation ───
  // High conductivity = high ionic strength → shields colloid charges → reduces coagulant efficiency
  // Formula: factor = 1 − 0.03 × log10(conductivity/1000) for conductivity > 1000
  let conductivityFactor = 1.0;
  if (p.conductivity > 1000) {
    conductivityFactor = Math.max(0.80, 1.0 - 0.03 * Math.log10(p.conductivity / 1000));
  }
  // Hardness (Ca²⁺/Mg²⁺) aids sweep floc coagulation; too low = poor floc formation
  // Formula: factor = 0.95 + 0.05 × min(hardness/300, 1) for hardness < 50, factor = 0.95
  let hardnessFactor = 1.0;
  if (p.hardness < 50) hardnessFactor = 0.95;
  else if (p.hardness > 300) hardnessFactor = Math.min(1.08, 1.0 + 0.05 * ((p.hardness - 300) / 200));

  // ─── Coagulant Chemistry (per spec §2.2) ───
  let phDrop = 0;
  let precipitateRatio = 0;
  let coagBoost = 0;
  let hydrolysisEq = '';
  let basePhDropFactor = 0;

  if (p.coagulantType === 'ferric') {
    basePhDropFactor = 0.001;
    precipitateRatio = 2.7;
    coagBoost = p.coagulantDose > 0 ? 0.30 : 0;
    hydrolysisEq = 'FeCl₃ + 3H₂O → Fe(OH)₃↓ + 3HCl';
  } else if (p.coagulantType === 'alum') {
    basePhDropFactor = 0.0006;
    precipitateRatio = 2.9;
    coagBoost = p.coagulantDose > 0 ? 0.20 : 0;
    hydrolysisEq = 'Al³⁺ + 3H₂O → Al(OH)₃↓ + 3H⁺';
  } else if (p.coagulantType === 'pac') {
    basePhDropFactor = 0.0002;
    precipitateRatio = 1.5;
    coagBoost = p.coagulantDose > 0 ? 0.15 : 0;
    hydrolysisEq = 'PAC → Al(OH)₃↓ (pre-hydrolysed)';
  }

  // pH drop is buffered by alkalinity: higher alkalinity = less pH drop
  // Formula: actualDrop = (dose × baseFactor) / (1 + alkalinity/100)
  phDrop = p.coagulantDose > 0 ? (p.coagulantDose * basePhDropFactor) / (1 + p.alkalinity / 100) : 0;
  const effluentPH = Math.max(4.0, p.ph - phDrop);
  // Apply conductivity/hardness factors to coagulation boost
  const adjustedCoagBoost = coagBoost * conductivityFactor * hardnessFactor;

  // Precipitate mass: m_precip = coagDose × ratio (mg/L as M(OH)n)
  const m_precip_mg_L = p.coagulantDose * precipitateRatio;
  // Sludge precipitate: kg/day = m_precip × Q × 24 / 1,000,000
  const coagPrecipitate = (m_precip_mg_L * p.flowRate) / 1000; // kg/day

  // ─── Air System — Henry's Law + van't Hoff (§2.3) ───
  // Issue fix: UI shows GAUGE pressure. Henry's law requires ABSOLUTE pressure.
  //   P_abs = P_gauge + P_atm_bar  (1.013 bar at sea level)
  // Reference: WEF MOP 8 §6.4; Metcalf & Eddy (2014) §7.2.2
  const C_sat_20 = 25; // mg/L at 20°C, 1.013 bar absolute
  // Temperature correction: van't Hoff
  const C_sat_T = C_sat_20 * Math.exp(-1300 * (1/tempK - 1/293.15));
  // Pressure correction — use absolute pressure for Henry's law
  const P_atm_bar = 1.013; // bar
  const P_abs_sat = p.saturatorPressure + P_atm_bar; // bar absolute
  const C_sat_P = C_sat_T * P_abs_sat;
  // Actual dissolved air (accounting for saturator efficiency)
  const eta_sat = p.saturatorEfficiency / 100;
  const C_air = C_sat_P * eta_sat; // mg/L
  // Air mass in recycle stream
  const R = p.recycleRate / 100;
  const Q_recycle = Q * R; // m³/h
  const airMass_kg_h = (C_air * Q_recycle) / 1000; // kg/h
  const airDemand = airMass_kg_h * 24; // kg/day
  // A/S Ratio
  const TSSmass = (p.tss * Q) / 1000; // kg/h (inlet solids loading)
  const A_S = TSSmass > 0 ? airMass_kg_h / TSSmass : 0;
  // A/S status
  let asStatus: DAFSize['asStatus'];
  if (A_S < 0.02) asStatus = 'poor';
  else if (A_S < 0.03) asStatus = 'marginal';
  else if (A_S <= 0.08) asStatus = 'optimal';
  else asStatus = 'excessive';

  // ─── Bubble Physics (§2.4) ───
  const P_sat = p.saturatorPressure; // bar gauge (used for empirical bubble-size formula only)
  // d_bubble = 80 - 8 × (P_gauge - 4), clamp 20-100 μm
  // Empirical correlation calibrated against gauge pressure (van't Hoff 1989; Edzwald 1995)
  let bubbleSize = 80 - 8 * (P_sat - 4);
  bubbleSize = Math.max(20, Math.min(100, bubbleSize));
  const d_bubble_m = bubbleSize * 1e-6; // m
  // Air density at ABSOLUTE pressure: ρ_a = 1.2 × (P_abs / 1 bar_abs) × (293 / T_K)
  // Ideal gas law: ρ = ρ_std × P_abs / P_std × T_std / T
  const rhoAir = 1.2 * P_abs_sat * (293 / tempK);
  // Stokes' rise velocity: v_Stokes = g(ρ_w − ρ_a)d² / 18μ
  const bubbleRise_ms_stokes = (9.81 * (rhoWater - rhoAir) * d_bubble_m * d_bubble_m) / (18 * mu);
  // Hadamard–Rybczynski correction for fluid spheres (μ_air → 0):
  //   v_HR = v_Stokes × 3/2 (clean water). In wastewater, surfactants partially
  //   immobilise the gas–liquid interface, so a conservative factor of 1.20 is applied.
  //   Reference: Hadamard (1911); Rybczynski (1911); Clift et al. "Bubbles Drops & Particles" (1978)
  const bubbleRise = bubbleRise_ms_stokes * 1.20 * 3600; // m/h (H-R corrected)
  // Bubble count for visualisation
  const bubbleCount = Math.max(8, Math.min(20, Math.round(A_S * 200)));

  // ─── Flocculation — Camp-Stein with Regime-Dependent Power Number (§2.5) ───
  //
  // Power number (Np) is impeller-type specific and regime-dependent.
  // Reference: Uhl & Gray (1966); Oldshue (1983); Metcalf & Eddy (2014)
  //
  // Turbulent Np (Re > 10,000):
  //   paddle (2-blade flat)   : Np_turb = 1.70, Kp_lam = 36.5
  //   hydrofoil (A310-type)   : Np_turb = 0.31, Kp_lam = 41.0
  //   pitched-blade 4×45°    : Np_turb = 1.27, Kp_lam = 44.0
  //   Rushton 6-blade disc    : Np_turb = 5.90, Kp_lam = 71.3
  //
  // Regime boundaries (impeller Re = ρ·N·D²/μ):
  //   Re > 10,000             : turbulent  → Np = Np_turb
  //   10 < Re ≤ 10,000        : transitional → Np = Np_turb × (10000/Re)^0.14
  //   Re ≤ 10                 : laminar    → Np = Kp_lam / Re

  const t_floc = p.flocRetentionTime; // min (user-set target)
  const n_floc = p.flocNumCompartments;
  const D_imp  = p.flocImpellerDia > 0 ? p.flocImpellerDia : 0.3;
  const N_rps  = p.flocMotorSpeed / 60; // rev/s

  // Impeller type → turbulent Np and laminar Kp
  type ImpType = 'paddle' | 'hydrofoil' | 'pitched' | 'rushton';
  const IMP_DATA: Record<ImpType, { Np_turb: number; Kp_lam: number; label: string }> = {
    paddle:    { Np_turb: 1.70, Kp_lam: 36.5, label: 'Flat-blade paddle' },
    hydrofoil: { Np_turb: 0.31, Kp_lam: 41.0, label: 'Hydrofoil (A310)' },
    pitched:   { Np_turb: 1.27, Kp_lam: 44.0, label: 'Pitched-blade 45°' },
    rushton:   { Np_turb: 5.90, Kp_lam: 71.3, label: 'Rushton disc turbine' },
  };
  const impType = (p.flocImpellerType || 'paddle') as ImpType;
  const { Np_turb, Kp_lam } = IMP_DATA[impType];

  // Impeller Reynolds number: Re = ρ · N · D² / μ
  const Re_imp = N_rps > 0 ? (rhoWater * N_rps * Math.pow(D_imp, 2)) / mu : 0;

  // Regime-dependent power number
  let flocRegime: 'turbulent' | 'transitional' | 'laminar';
  let Np_eff: number;
  if (Re_imp > 10000) {
    flocRegime = 'turbulent';
    Np_eff = Np_turb;
  } else if (Re_imp > 10) {
    flocRegime = 'transitional';
    // Interpolate in log-space: Np drops from Np_turb toward Np_lam as Re falls
    Np_eff = Np_turb * Math.pow(10000 / Re_imp, 0.14);
  } else if (Re_imp > 0) {
    flocRegime = 'laminar';
    Np_eff = Kp_lam / Re_imp;
  } else {
    flocRegime = 'laminar';
    Np_eff = Np_turb;
  }

  // Shaft power: P = Np · ρ · N³ · D⁵   (Watts)
  const P_floc_W = Np_eff * rhoWater * Math.pow(N_rps, 3) * Math.pow(D_imp, 5);

  // Camp-Stein velocity gradient: G = √(P / μV)   (s⁻¹)
  // V = total flocculation tank volume (m³)
  const G = P_floc_W > 0 ? Math.sqrt(P_floc_W / (mu * p.flocTankVolume)) : 0;

  // Actual hydraulic retention time (may differ from target if tank is over/under-sized)
  const flocRetentionTime = (p.flocTankVolume / Q) * 60; // min

  // Dimensionless G×t product — Camp's flocculation criterion
  // Optimal for pre-DAF flocculation: Gt = 20,000–80,000
  const Gt = G * flocRetentionTime * 60; // dimensionless (G in s⁻¹, t in s)

  // Volume per compartment
  const V_floc = p.flocTankVolume / Math.max(n_floc, 1); // m³ each

  // Tip speed: v_tip = π · D · N
  const tipSpeed_ms = Math.PI * D_imp * N_rps; // m/s

  // Motor torque: τ = P / ω
  const omega = 2 * Math.PI * N_rps;
  const flocMotorTorque = P_floc_W / Math.max(omega, 0.001); // Nm

  // ─── Floc Efficiency Factor from G / Gt (§2.5a) ───
  // Couples mixing quality directly into the removal efficiency model.
  // Source: Camp & Stein (1943); WEF MOP 8 (2010)
  let flocEffFactor: number;
  if (G >= 20 && G <= 75 && Gt >= 20000 && Gt <= 80000) {
    flocEffFactor = 1.00; // optimal — gentle turbulence, well-formed floc
  } else if (G > 100 || Gt > 150000) {
    flocEffFactor = 0.82; // over-mixed — floc shear, micro-floc doesn't float well
  } else if (G < 10 || Gt < 5000) {
    flocEffFactor = 0.75; // under-mixed — colloids not destabilised, floc doesn't form
  } else if ((G >= 10 && G < 20) || (Gt >= 5000 && Gt < 20000)) {
    flocEffFactor = 0.90; // slightly under-mixed — acceptable for robust influents
  } else if ((G > 75 && G <= 100) || (Gt > 80000 && Gt <= 150000)) {
    flocEffFactor = 0.93; // slightly over-mixed — minor floc breakup
  } else {
    flocEffFactor = 0.95;
  }

  // ─── Turbidity factor ───
  // High turbidity (>500 NTU) indicates fine colloidal particles that are harder to remove
  const turbidityFactor = p.turbidity > 500 ? 0.92 : p.turbidity > 300 ? 0.96 : 1.00;

  // ─── Removal Efficiency — Multi-Factor Model (§2.6) ───
  const base_TSS = 0.45;
  const base_OG = 0.65;

  // Polymer boost
  const polyDose = p.flocculantDose;
  const polyBoost_TSS = Math.min(polyDose * 0.12, 0.25);
  const polyBoost_OG = Math.min(polyDose * 0.20, 0.40);

  // A/S factor
  let asFactor: number;
  if (A_S < 0.02) asFactor = 0.70;
  else if (A_S < 0.03) asFactor = 0.85;
  else if (A_S <= 0.08) asFactor = 1.00;
  else asFactor = 0.95;

  // Bubble size factor
  let bubbleFactor: number;
  if (bubbleSize < 30) bubbleFactor = 1.15;
  else if (bubbleSize > 80) bubbleFactor = 0.90;
  else bubbleFactor = 1.00;

  // HLR factor
  const HLR = hlr;
  let hlrFactor: number;
  if (HLR < 3) hlrFactor = 1.05;
  else if (HLR > 12) hlrFactor = 0.70;
  else if (HLR > 8) hlrFactor = 0.90;
  else hlrFactor = 1.00;

  // Retention time factor
  const t_ret = (surfaceArea * 1.5) / Q; // h, using h_sep = 1.5m
  let retFactor: number;
  if (t_ret < 0.25) retFactor = 0.80;
  else if (t_ret > 1.0) retFactor = 1.05;
  else retFactor = 0.90 + 0.15 * t_ret;

  // Final removal — flocEffFactor couples G/Gt mixing quality into removal.
  // IMPORTANT: conductivityFactor/hardnessFactor are already baked into adjustedCoagBoost —
  // multiplying by them again here would square the chemistry penalty (e.g. 0.87→0.76 at
  // conductivity=8000). Remove from eff_TSS outer product. eff_OG has no adjusted boost so
  // single application (outer only) remains correct.
  let eff_TSS = Math.min(base_TSS + adjustedCoagBoost + polyBoost_TSS, 0.90) * asFactor * bubbleFactor * hlrFactor * retFactor * turbidityFactor * flocEffFactor;
  let eff_OG  = Math.min(base_OG  + polyBoost_OG,                     0.92) * asFactor * bubbleFactor * hlrFactor * retFactor * turbidityFactor * conductivityFactor * hardnessFactor * flocEffFactor;
  // Issue fix: realistic DAF caps. Even best-case DAF rarely exceeds 95% TSS or 98% O&G.
  // Over-prediction causes sludge underestimates and overly optimistic effluent quality.
  eff_TSS = Math.max(0.05, Math.min(0.95, eff_TSS));
  eff_OG  = Math.max(0.10, Math.min(0.98, eff_OG));

  const effluentTSS = p.tss * (1 - eff_TSS);
  const effluentOG = p.og * (1 - eff_OG);

  // ─── BOD / COD Removal (§2.6 extended) ───
  // DAF removes particle-associated and colloidal organics via coagulation
  // BOD is less removed than COD because soluble BOD passes through
  const base_BOD = 0.25;
  const base_COD = 0.35;
  const coagBoost_BOD = Math.min(p.coagulantDose * 0.004, 0.20);
  const coagBoost_COD = Math.min(p.coagulantDose * 0.005, 0.25);
  const polyBoost_BOD = Math.min(polyDose * 0.06, 0.15);
  const polyBoost_COD = Math.min(polyDose * 0.08, 0.20);

  // conductivityFactor/hardnessFactor already applied inside coagBoost_BOD/COD term —
  // remove from the outer product to avoid squaring the chemistry penalty.
  let eff_BOD = Math.min(base_BOD + coagBoost_BOD * conductivityFactor * hardnessFactor + polyBoost_BOD, 0.90) * asFactor * bubbleFactor * hlrFactor * retFactor * flocEffFactor;
  let eff_COD = Math.min(base_COD + coagBoost_COD * conductivityFactor * hardnessFactor + polyBoost_COD, 0.90) * asFactor * bubbleFactor * hlrFactor * retFactor * flocEffFactor;
  eff_BOD = Math.max(0.05, Math.min(0.90, eff_BOD));
  eff_COD = Math.max(0.10, Math.min(0.90, eff_COD));

  const effluentBOD = p.bod * (1 - eff_BOD);
  const effluentCOD = p.cod * (1 - eff_COD);
  const removalEfficiency = ((eff_TSS + eff_OG + eff_BOD + eff_COD) / 4) * 100;

  // ─── Power Calculations (§2.7) ───
  // Recycle pump: pump overcomes GAUGE pressure (net head above atmospheric).
  //   H = P_gauge × (1/ρg) = P_gauge_Pa / (rho × 9.81) — exact conversion
  //   1 bar gauge = 100,000 Pa → H = 100000 / (rhoWater × 9.81) ≈ 10.197 m/bar
  const H_pump = P_sat * (100000 / (rhoWater * 9.81)); // m (exact: ~10.197 m/bar)
  const eta_pump = 0.75;
  const pumpPower = (rhoWater * 9.81 * (Q_recycle / 3600) * H_pump) / eta_pump / 1000; // kW
  // Air compressor (isothermal): W = Q × P_atm_abs × ln(P_abs / P_atm_abs) / η_comp
  //   Must use ABSOLUTE pressures in the compression ratio — ratio of absolute pressures.
  //   P_abs = P_gauge + P_atm; Reference: Çengel & Boles "Thermodynamics" (2014) §9.9
  const eta_comp = 0.65;
  const compressorPower = ((Q_recycle / 3600) * P_atm_bar * 100000 * Math.log(P_abs_sat / P_atm_bar)) / eta_comp / 1000; // kW
  // Flocculation mixer
  const mixerPower = P_floc_W / 1000; // kW
  const power = pumpPower + compressorPower + mixerPower;

  // ─── Sludge Production & Blanket (§2.8) ───
  const sludgeTSS = (p.tss * eff_TSS * p.flowRate) / 1000; // kg/day
  const sludgeOG = (p.og * eff_OG * p.flowRate) / 1000; // kg/day
  const sludgeBOD = (p.bod * eff_BOD * p.flowRate) / 1000; // kg/day
  const sludgeCOD = (p.cod * eff_COD * p.flowRate) / 1000; // kg/day
  const sludgeChem = coagPrecipitate; // kg/day (from coagulant hydrolysis)
  // Issue fix: BOD and COD are oxygen-demand parameters, not physical solids masses.
  // Including them in sludge total overpredicts floated solids volume.
  // Sludge = captured physical solids only: TSS + floated O&G + coagulant precipitate.
  // sludgeBOD and sludgeCOD are kept for effluent balance but excluded from sludge mass.
  const sludgeTotal = sludgeTSS + sludgeOG + sludgeChem;
  // Wet sludge volume at 3% DS
  const sludgeVolume = sludgeTotal / 30; // m³/day (3% DS ≈ 30 kg solids/m³ wet sludge)
  // Blanket thickness
  const DAF_area = surfaceArea;
  const blanketThickness_mm = (sludgeVolume / DAF_area / 24) * 1000 * 2; // mm
  let blanketDensity: string;
  if (blanketThickness_mm < 80) blanketDensity = 'Light';
  else if (blanketThickness_mm < 200) blanketDensity = 'Normal';
  else blanketDensity = 'Thick — increase scraper speed';

  // ─── Dosing Mass Balance (§2.8a) ───
  const coagulantDemand  = (p.flowRate * p.coagulantDose)  / 1000; // kg/day
  const flocculantDemand = (p.flowRate * p.flocculantDose) / 1000; // kg/day

  // ─── Alkalinity Consumption by Coagulant Hydrolysis (§2.8b) ───
  // Stoichiometry (mg CaCO₃ consumed per mg coagulant added):
  //   FeCl₃:  FeCl₃ + 3H₂O → Fe(OH)₃↓ + 3HCl
  //           MW(FeCl₃)=162.2; 3×50 mg CaCO₃ per mol → factor = 150/162.2 = 0.925
  //           (Note: commercial 40% FeCl₃ solution; factor applied to dose as FeCl₃ equiv.)
  //   Alum:   Al₂(SO₄)₃·18H₂O + 6H₂O → 2Al(OH)₃↓ + 3H₂SO₄
  //           MW=666.4; 6×50 mg CaCO₃ per mol → factor = 300/666.4 = 0.450
  //   PAC:    Pre-hydrolysed; lower H⁺ release → factor ≈ 0.20 (empirical)
  //   none:   0
  const ALK_FACTORS: Record<string, number> = {
    ferric: 0.925, alum: 0.450, pac: 0.200, none: 0,
  };
  const alkConsumedFactor = ALK_FACTORS[p.coagulantType] ?? 0;
  const alkalinityConsumed = p.coagulantDose * alkConsumedFactor; // mg/L as CaCO₃
  const residualAlkalinity = Math.max(0, p.alkalinity - alkalinityConsumed);

  // ─── Polymer Charge Compatibility (§2.8c) ───
  // Metal coagulants (ferric/alum/PAC) form positively-charged hydroxide floc.
  // Anionic polyelectrolyte bridges +ve floc → OPTIMAL (charge-patch + bridging).
  // Cationic polymer adds more +ve charge → repulsion with metal hydroxide → POOR.
  // Without coagulant: cationic polymer directly neutralises -ve colloids → GOOD.
  // Reference: Bratby (2006) "Coagulation and Flocculation in Water Treatment"
  let polymerChargeMatch: 'optimal' | 'good' | 'acceptable' | 'poor';
  let polymerChargeNote: string;
  const hasCoag = p.coagulantType !== 'none' && p.coagulantDose > 0;
  const hasPoly = p.flocculantType !== 'none' && p.flocculantDose > 0;

  if (!hasPoly) {
    polymerChargeMatch = 'acceptable';
    polymerChargeNote = 'No polymer. Coagulant-only system — adequate for most industrial effluents above 300 mg/L TSS.';
  } else if (hasCoag && p.flocculantType === 'anionic') {
    polymerChargeMatch = 'optimal';
    polymerChargeNote = `Anionic polymer + ${p.coagulantType} coagulant: positively-charged metal hydroxide floc bridges with anionic polyelectrolyte chain (charge-patch + bridging mechanism). Maximum removal efficiency.`;
  } else if (hasCoag && p.flocculantType === 'nonionic') {
    polymerChargeMatch = 'acceptable';
    polymerChargeNote = `Nonionic polymer + ${p.coagulantType}: steric bridging only, no charge interaction. Functional but 10–20% less effective than anionic pairing.`;
  } else if (hasCoag && p.flocculantType === 'cationic') {
    polymerChargeMatch = 'poor';
    polymerChargeNote = `⚠ Cationic polymer + ${p.coagulantType} coagulant: both positively charged — electrostatic repulsion between cationic polymer and metal hydroxide floc. Switch to anionic polymer for this coagulant type.`;
  } else if (!hasCoag && p.flocculantType === 'cationic') {
    polymerChargeMatch = 'good';
    polymerChargeNote = 'Cationic polymer (no mineral coagulant): directly neutralises negatively-charged colloids. Effective for low-TSS emulsified streams.';
  } else if (!hasCoag && p.flocculantType === 'anionic') {
    polymerChargeMatch = 'poor';
    polymerChargeNote = '⚠ Anionic polymer without coagulant: both anionic — electrostatic repulsion prevents attachment to negatively-charged colloids. Add cationic coagulant or switch polymer type.';
  } else {
    polymerChargeMatch = 'acceptable';
    polymerChargeNote = 'Nonionic polymer without mineral coagulant — limited bridging efficiency. Add coagulant for improved performance.';
  }

  // ─── Scraper Design (§2.9) ─────────────────────────────────────────────
  // Physics-based chain-and-flight scraper sizing for float blanket management.
  // Reference: WEF MOP 8 (2010), Metcalf & Eddy (2014), Bratby (2006)

  // Float dry solids content (%)
  //   Base 3% DS is standard for DAF float (water industry practice)
  //   Coagulant: metal-hydroxide precipitate binds water → denser mat
  //   Polymer: gel network reduces water drainage → slightly higher DS
  //   HLR: high HLR = shorter float residence → less compaction → wetter
  const baseDS_pct = 3.0;
  const f_coag_ds  = 1.0 + Math.min(0.30, p.coagulantDose / 100);   // 0–30% richer
  const f_poly_ds  = 1.0 + Math.min(0.40, p.flocculantDose * 0.08); // 0–40% gel effect
  const f_hlr_ds   = Math.max(0.80, 1.0 - (hlr - 4.0) / 20.0);     // compaction loss at high HLR
  const floatDS_pct = Math.max(1.5, Math.min(8.0, baseDS_pct * f_coag_ds * f_poly_ds * f_hlr_ds));

  // Float density (kg/m³) — micro-bubbles trapped in the float reduce bulk density
  // Air void fraction: rises with A/S ratio (more air = more trapped bubbles)
  const phi_air_float = Math.min(0.15, 0.05 + A_S * 0.5);
  const floatDensity_kg_m3 = Math.round(rhoWater * (1 - phi_air_float) + rhoAir * phi_air_float);

  // Wet float volume produced per hour (m³/h)
  //   Total captured solids mass (kg/h) → wet float @ floatDS%
  const capturedMass_kg_h = (sludgeTSS + sludgeOG + sludgeChem) / 24; // daily → hourly
  const floatVol_m3_h = capturedMass_kg_h / (floatDS_pct / 100) / floatDensity_kg_m3;

  // Blanket accumulation rate (mm/h) — vertical float layer build-up rate
  const blanketAccumRate_mm_h = (floatVol_m3_h / surfaceArea) * 1000;

  // Scraper count — number of blade flights on the chain-and-flight scraper
  // Flights are spaced at ~1.5 m intervals along the DAF length to ensure
  // continuous float clearance with no dead zones. Minimum 2 for any DAF.
  const scraperCount = Math.max(2, Math.ceil(p.length / 1.5));

  // Scraper travel speed (m/min) — physics-constrained 0.15–0.60 m/min
  //   Base speed 0.20 m/min (industry standard for stable operation)
  //   + A/S boost: higher air content means float is lighter & easier to push
  //   + Accumulation boost: faster build-up demands faster clearing
  //   + O&G boost: greasy/oily float requires faster mechanical clearing
  const speedBoost_AS    = Math.min(0.15, A_S * 1.5);
  const speedBoost_accum = Math.min(0.20, blanketAccumRate_mm_h / 30.0);
  const speedBoost_og    = Math.min(0.10, p.og / 2000.0);
  const scraperSpeedMpm  = Math.max(0.15, Math.min(0.60,
    0.20 + speedBoost_AS + speedBoost_accum + speedBoost_og
  ));

  // Passes per hour = (speed m/min × 60 min/h) / DAF length
  const scraperPassesPerHour = (scraperSpeedMpm * 60) / Math.max(p.length, 0.1);

  // Scraper frequency (min between complete full-length passes)
  const scraperFrequencyMin = 60.0 / scraperPassesPerHour;

  // Operating (steady-state) blanket thickness (mm)
  // At steady state, removal rate = accumulation rate:
  //   accum_rate [mm/h] = passes_h × eta_scrape × operating_blanket [mm]
  //   → operating_blanket = accum_rate / (passes_h × eta_scrape)
  // eta_scrape = 0.87: fraction of standing float cleared per scraper pass
  //   (validated against van Dijk & Wilms, 1991; accounts for skip zones at wall)
  const eta_scrape = 0.87;
  const operatingBlanket_mm = blanketAccumRate_mm_h / Math.max(scraperPassesPerHour * eta_scrape, 0.001);

  // Scraper blade immersion depth (mm)
  // Blade must reach below the operating blanket with a clearance margin.
  // Rule: blade_depth = operating_blanket × 2.0 + 25 mm clearance
  // Clamped: 50–250 mm (practical fabrication & structural range)
  const scraperBladeDepth_mm = Math.min(250, Math.max(50,
    operatingBlanket_mm * 2.0 + 25
  ));

  // Scraper motor power (kW) — drag force model
  // F_drag = C_D × ρ_float × v² × A_blade_per_flight × n_flights
  //   A_blade = blade_depth × width of DAF (full-width blade)
  //   C_D ≈ 1.8 for flat plate (Hoerner 1965)
  //   F_total × 1.50 for chain tension, rail friction, start-up torque
  //   η_chain = 0.65 (chain gearbox drive efficiency)
  const v_scraper_ms    = scraperSpeedMpm / 60.0; // m/s
  const A_blade_m2      = (scraperBladeDepth_mm / 1000.0) * p.width;
  const C_D_scraper     = 1.8;
  const F_drag_N        = C_D_scraper * floatDensity_kg_m3 * v_scraper_ms * v_scraper_ms * A_blade_m2 * scraperCount;
  const F_total_N       = F_drag_N * 1.50;
  const chainEff        = 0.65;
  const scraperMotorPower_kW = Math.max(0.18, (F_total_N * v_scraper_ms) / chainEff / 1000.0);

  // Scraper status classification
  let scraperStatus: DAFSize['scraperStatus'];
  let scraperStatusDetail: string;
  if (operatingBlanket_mm <= 80 && scraperPassesPerHour >= 2 && scraperSpeedMpm <= 0.45) {
    scraperStatus = 'optimal';
    scraperStatusDetail = `Blanket ${operatingBlanket_mm.toFixed(0)} mm — well controlled. ${scraperPassesPerHour.toFixed(1)} passes/h at ${scraperSpeedMpm.toFixed(2)} m/min.`;
  } else if (operatingBlanket_mm <= 150 && scraperPassesPerHour >= 1) {
    scraperStatus = 'adequate';
    scraperStatusDetail = `Blanket ${operatingBlanket_mm.toFixed(0)} mm — acceptable. Monitor for build-up under peak loads.`;
  } else if (operatingBlanket_mm <= 250) {
    scraperStatus = 'marginal';
    scraperStatusDetail = `Blanket ${operatingBlanket_mm.toFixed(0)} mm — marginal. Increase scraper speed or reduce solids loading.`;
  } else {
    scraperStatus = 'overloaded';
    scraperStatusDetail = `Blanket ${operatingBlanket_mm.toFixed(0)} mm — overloaded! Float accumulates faster than scrapers clear. Increase speed, add flights, or cut loading.`;
  }

  // ─── Status ───
  // Classification is primarily hydraulic/air-system — NOT solely removal efficiency.
  // eff_TSS can be moderate (<0.65) for correctly-sized DAFs treating low-TSS or
  // high-conductivity streams (chemistry-limited, not size-limited).
  // "Oversized" requires a genuine HLR criterion (< 1.0 m³/m²·h), not a catch-all.
  let status: DAFSize['status']; let statusDetail: string;
  if (hlr >= 1.5 && hlr <= 5 && eff_TSS >= 0.72 && A_S >= 0.03 && A_S <= 0.08 && bubbleSize <= 80) {
    // All parameters within ideal operating window
    status = 'optimal';
    statusDetail = `Optimal. HLR ${hlr.toFixed(1)} m³/m²·h, A/S ${A_S.toFixed(3)}, TSS removal ${(eff_TSS*100).toFixed(0)}%.`;
  } else if (hlr <= 10 && eff_TSS >= 0.50 && A_S >= 0.02) {
    // Hydraulically sound; removal ≥ 50% is acceptable DAF performance
    status = 'acceptable';
    statusDetail = `Acceptable. HLR ${hlr.toFixed(1)} m³/m²·h, A/S ${A_S.toFixed(3)}, TSS removal ${(eff_TSS*100).toFixed(0)}%.${eff_TSS < 0.65 ? ' Removal limited by influent chemistry — review coagulant dose or type.' : ''}`;
  } else if (hlr > 12 || eff_TSS < 0.40 || A_S < 0.015) {
    // Hydraulically overloaded, critically low air, or very poor predicted removal
    const reason = hlr > 12
      ? `HLR ${hlr.toFixed(1)} m³/m²·h exceeds 12 — increase DAF area`
      : A_S < 0.015
        ? `A/S ${A_S.toFixed(3)} critically low — increase saturator pressure or recycle rate`
        : `TSS removal ${(eff_TSS*100).toFixed(0)}% insufficient — check coagulant and polymer doses`;
    status = 'overloaded';
    statusDetail = `Overloaded. ${reason}.`;
  } else if (volume < 5 || DAF_area < 2) {
    // Physically too small for stable DAF operation
    status = 'undersized';
    statusDetail = `Undersized. DAF area ${DAF_area.toFixed(1)} m² — increase dimensions for stable operation.`;
  } else if (hlr < 1.0) {
    // Genuine oversizing: cell far too large for the flow rate
    status = 'oversized';
    statusDetail = `Oversized. HLR ${hlr.toFixed(1)} m³/m²·h — DAF area much larger than required for this flow. Reduce dimensions to lower capital and operating cost.`;
  } else {
    // Borderline acceptable: HLR 10–12, moderate removal, or high A/S outside optimal window
    const limitingFactor = hlr > 8
      ? `high hydraulic loading (HLR ${hlr.toFixed(1)} m³/m²·h)`
      : A_S > 0.08
        ? `high A/S ratio (${A_S.toFixed(3)}) — consider reducing recycle rate`
        : `moderate predicted removal (${(eff_TSS*100).toFixed(0)}%)`;
    status = 'acceptable';
    statusDetail = `Acceptable — borderline. Limited by ${limitingFactor}. Optimise chemistry or dimensions to improve performance.`;
  }

  // ─── Design Notes / Warnings ───
  const designNotes: string[] = [];
  if (A_S < 0.02) designNotes.push('⚠️ A/S ratio below optimal. Increase pressure or recycle ratio.');
  if (effluentPH < 6.0) designNotes.push('⚠️ Effluent pH acidic. Consider alkali addition.');
  if (HLR > 12) designNotes.push('⚠️ High hydraulic loading. Reduce flow or increase DAF area.');
  if (bubbleSize > 80) designNotes.push('ℹ️ Large bubbles. Increase saturator pressure.');
  if (blanketThickness_mm > 300) designNotes.push('⚠️ Thick sludge blanket. Increase scraper frequency.');
  // Scraper notes
  if (scraperStatus === 'overloaded') designNotes.push(`❌ Scraper overloaded — steady-state blanket ${operatingBlanket_mm.toFixed(0)} mm exceeds safe limit. Float will re-entrain into effluent. Increase scraper speed to ≥${Math.min(0.60, scraperSpeedMpm * 1.3).toFixed(2)} m/min or reduce solids load.`);
  if (scraperStatus === 'marginal') designNotes.push(`⚠️ Scraper marginal — blanket ${operatingBlanket_mm.toFixed(0)} mm. Recommend increasing speed to ≥${Math.min(0.60, scraperSpeedMpm * 1.2).toFixed(2)} m/min or adding a flight to the chain.`);
  if (scraperStatus === 'optimal') designNotes.push(`✅ Scraper system optimal — ${scraperCount} flights at ${scraperSpeedMpm.toFixed(2)} m/min maintain ${operatingBlanket_mm.toFixed(0)} mm operating blanket.`);
  if (scraperMotorPower_kW > 3) designNotes.push(`ℹ️ High scraper motor demand (${scraperMotorPower_kW.toFixed(2)} kW). Verify gearbox selection — consider VFD for variable-load operation.`);
  if (p.bod > 3000) designNotes.push('⚠️ Very high BOD. DAF alone may be insufficient — consider biological pre-treatment.');
  if (p.cod > 5000) designNotes.push('⚠️ Very high COD. Consider upstream anaerobic or activated sludge stage.');
  if (p.bod / Math.max(p.cod, 1) > 0.6) designNotes.push('ℹ️ High BOD/COD ratio (>0.6) indicates readily biodegradable organics. Biological treatment highly suitable.');
  if (p.bod / Math.max(p.cod, 1) < 0.3) designNotes.push('ℹ️ Low BOD/COD ratio (<0.3) indicates refractory organics. Enhanced coagulation or advanced oxidation recommended.');
  // G-value / mixing
  if (G > 100) designNotes.push('⚠️ Floc breakup risk! G-value too high (>100 s⁻¹). Reduce mixer RPM or increase tank volume. Shear destroys formed floc.');
  if (G < 20 && G > 0) designNotes.push(`⚠️ Insufficient mixing. G=${G.toFixed(0)} s⁻¹ is below 20 s⁻¹ minimum. Increase RPM, impeller size, or reduce tank volume.`);
  if (G >= 20 && G <= 75) designNotes.push(`✅ G-value optimal at ${G.toFixed(0)} s⁻¹ (target 20–75 s⁻¹ for pre-DAF flocculation).`);
  // Gt Camp criterion
  if (Gt < 20000 && Gt > 0) designNotes.push(`⚠️ Gt = ${Math.round(Gt).toLocaleString()} — below 20,000 minimum (Camp criterion). Extend retention time or increase G.`);
  if (Gt >= 20000 && Gt <= 80000) designNotes.push(`✅ Gt = ${Math.round(Gt).toLocaleString()} — within optimal range (20,000–80,000) for effective pre-DAF flocculation.`);
  if (Gt > 150000) designNotes.push(`ℹ️ Gt = ${Math.round(Gt).toLocaleString()} exceeds 150,000 — over-flocculation risk. Floc may break and re-disperse. Reduce retention or mixing power.`);
  // Tip speed / impeller
  if (tipSpeed_ms > 5) designNotes.push(`⚠️ Tip speed ${tipSpeed_ms.toFixed(2)} m/s exceeds 5 m/s. Shear damage to formed floc. Reduce RPM or impeller diameter.`);
  if (D_imp > 1.5) designNotes.push('ℹ️ Large impeller (>1.5 m). Verify adequate wall clearance — D_imp/D_tank should be 0.5–0.8 for paddle mixers.');
  // Regime note
  if (flocRegime === 'laminar') designNotes.push(`⚠️ Impeller Re = ${Re_imp.toFixed(0)} — laminar flow regime. G-value calculation may be unreliable. Increase RPM or impeller size to reach turbulent regime (Re > 10,000).`);
  if (flocRegime === 'transitional') designNotes.push(`ℹ️ Impeller Re = ${Re_imp.toFixed(0)} — transitional regime. Power number reduced by ${((1 - Np_eff/Np_turb)*100).toFixed(0)}% from fully-turbulent value.`);
  // Retention time
  if (flocRetentionTime < 5) designNotes.push('⚠️ Flocculation retention time <5 min — insufficient contact time for floc formation. Increase tank volume.');
  if (flocRetentionTime > 30) designNotes.push('ℹ️ Retention time >30 min — tank likely oversized. Consider reducing volume to save capital cost.');
  // Alkalinity
  if (residualAlkalinity < 30 && p.coagulantType !== 'none') designNotes.push(`⚠️ Residual alkalinity critically low (${residualAlkalinity.toFixed(0)} mg/L CaCO₃ after coagulant consumes ${alkalinityConsumed.toFixed(0)} mg/L). Add lime or sodium bicarbonate — risk of pH crash.`);
  if (residualAlkalinity >= 30 && residualAlkalinity < 60 && p.coagulantType !== 'none') designNotes.push(`ℹ️ Residual alkalinity marginal (${residualAlkalinity.toFixed(0)} mg/L CaCO₃). Monitor pH closely — reduce coagulant dose or supplement alkalinity.`);
  // Polymer compatibility
  if (polymerChargeMatch === 'poor') designNotes.push(`⚠️ ${polymerChargeNote}`);
  if (polymerChargeMatch === 'optimal') designNotes.push(`✅ Polymer–coagulant pairing optimal (${p.flocculantType} + ${p.coagulantType}): bridging mechanism active.`);
  if (t_floc < 5) designNotes.push('⚠️ Flocculation retention time too short (<5 min). Increase tank volume or reduce flow.');
  if (t_floc > 30) designNotes.push('ℹ️ Over-flocculation risk. Retention time >30 min — tank may be oversized.');
  if (validationNotes.length > 0) designNotes.unshift(...validationNotes);
  if (designNotes.length === 0) designNotes.push('✅ All design parameters within recommended ranges.');

  return {
    volume, volumeWithFreeboard, surfaceArea, hlr,
    slr: (p.tss * Q / 1000) / surfaceArea,
    olr: ((p.bod + p.cod + p.og) * Q / 1000) / surfaceArea,
    actualRetention: (volume / Q) * 60,
    tssRemoval: eff_TSS, ogRemoval: eff_OG, removalEfficiency,
    effluentTSS, effluentOG, effluentPH, effluentBOD, effluentCOD,
    bodRemoval: eff_BOD, codRemoval: eff_COD,
    airDemand, airToSolids: A_S, airSolubility: C_air,
    bubbleSize, bubbleRise, bubbleCount,
    flocRetentionTime, flocGValue: G, flocGt: Gt, flocVelocityGradient: G,
    flocPower: mixerPower, flocPerCompartmentVol: V_floc,
    flocMixerSpeedActual: p.flocMotorSpeed, flocTipSpeed: tipSpeed_ms,
    flocMotorTorque,
    flocReynolds: Re_imp, flocRegime, flocNpUsed: Np_eff, flocEffFactor,
    sludgeTSS, sludgeOG, sludgeBOD, sludgeCOD, sludgeChem, sludgeTotal, sludgeVolume,
    blanketThickness: blanketThickness_mm, blanketDensity,
    floatDS_pct, floatDensity_kg_m3, floatVol_m3_h, blanketAccumRate_mm_h,
    scraperCount, scraperSpeedMpm, scraperPassesPerHour, scraperFrequencyMin,
    operatingBlanket_mm, scraperBladeDepth_mm, scraperMotorPower_kW,
    scraperStatus, scraperStatusDetail,
    coagulantDemand, flocculantDemand, phDrop, coagPrecipitate,
    alkalinityConsumed, residualAlkalinity,
    polymerChargeMatch, polymerChargeNote,
    conductivityFactor, hardnessFactor,
    pumpPower, compressorPower, mixerPower, power,
    status, statusDetail, asStatus, designNotes,
  };

}

/* ─── Safe number formatter ─── */
const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

/* ─── White Water Pump Impact Analysis ─────────────────── */
function calcWhiteWaterImpact(params: DAFParams, baseSize: DAFSize) {
  const pumpMaxFlow = params.selectedPumpMaxFlowM3h;
  const pumpMaxPressure = params.selectedPumpMaxPressureBar;
  if (!params.selectedPumpId || !pumpMaxFlow || !pumpMaxPressure) return null;

  const Q = params.flowRate / 24;
  const requiredRecycleFlow = Q * (params.recycleRate / 100);
  const targetPressure = params.saturatorPressure;

  const achievableFlow = Math.min(pumpMaxFlow, requiredRecycleFlow);
  const achievablePressure = Math.min(pumpMaxPressure, targetPressure);
  const effectiveRecycleRate = (achievableFlow / Math.max(Q, 0.001)) * 100;

  const constrainedSize = calcDAF({ ...params, saturatorPressure: achievablePressure, recycleRate: effectiveRecycleRate });

  const isFlowLimiting = pumpMaxFlow < requiredRecycleFlow * 0.95;
  const isPressureLimiting = pumpMaxPressure < targetPressure * 0.97;
  const isOversized = pumpMaxFlow > requiredRecycleFlow * 2.0;
  const flowUtilization = (requiredRecycleFlow / pumpMaxFlow) * 100;
  const pressureUtilization = (targetPressure / pumpMaxPressure) * 100;

  const tempK = params.temp + 273.15;
  const C_sat_T = 25 * Math.exp(-1300 * (1 / tempK - 1 / 293.15));
  const eta = params.saturatorEfficiency / 100;
  // Use absolute pressure (gauge + 1.013 bar) for Henry's law dissolved air calc
  const targetDissolvedAir = C_sat_T * (targetPressure + 1.013) * eta;
  const actualDissolvedAir = C_sat_T * (achievablePressure + 1.013) * eta;

  const targetBubbleSize = Math.max(20, Math.min(100, 80 - 8 * (targetPressure - 4)));
  const actualBubbleSize = Math.max(20, Math.min(100, 80 - 8 * (achievablePressure - 4)));

  const tssRemovalDelta = (constrainedSize.tssRemoval - baseSize.tssRemoval) * 100;
  const ogRemovalDelta = (constrainedSize.ogRemoval - baseSize.ogRemoval) * 100;
  const bodRemovalDelta = (constrainedSize.bodRemoval - baseSize.bodRemoval) * 100;

  const notes: string[] = [];
  if (isPressureLimiting) {
    const airDrop = (((targetDissolvedAir - actualDissolvedAir) / targetDissolvedAir) * 100).toFixed(0);
    notes.push(`Pump max pressure (${pumpMaxPressure} bar) limits saturator to ${achievablePressure} bar — dissolved air reduced ${airDrop}%, bubbles grow from ${targetBubbleSize} to ${actualBubbleSize} μm`);
  }
  if (isFlowLimiting) {
    notes.push(`Pump max flow (${pumpMaxFlow} m³/h) below required recycle (${requiredRecycleFlow.toFixed(1)} m³/h) — air delivery cut proportionally, A/S ratio drops`);
  }
  if (isOversized) {
    notes.push(`Pump oversized: ${pumpMaxFlow} m³/h available vs ${requiredRecycleFlow.toFixed(1)} m³/h needed — energy waste, consider VFD or smaller unit`);
  }
  if (!isPressureLimiting && !isFlowLimiting && !isOversized) {
    notes.push(`Pump fully matches system requirements — target white water quality and treatment performance achievable`);
  }

  let overallStatus: 'optimal' | 'limited' | 'oversized' | 'critical';
  if (tssRemovalDelta < -5 || (isPressureLimiting && achievablePressure < targetPressure * 0.8)) {
    overallStatus = 'critical';
  } else if (isPressureLimiting || isFlowLimiting) {
    overallStatus = 'limited';
  } else if (isOversized) {
    overallStatus = 'oversized';
  } else {
    overallStatus = 'optimal';
  }

  return {
    pumpCode: params.selectedPumpCode ?? '',
    pumpMaxFlow, pumpMaxPressure, pumpMotorKw: params.selectedPumpMotorKw ?? null,
    requiredRecycleFlow, targetPressure,
    achievableFlow, achievablePressure,
    flowUtilization, pressureUtilization,
    isFlowLimiting, isPressureLimiting, isOversized,
    targetDissolvedAir, actualDissolvedAir,
    targetBubbleSize, actualBubbleSize,
    targetBubbleRise: baseSize.bubbleRise,
    actualBubbleRise: constrainedSize.bubbleRise,
    targetAS: baseSize.airToSolids, actualAS: constrainedSize.airToSolids,
    targetTSSRemoval: baseSize.tssRemoval * 100, actualTSSRemoval: constrainedSize.tssRemoval * 100,
    targetOGRemoval: baseSize.ogRemoval * 100, actualOGRemoval: constrainedSize.ogRemoval * 100,
    targetBODRemoval: baseSize.bodRemoval * 100, actualBODRemoval: constrainedSize.bodRemoval * 100,
    targetPumpPower: baseSize.pumpPower, actualPumpPower: constrainedSize.pumpPower,
    tssRemovalDelta, ogRemovalDelta, bodRemovalDelta,
    deltaBubbleSize: actualBubbleSize - targetBubbleSize,
    deltaDissolvedAir: actualDissolvedAir - targetDissolvedAir,
    deltaAS: constrainedSize.airToSolids - baseSize.airToSolids,
    overallStatus, notes,
  };
}

/* ─── Optimal DAF Settings Calculator ───────────────────
   Science & physics-based sizing from first principles.
   Returns a full DAFParams with all dimensions, air system,
   flocculation, and chemistry optimised for the current influent.
   ─────────────────────────────────────────────────────────── */
export function calcOptimalDAFSettings(p: DAFParams): DAFParams {
  const Q_h = p.flowRate / 24; // m³/h

  // ── Water properties at operating temperature ──────────
  const tempK = p.temp + 273.15;
  const mu    = 0.001 * Math.exp(-1.5 * (p.temp - 20) / tempK); // Pa·s
  const rho   = 998 - 0.2 * (p.temp - 20);                       // kg/m³

  // ── 1. DAF cell sizing ─────────────────────────────────
  // HLR target: scale with TSS loading
  //   TSS < 300 mg/L → 4.5 m³/m²·h (light load)
  //   TSS 300–600    → 4.0 m³/m²·h
  //   TSS > 600      → 3.5 m³/m²·h (heavy load — more contact time)
  const hlrTarget = p.tss < 300 ? 4.5 : p.tss <= 600 ? 4.0 : 3.5;
  const areaRequired = Q_h / hlrTarget; // m²

  // Width: 1.5–4.0 m stepped at practical increments, avoid >4 m (structural constraint)
  // L/W ratio must remain 1.5–3.0
  // Steps: 1.5 | 2.0 | 2.5 | 3.0 | 3.5 | 4.0
  const widthSteps = [1.5, 2.0, 2.5, 3.0, 3.5, 4.0];
  let bestW = 1.5, bestL = 4.5;
  for (const w of widthSteps) {
    const lNeeded = areaRequired / w;
    const lRounded = Math.ceil(lNeeded * 2) / 2; // round UP to nearest 0.5 m
    const ratio = lRounded / w;
    if (ratio >= 1.5 && ratio <= 3.0) { bestW = w; bestL = lRounded; break; }
    // If ratio exceeds 3.0 with this width, next (wider) step will solve it
    if (ratio > 3.0) { bestW = w; bestL = lRounded; } // keep as fallback, continue
  }
  // Final L/W clamp — if no exact step hit, take widest step and cap length
  const finalW = Math.min(bestW, 4.0);
  const rawL   = areaRequired / finalW;
  const finalL = Math.min(Math.ceil(rawL * 2) / 2, finalW * 3.0); // cap at 3× W
  // Minimum practical cell: 2 m × 1.5 m
  const optW = Math.max(finalW, 1.5);
  const optL = Math.max(finalL, 2.0);

  // Depth: 2.0 m for small flows (<100 m³/day), 2.5 m for larger
  const optDepth = p.flowRate < 100 ? 2.0 : 2.5;
  const optFreeboard = 0.5;

  // ── 2. Air system ──────────────────────────────────────
  // Saturator: always 5 bar for proven fine-bubble performance
  const optSaturatorPressure = 5;
  const optSaturatorEfficiency = 85;
  // Target A/S = 0.05 (mid-optimal range 0.03–0.08)
  // From A/S definition: A/S = (C_air × R × Q_h) / (TSS mg/L × Q_h / 1000)
  //   C_air = C_sat_T × P_sat × η_sat
  const C_sat_T   = 25 * Math.exp(-1300 * (1 / tempK - 1 / 293.15));
  // Use absolute pressure for Henry's law (same correction as calcDAF)
  const C_air_opt = C_sat_T * (optSaturatorPressure + 1.013) * (optSaturatorEfficiency / 100);
  const TSSmass_h = p.tss / 1000; // kg/m³ = kg per m³ of flow
  const AS_target = 0.05;
  // A/S = (C_air_opt × R) / (TSSmass_h × 1000)  → R = AS_target × TSSmass_h × 1000 / C_air_opt
  const rawRecycle = C_air_opt > 0 ? (AS_target * TSSmass_h * 1000) / C_air_opt : 30;
  const optRecycle = Math.max(20, Math.min(40, Math.round(rawRecycle * 100) / 100)); // % clamped 20–40
  const optAirPressure = optSaturatorPressure; // recycle pump head equiv.

  // ── 3. Flocculation chamber ────────────────────────────
  // HRT = 10 min for all cases (standard pre-DAF flocculation)
  const optFlocHRT    = 10; // min
  const optFlocVol    = Q_h * (optFlocHRT / 60); // m³
  const optFlocComps  = 3;

  // Paddle impeller: Np_turb = 1.70 (Uhl & Gray 1966)
  // Target G = 50 s⁻¹ (optimal pre-DAF flocculation, Camp 1955)
  // P = G² × μ × V_floc
  const Np_turb_pad = 1.70;
  const G_target    = 50; // s⁻¹
  const P_target_W  = G_target * G_target * mu * optFlocVol; // Watts

  // D_imp: empirical D = 0.35 × V^(1/3), clamp 0.3–1.2 m
  const D_imp = Math.max(0.3, Math.min(1.2, 0.35 * Math.pow(optFlocVol, 1 / 3)));

  // Solve N from P = Np × ρ × N³ × D⁵
  //   N³ = P / (Np × ρ × D⁵)  → N = (P / (Np × ρ × D⁵))^(1/3)
  const N_rps_raw = Math.pow(P_target_W / (Np_turb_pad * rho * Math.pow(D_imp, 5)), 1 / 3);
  const N_rpm_raw = N_rps_raw * 60;
  // Clamp to practical range: 15–80 rpm for paddle flocculators
  const optRpm     = Math.max(15, Math.min(80, Math.round(N_rpm_raw)));
  // Motor power: P_shaft × 1.2 safety factor, round up to nearest 0.25 kW
  const P_shaft_kW = P_target_W / 1000;
  const optMotorKW = Math.max(0.37, Math.ceil((P_shaft_kW * 1.2) / 0.25) * 0.25);

  // ── 4. Coagulant & polymer chemistry ──────────────────
  // Ferric chloride: best for high OG (emulsion breaking), neutral-to-alkaline pH,
  //                  or TSS > 400 mg/L (metal hydroxide sweep floc)
  // Alum: preferred for acidic pH (< 6) — forms Al(OH)₃ at lower pH
  // PAC: for pre-treated or low-TSS streams
  let optCoagType: DAFParams['coagulantType'];
  if (p.ph < 6.0) {
    optCoagType = 'alum';  // alum effective pH 5.5–7.5; ferric less so at low pH
  } else if (p.og > 100 || p.tss > 400) {
    optCoagType = 'ferric'; // ferric for high OG (emulsion break) and heavy TSS
  } else {
    optCoagType = 'alum';   // default for moderate-strength streams
  }

  // Dose: empirical TSS-based formula (Metcalf & Eddy 2014, §13):
  //   dose = max(5, min(30, TSS/15 + OG/20))  rounded to nearest 0.5 mg/L
  const rawDose   = Math.max(5, Math.min(30, p.tss / 15 + p.og / 20));
  const optDose   = Math.round(rawDose * 2) / 2; // round to 0.5

  // Polymer: anionic always with metal coagulant (Bratby 2006 — optimal charge patch + bridging)
  const optPolyType: DAFParams['flocculantType'] = 'anionic';
  // Polymer dose: 0.5–1.5 mg/L scaled with TSS
  const rawPolyDose = Math.max(0.5, Math.min(1.5, p.tss / 600));
  const optPolyDose  = Math.round(rawPolyDose * 4) / 4; // round to 0.25

  // ── 5. Target removals based on process capability ────
  // Conservative but achievable (avoid over-promising)
  const optTargetTSS = p.tss > 600 ? 80 : p.tss > 300 ? 85 : 88;
  const optTargetOG  = p.og  > 200 ? 88 : p.og  > 100 ? 90 : 92;

  return {
    ...p, // preserve influent quality + pump selection
    // DAF cell
    length: optL,
    width: optW,
    depth: optDepth,
    freeboard: optFreeboard,
    // Air system
    airPressure: optAirPressure,
    recycleRate: optRecycle,
    saturatorPressure: optSaturatorPressure,
    saturatorEfficiency: optSaturatorEfficiency,
    // Flocculation
    flocTankVolume: Math.round(optFlocVol * 10) / 10,
    flocNumCompartments: optFlocComps,
    flocRetentionTime: optFlocHRT,
    flocMotorPower: optMotorKW,
    flocMotorSpeed: optRpm,
    flocImpellerDia: Math.round(D_imp * 100) / 100,
    flocImpellerType: 'paddle',
    // Chemistry
    coagulantType: optCoagType,
    coagulantDose: optDose,
    flocculantType: optPolyType,
    flocculantDose: optPolyDose,
    // Targets
    targetRemovalTSS: optTargetTSS,
    targetRemovalOG: optTargetOG,
  };
}

/* ─── UI Components ──────────────────────────────────── */
function S({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : min;
  const pct = Math.min(100, Math.max(0, ((safeValue - min) / (max - min)) * 100));
  const displayVal = step < 0.1 ? safeValue.toFixed(2) : step < 1 ? safeValue.toFixed(1) : Math.round(safeValue).toString();

  return (
    <div className="space-y-2 group/slider">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-colors" style={{ backgroundColor: `${color}22` }}>
            <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
          </div>
          <span className="text-sm font-medium text-slate-200 truncate">{label}</span>
          {info && (
            <div className="group/tip relative shrink-0">
              <Info className="w-3.5 h-3.5 text-slate-600 cursor-help hover:text-slate-400 transition-colors" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-[#080e1a] border border-slate-700/80 rounded-xl text-[11px] text-slate-400 opacity-0 group-hover/tip:opacity-100 transition-all pointer-events-none z-50 shadow-2xl leading-relaxed">
                {info}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 bg-slate-800/60 rounded-lg px-2.5 py-1 border border-slate-800/40">
          <span className="text-sm font-bold text-white tabular-nums">{displayVal}</span>
          <span className="text-xs text-slate-500 font-normal">{unit}</span>
        </div>
      </div>
      <div className="relative h-8 flex items-center">
        {/* Track background */}
        <div className="absolute inset-x-0 h-3 bg-slate-800/80 rounded-full overflow-hidden border border-slate-800/30">
          {/* Filled portion */}
          <div
            className="h-full rounded-full transition-all duration-75"
            style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}44, ${color}ee)` }}
          />
        </div>
        {/* Glow on filled track */}
        <div
          className="absolute h-3 rounded-full pointer-events-none transition-all duration-75"
          style={{ width: `${pct}%`, boxShadow: `0 0 8px ${color}55`, background: 'transparent' }}
        />
        {/* Thumb */}
        <div
          className="absolute w-5 h-5 rounded-full bg-rb-surface shadow-xl -translate-x-1/2 transition-transform duration-75 group-hover/slider:scale-110 pointer-events-none"
          style={{
            left: `${pct}%`,
            border: `2.5px solid ${color}`,
            boxShadow: `0 0 0 3px ${color}33, 0 2px 8px rgba(0,0,0,0.6)`,
          }}
        />
        {/* Invisible range input overlay */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={safeValue}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="rb-slider absolute inset-x-0 opacity-0 h-8 cursor-pointer"
          style={{ color }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-slate-600 select-none px-0.5">
        <span>{min} {unit && unit.length < 5 ? unit : ''}</span>
        <span>{max} {unit && unit.length < 5 ? unit : ''}</span>
      </div>
    </div>
  );
}

function G({ value, max, label, unit, color }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  const safeMax = typeof max === 'number' && max > 0 ? max : 1;
  const pct = Math.min(100, (safeValue / safeMax) * 100);
  const displayVal = safeValue < 10 ? safeValue.toFixed(1) : safeValue.toFixed(0);
  return (
    <div className="rb-metric-card p-2.5 space-y-1.5">
      <div className="flex justify-between items-baseline gap-1">
        <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold leading-tight">{label}</span>
        <span className="text-xs font-bold tabular-nums shrink-0" style={{ color }}>{displayVal}<span className="text-[10px] font-normal text-slate-600 ml-0.5">{unit}</span></span>
      </div>
      <div className="h-1.5 bg-slate-800/80 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          className="h-full rounded-full"
          style={{ background: `linear-gradient(90deg, ${color}55, ${color})` }}
        />
      </div>
      <div className="text-right text-[10px] font-medium tabular-nums" style={{ color: `${color}99` }}>{pct.toFixed(0)}%</div>
    </div>
  );
}

function PC({ profile, isActive, onClick }: any) {
  const Icon = profile.icon;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
        isActive
          ? 'bg-gradient-to-br from-amber-500/15 to-amber-600/5 border-amber-500/40 rb-profile-active'
          : 'bg-rb-elevated/60 border-slate-800/80 hover:border-slate-700 hover:bg-rb-elevated'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${
          isActive ? 'bg-amber-500/20 shadow-inner shadow-amber-900/30' : 'bg-slate-800'
        }`}>
          <Icon className={`w-5 h-5 transition-colors ${isActive ? 'text-amber-400' : 'text-slate-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold truncate transition-colors ${isActive ? 'text-amber-400' : 'text-slate-300'}`}>{profile.name}</p>
          <p className="text-[10px] text-slate-500 leading-tight mt-0.5 truncate">{profile.description}</p>
        </div>
        {isActive && (
          <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-3 h-3 text-amber-400" />
          </div>
        )}
      </div>
    </button>
  );
}

function AB({ level, title, message }: any) {
  const s: any = {
    low: { b: 'border-emerald-500/20', bg: 'bg-emerald-500/5', c: 'text-emerald-400', i: CheckCircle2 },
    moderate: { b: 'border-amber-500/20', bg: 'bg-amber-500/5', c: 'text-amber-400', i: AlertCircle },
    high: { b: 'border-rose-500/20', bg: 'bg-rose-500/5', c: 'text-rose-400', i: ShieldAlert },
    poor: { b: 'border-rose-500/20', bg: 'bg-rose-500/5', c: 'text-rose-400', i: ShieldAlert },
    marginal: { b: 'border-amber-500/20', bg: 'bg-amber-500/5', c: 'text-amber-400', i: AlertCircle },
    optimal: { b: 'border-emerald-500/20', bg: 'bg-emerald-500/5', c: 'text-emerald-400', i: CheckCircle2 },
    excessive: { b: 'border-sky-500/20', bg: 'bg-sky-500/5', c: 'text-sky-400', i: Info },
  };
  const style = s[level] || s.moderate;
  const Icon = style.i;
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`rounded-xl border p-3 ${style.bg} ${style.b}`}>
      <div className="flex items-center gap-2"><Icon className={`w-4 h-4 ${style.c}`} /><span className={`text-xs font-bold uppercase ${style.c}`}>{title}</span></div>
      <p className={`text-[10px] mt-1 leading-relaxed ${style.c} opacity-80`}>{message}</p>
    </motion.div>
  );
}

/* InfluentIntelligencePanel and classifyInfluent live in their own modules:
   @/components/InfluentIntelligencePanel  and  @/lib/dafClassify
   (Extracted to prevent Rollup from DCE-ing them via pure JSX annotations)  */

/* Types re-exported from dafClassify for any external consumers */
export type { InfluentSource, InfluentFlag, InfluentClassification } from '@/lib/dafClassify';
export { classifyInfluent } from '@/lib/dafClassify';

/* ─── Pump Impact Panel ──────────────────────────────── */
type PumpImpact = NonNullable<ReturnType<typeof calcWhiteWaterImpact>>;

const PumpImpactPanel = memo(function PumpImpactPanel({ impact, onClose }: { impact: PumpImpact; onClose: () => void }) {
  const sc = impact.overallStatus === 'optimal' ? '#10b981'
    : impact.overallStatus === 'oversized' ? '#f59e0b'
    : impact.overallStatus === 'limited'   ? '#f97316'
    : '#ef4444';

  function DRow({ label, target, actual, delta, unit, higherIsBetter }: {
    label: string; target: string; actual: string; delta: number; unit: string; higherIsBetter: boolean;
  }) {
    const unchanged = Math.abs(delta) < 0.005;
    const improved  = !unchanged && (higherIsBetter ? delta > 0 : delta < 0);
    const color = unchanged ? 'var(--rb-text-3)' : improved ? '#10b981' : '#ef4444';
    const arrow = delta > 0 ? '↑' : '↓';
    const abs   = Math.abs(delta);
    const ds    = abs < 0.01 ? abs.toFixed(3) : abs < 1 ? abs.toFixed(2) : abs.toFixed(1);
    return (
      <div className="flex items-center gap-2 py-1.5 border-b border-slate-800/50 last:border-0">
        <span className="text-[10px] text-slate-400 w-24 shrink-0 leading-tight">{label}</span>
        <span className="text-[10px] text-slate-300 tabular-nums w-14 shrink-0">{target}{unit}</span>
        <ArrowRight className="w-2.5 h-2.5 text-slate-400 shrink-0" />
        <span className="text-[10px] font-bold tabular-nums w-14 shrink-0" style={{ color: unchanged ? 'var(--rb-text-2)' : color }}>{actual}{unit}</span>
        <span className="text-[10px] font-bold tabular-nums ml-auto shrink-0" style={{ color }}>
          {unchanged ? '—' : `${arrow}${ds}${unit}`}
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-xl border p-4 space-y-3" style={{ background: 'var(--rb-canvas)', borderColor: `${sc}44` }}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${sc}1a` }}>
            <Droplets className="w-4 h-4" style={{ color: sc }} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold text-white">White Water — {impact.pumpCode}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ backgroundColor: `${sc}22`, color: sc }}>
                {impact.overallStatus}
              </span>
            </div>
            <p className="text-[10px] text-slate-500 leading-tight mt-0.5">
              Dissolved air quality · Bubble physics · Treatment delta vs design target
            </p>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-600 hover:text-slate-400 transition-colors shrink-0 mt-0.5 text-[10px]">
          ✕
        </button>
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

        {/* Col 1: Pump Delivery */}
        <div className="p-3 space-y-3 rb-card-sm">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider flex items-center gap-1.5">
            <Cog className="w-3 h-3" />Pump Delivery vs Required
          </p>
          {([
            { label: 'Recycle Flow', req: impact.requiredRecycleFlow, act: impact.achievableFlow, max: impact.pumpMaxFlow, unit: 'm³/h', limiting: impact.isFlowLimiting, util: impact.flowUtilization },
            { label: 'Saturator P', req: impact.targetPressure, act: impact.achievablePressure, max: impact.pumpMaxPressure, unit: 'bar', limiting: impact.isPressureLimiting, util: impact.pressureUtilization },
          ] as const).map(({ label, req, act, max, unit, limiting, util }) => (
            <div key={label} className="space-y-1">
              <div className="flex justify-between items-baseline text-[10px]">
                <span className="text-slate-400">{label}</span>
                <span className={limiting ? 'text-rose-400 font-bold' : 'text-emerald-400'}>
                  {fmt(act, 1)}<span className="text-slate-500">/{fmt(max, 1)} {unit}</span>
                </span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{
                  width: `${Math.min(100, util)}%`,
                  background: limiting ? '#ef4444' : util > 75 ? '#10b981' : '#f59e0b',
                }} />
              </div>
              <div className="flex justify-between text-[10px] text-slate-600">
                <span>Need: {fmt(req, 1)} {unit}</span>
                <span>{util.toFixed(0)}% pump cap</span>
              </div>
            </div>
          ))}
          {impact.pumpMotorKw != null && (
            <div className="pt-1 border-t border-slate-800 flex justify-between text-[10px]">
              <span className="text-slate-500">Motor rating</span>
              <span className="text-white font-bold">{impact.pumpMotorKw} kW</span>
            </div>
          )}
          {impact.isOversized && (
            <div className="text-[10px] text-amber-400 bg-amber-500/5 rounded p-1.5 border border-amber-500/20 leading-tight">
              Pump oversized — VFD recommended for energy saving
            </div>
          )}
        </div>

        {/* Col 2: White Water Quality */}
        <div className="p-3 rb-card-sm">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider flex items-center gap-1.5 mb-2">
            <Droplets className="w-3 h-3 text-sky-500" />White Water Quality
          </p>
          <p className="text-[10px] text-slate-600 mb-2 leading-tight">Henry's law · Stokes bubble physics · Design target → pump-constrained</p>
          <DRow label="Dissolved Air" target={impact.targetDissolvedAir.toFixed(1)} actual={impact.actualDissolvedAir.toFixed(1)} delta={impact.deltaDissolvedAir} unit=" mg/L" higherIsBetter={true} />
          <DRow label="Bubble Size" target={impact.targetBubbleSize.toFixed(0)} actual={impact.actualBubbleSize.toFixed(0)} delta={impact.deltaBubbleSize} unit=" μm" higherIsBetter={false} />
          <DRow label="Bubble Rise" target={impact.targetBubbleRise.toFixed(1)} actual={impact.actualBubbleRise.toFixed(1)} delta={impact.actualBubbleRise - impact.targetBubbleRise} unit=" m/h" higherIsBetter={true} />
          <DRow label="A/S Ratio" target={impact.targetAS.toFixed(4)} actual={impact.actualAS.toFixed(4)} delta={impact.deltaAS} unit="" higherIsBetter={true} />
          <div className="mt-2 pt-2 border-t border-slate-800/60 text-[10px] text-slate-600 leading-tight space-y-0.5">
            <p>Higher pressure → more dissolved O₂ → smaller bubbles</p>
            <p>Smaller bubbles → greater surface area → better particle attachment</p>
          </div>
        </div>

        {/* Col 3: Treatment Impact */}
        <div className="p-3 rb-card-sm">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider flex items-center gap-1.5 mb-2">
            <BarChart3 className="w-3 h-3 text-emerald-500" />Treatment Performance
          </p>
          <p className="text-[10px] text-slate-600 mb-2 leading-tight">Multi-factor model: A/S factor × bubble factor × HLR factor</p>
          <DRow label="TSS Removal" target={impact.targetTSSRemoval.toFixed(1)} actual={impact.actualTSSRemoval.toFixed(1)} delta={impact.tssRemovalDelta} unit="%" higherIsBetter={true} />
          <DRow label="O&G Removal" target={impact.targetOGRemoval.toFixed(1)} actual={impact.actualOGRemoval.toFixed(1)} delta={impact.ogRemovalDelta} unit="%" higherIsBetter={true} />
          <DRow label="BOD Removal" target={impact.targetBODRemoval.toFixed(1)} actual={impact.actualBODRemoval.toFixed(1)} delta={impact.bodRemovalDelta} unit="%" higherIsBetter={true} />
          <DRow label="Pump Power" target={impact.targetPumpPower.toFixed(2)} actual={impact.actualPumpPower.toFixed(2)} delta={impact.actualPumpPower - impact.targetPumpPower} unit=" kW" higherIsBetter={false} />
          {(impact.tssRemovalDelta < -2 || impact.ogRemovalDelta < -2) && (
            <div className="mt-2 pt-2 border-t border-slate-800/60 text-[10px] text-rose-400 bg-rose-500/5 rounded p-1.5 border border-rose-500/20 leading-tight">
              Significant removal loss — consider higher-pressure pump
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-1.5">
        {impact.notes.map((note, i) => {
          const isWarn = note.startsWith('Pump max') || note.startsWith('Pump over') || note.includes('below required');
          const isInfo = note.startsWith('Pump oversized');
          const isOk   = note.startsWith('Pump fully');
          return (
            <div key={i} className={`text-[10px] p-2 rounded-lg leading-relaxed border ${
              isOk   ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' :
              isInfo  ? 'bg-sky-500/5 border-sky-500/20 text-sky-400' :
                        'bg-rose-500/5 border-rose-500/20 text-rose-400'
            }`}>{note}</div>
          );
        })}
      </div>
    </div>
  );
});

/* ─── P&ID-Style Flow Diagram ────────────────────────── */
const FlowDiagram = memo(function FlowDiagram({ params, size }: { params: DAFParams; size: DAFSize }) {
  const safe = {
    Q: typeof params.flowRate === 'number' ? params.flowRate / 24 : 10,
    hlr: typeof size?.hlr === 'number' ? size.hlr : 0,
    actualRetention: typeof size?.actualRetention === 'number' ? size.actualRetention : 0,
    airDemand: typeof size?.airDemand === 'number' ? size.airDemand : 0,
    removalEfficiency: typeof size?.removalEfficiency === 'number' ? size.removalEfficiency : 0,
    bubbleSize: typeof size?.bubbleSize === 'number' ? size.bubbleSize : 60,
    bubbleRise: typeof size?.bubbleRise === 'number' ? size.bubbleRise : 8,
    airToSolids: typeof size?.airToSolids === 'number' ? size.airToSolids : 0.04,
    blanketThickness: typeof size?.blanketThickness === 'number' ? size.blanketThickness : 80,
    sludgeTotal: typeof size?.sludgeTotal === 'number' ? size.sludgeTotal : 0,
    power: typeof size?.power === 'number' ? size.power : 0,
    flocGValue: typeof size?.flocGValue === 'number' ? size.flocGValue : 0,
    effluentPH: typeof size?.effluentPH === 'number' ? size.effluentPH : 0,
    phDrop: typeof size?.phDrop === 'number' ? size.phDrop : 0,
    coagPrecipitate: typeof size?.coagPrecipitate === 'number' ? size.coagPrecipitate : 0,
    effluentTSS: typeof size?.effluentTSS === 'number' ? size.effluentTSS : 0,
    effluentOG: typeof size?.effluentOG === 'number' ? size.effluentOG : 0,
    effluentBOD: typeof size?.effluentBOD === 'number' ? size.effluentBOD : 0,
    effluentCOD: typeof size?.effluentCOD === 'number' ? size.effluentCOD : 0,
    bodRemoval: typeof size?.bodRemoval === 'number' ? size.bodRemoval : 0,
    codRemoval: typeof size?.codRemoval === 'number' ? size.codRemoval : 0,
    pumpPower: typeof size?.pumpPower === 'number' ? size.pumpPower : 0,
    compressorPower: typeof size?.compressorPower === 'number' ? size.compressorPower : 0,
    mixerPower: typeof size?.mixerPower === 'number' ? size.mixerPower : 0,
    asStatus: size?.asStatus || 'optimal',
  };

  const bubbleCount = Math.max(8, Math.min(20, Math.round(safe.airToSolids * 200)));
  const bubbleVisualSize = Math.max(1.5, Math.min(5, safe.bubbleSize / 15));
  const blanketHeightPx = Math.min(40, Math.max(8, safe.blanketThickness / 6));

  const coagLabel = params.coagulantType === 'ferric' ? 'FeCl₃' : params.coagulantType === 'alum' ? 'Al₂(SO₄)₃' : params.coagulantType === 'pac' ? 'PAC' : 'None';
  const flocLabel = params.flocculantType === 'anionic' ? 'Anionic Poly' : params.flocculantType === 'cationic' ? 'Cationic Poly' : params.flocculantType === 'nonionic' ? 'Nonionic Poly' : 'None';

  const asColor = safe.asStatus === 'poor' ? '#ef4444' : safe.asStatus === 'marginal' ? '#f59e0b' : safe.asStatus === 'optimal' ? '#10b981' : '#0ea5e9';

  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowRightLeft className="w-3 h-3" /> Process Flow — Influent → Flocculation → DAF → Effluent
      </p>
      <svg viewBox="0 0 1100 560" className="w-full h-auto" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <defs>
          <linearGradient id="waterGradD" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rb-active)" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#0f2847" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="sludgeGradD" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8B4513" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#5c2e0b" stopOpacity="0.8" />
          </linearGradient>
          <radialGradient id="bubbleGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.4" />
          </radialGradient>

          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="shadow" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur"/>
            <feOffset in="blur" dx="1" dy="2" result="offsetBlur"/>
            <feFlood floodColor="#000" floodOpacity="0.4" result="color"/>
            <feComposite in="color" in2="offsetBlur" operator="in" result="shadow"/>
            <feMerge>
              <feMergeNode in="shadow"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          <marker id="arrowBlue" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L9,3 z" fill="#2196F3" />
          </marker>
          <marker id="arrowViolet" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L9,3 z" fill="#8b5cf6" />
          </marker>
          <marker id="arrowEmerald" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L9,3 z" fill="#10b981" />
          </marker>
          <marker id="arrowSky" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L9,3 z" fill="#0288D1" />
          </marker>
          <marker id="arrowBrown" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L9,3 z" fill="#5D4037" />
          </marker>
          <marker id="arrowRed" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L9,3 z" fill="#D32F2F" />
          </marker>
          <marker id="arrowPurple" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L9,3 z" fill="#7B1FA2" />
          </marker>
        </defs>

        <text x="550" y="18" fill="#94a3b8" fontSize="12" textAnchor="middle" fontWeight="600" letterSpacing="0.5">DISSOLVED AIR FLOTATION — P&ID PHYSICOCHEMICAL PROCESS TRAIN</text>
        <text x="1050" y="18" fill="#64748b" fontSize="10" textAnchor="end" fontStyle="italic">Hover elements for data readout</text>

        {/* ═══ INFLUENT BOX ═══ */}
        <g filter="url(#shadow)">
          <title>Influent Data: Q = {fmt(safe.Q,1)} m³/h | TSS {fmt(params.tss,0)} mg/L | O&G {fmt(params.og,0)} mg/L | BOD {fmt(params.bod,0)} mg/L | COD {fmt(params.cod,0)} mg/L | pH {fmt(params.ph,1)} | Temp {fmt(params.temp,0)}°C</title>
          <rect x="20" y="45" width="130" height="120" rx="8" fill="url(#waterGradD)" stroke="#2196F3" strokeWidth="2" />
          <rect x="24" y="49" width="122" height="22" rx="4" fill="#2196F3" opacity="0.15" />
          <text x="85" y="64" fill="#2196F3" fontSize="10" textAnchor="middle" fontWeight="700">Influent</text>
          <text x="85" y="82" fill="#94a3b8" fontSize="10" textAnchor="middle">Q = {fmt(safe.Q, 1)} m³/h</text>
          <text x="85" y="96" fill="#2196F3" fontSize="10" textAnchor="middle">TSS {fmt(params.tss, 0)} mg/L</text>
          <text x="85" y="110" fill="#2196F3" fontSize="10" textAnchor="middle">O&G {fmt(params.og, 0)} mg/L</text>
          <text x="85" y="124" fill="#f59e0b" fontSize="10" textAnchor="middle">BOD {fmt(params.bod, 0)} mg/L</text>
          <text x="85" y="138" fill="#f59e0b" fontSize="10" textAnchor="middle">COD {fmt(params.cod, 0)} mg/L</text>
          <text x="85" y="152" fill="#a855f7" fontSize="10" textAnchor="middle">pH {fmt(params.ph, 1)} | {fmt(params.temp, 0)}°C</text>
        </g>

        {/* Inlet pipe */}
        <g>
          <title>Inlet Pipe: Raw influent entering flocculation train</title>
          <line x1="150" y1="105" x2="200" y2="105" stroke="#2196F3" strokeWidth="3" strokeDasharray="8 4" markerEnd="url(#arrowBlue)">
            <animate attributeName="stroke-dashoffset" from="24" to="0" dur="1s" repeatCount="indefinite" />
          </line>
        </g>

        {/* ═══ COAGULANT DOSING ═══ */}
        <g filter="url(#shadow)">
          <title>Coagulant Dosing: {coagLabel} @ {fmt(params.coagulantDose,0)} mg/L | pH drop {fmt(safe.phDrop,3)} | Precipitate {fmt(safe.coagPrecipitate,1)} kg/d</title>
          <rect x="185" y="35" width="85" height="55" rx="5" fill="#0f172a" stroke="#D32F2F" strokeWidth="1.5" />
          <text x="227" y="52" fill="#D32F2F" fontSize="10" textAnchor="middle" fontWeight="700">{coagLabel}</text>
          <text x="227" y="65" fill="#94a3b8" fontSize="10" textAnchor="middle">{fmt(params.coagulantDose, 0)} mg/L</text>
          <text x="227" y="78" fill="#94a3b8" fontSize="10" textAnchor="middle">↓pH {fmt(safe.phDrop, 3)}</text>
          <line x1="227" y1="90" x2="227" y2="105" stroke="#D32F2F" strokeWidth="1.5" strokeDasharray="3 2" markerEnd="url(#arrowRed)" />
        </g>

        {/* ═══ POLYMER DOSING ═══ */}
        <g filter="url(#shadow)">
          <title>Polymer Dosing: {flocLabel} @ {fmt(params.flocculantDose,1)} mg/L</title>
          <rect x="185" y="130" width="85" height="42" rx="5" fill="#0f172a" stroke="#7B1FA2" strokeWidth="1.5" />
          <text x="227" y="146" fill="#7B1FA2" fontSize="10" textAnchor="middle" fontWeight="700">{flocLabel}</text>
          <text x="227" y="160" fill="#94a3b8" fontSize="10" textAnchor="middle">{fmt(params.flocculantDose, 1)} mg/L</text>
          <line x1="227" y1="130" x2="227" y2="115" stroke="#7B1FA2" strokeWidth="1.5" strokeDasharray="3 2" markerEnd="url(#arrowPurple)" />
        </g>

        {/* ═══ FLOCCULATION TANK (3 compartments) ═══ */}
        <g filter="url(#shadow)">
          <title>Flocculation Tank: {params.flocNumCompartments} compartments | G = {fmt(safe.flocGValue,0)} s⁻¹ | t = {fmt(params.flocRetentionTime,0)} min | Vol {fmt(params.flocTankVolume,0)} m³ | Mixer {fmt(safe.mixerPower,2)} kW</title>
          <rect x="290" y="40" width="250" height="130" rx="8" fill="url(#waterGradD)" stroke="#8b5cf6" strokeWidth="2" />
          <text x="415" y="58" fill="#8b5cf6" fontSize="10" textAnchor="middle" fontWeight="700">Flocculation Tank — {params.flocNumCompartments} Compartments</text>
          <text x="415" y="76" fill="#94a3b8" fontSize="10" textAnchor="middle">G = {fmt(safe.flocGValue, 0)} s⁻¹ | t = {fmt(params.flocRetentionTime, 0)} min</text>
          <text x="415" y="92" fill="#94a3b8" fontSize="10" textAnchor="middle">Vol {fmt(params.flocTankVolume, 0)} m³ | Power {fmt(safe.mixerPower, 2)} kW</text>
          <text x="415" y="108" fill="#f59e0b" fontSize="10" textAnchor="middle">{coagLabel} {fmt(params.coagulantDose, 0)} mg/L | Precip {fmt(safe.coagPrecipitate, 1)} kg/d</text>

          {/* Compartment dividers */}
          <line x1="373" y1="40" x2="373" y2="170" stroke="#8b5cf6" strokeWidth="0.5" opacity="0.5" />
          <line x1="456" y1="40" x2="456" y2="170" stroke="#8b5cf6" strokeWidth="0.5" opacity="0.5" />

          {/* Impellers in each compartment — radius scales with impeller diameter */}
          {Array.from({ length: Math.min(4, params.flocNumCompartments) }).map((_, i) => {
            const impRadiusPx = Math.min(15, Math.max(4, params.flocImpellerDia * 8));
            const cx = 325 + i * 67;
            const cy = 145;
            return (
              <motion.g key={i} animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: Math.max(0.5, 3 - params.flocMotorSpeed / 60), ease: "linear" }} style={{ transformOrigin: `${cx}px ${cy}px` }}>
                <title>Impeller {i+1}: Ø {fmt(params.flocImpellerDia, 2)} m @ {fmt(params.flocMotorSpeed, 0)} RPM</title>
                <circle cx={cx} cy={cy} r={impRadiusPx} fill="none" stroke="#8b5cf6" strokeWidth="1.5" />
                <line x1={cx} y1={cy - impRadiusPx} x2={cx} y2={cy + impRadiusPx} stroke="#8b5cf6" strokeWidth="1.5" />
                <line x1={cx - impRadiusPx} y1={cy} x2={cx + impRadiusPx} y2={cy} stroke="#8b5cf6" strokeWidth="1.5" />
                <circle cx={cx} cy={cy} r={Math.max(2, impRadiusPx * 0.25)} fill="#8b5cf6" opacity="0.5" />
              </motion.g>
            );
          })}
          <text x="415" y="164" fill="#8b5cf6" fontSize="10" textAnchor="middle">Camp-Stein: G = √(P/μV)</text>
        </g>

        {/* Arrow: Floc → DAF */}
        <g>
          <title>Flocculated Water to DAF Cell</title>
          <line x1="540" y1="105" x2="580" y2="105" stroke="#8b5cf6" strokeWidth="2.5" strokeDasharray="6 4" markerEnd="url(#arrowViolet)" />
        </g>

        {/* ═══ DAF CELL ═══ */}
        <g filter="url(#shadow)">
          <title>DAF Cell: {fmt(params.length,1)} × {fmt(params.width,1)} m, {fmt(params.depth,1)} m deep | HLR {fmt(safe.hlr,1)} m³/m²·h | Ret {fmt(safe.actualRetention,0)} min | A/S {fmt(safe.airToSolids,3)} | Ø {fmt(safe.bubbleSize,0)} μm | Rise {fmt(safe.bubbleRise,1)} m/h | Air {fmt(safe.airDemand,1)} kg/d | Power {fmt(safe.power,1)} kW</title>
          <rect x="585" y="40" width="280" height="130" rx="8" fill="url(#waterGradD)" stroke="#10b981" strokeWidth="2" />
          <text x="725" y="58" fill="#10b981" fontSize="10" textAnchor="middle" fontWeight="700">DAF Cell</text>
          <text x="725" y="76" fill="#94a3b8" fontSize="10" textAnchor="middle">{fmt(params.length, 1)} × {fmt(params.width, 1)} m, {fmt(params.depth, 1)} m deep</text>
          <text x="725" y="92" fill="#94a3b8" fontSize="10" textAnchor="middle">HLR {fmt(safe.hlr, 1)} m³/m²·h | Ret {fmt(safe.actualRetention, 0)} min</text>
          <text x="725" y="108" fill="#f59e0b" fontSize="10" textAnchor="middle">A/S {fmt(safe.airToSolids, 3)} | Ø {fmt(safe.bubbleSize, 0)} μm | Rise {fmt(safe.bubbleRise, 1)} m/h</text>
          <text x="725" y="124" fill="#94a3b8" fontSize="10" textAnchor="middle">Air {fmt(safe.airDemand, 1)} kg/d | Power {fmt(safe.power, 1)} kW</text>

          {/* Bubbles in DAF */}
          {Array.from({ length: bubbleCount }).map((_, i) => (
            <motion.circle
              key={i}
              cx={605 + (i % 10) * 24 + (Math.sin(i * 2.3) * 10)}
              cy={155 - (i % 6) * 12}
              r={bubbleVisualSize + Math.sin(i) * 0.8}
              fill="url(#bubbleGrad)"
              opacity="0.7"
              animate={{ cy: [155, 55, 48], opacity: [0.7, 0.25, 0] }}
              transition={{ repeat: Infinity, duration: 2 + (i % 4) * 0.4, delay: i * 0.12 }}
            />
          ))}

          {/* Blanket — height proportional to thickness */}
          <rect x="589" y={170 - blanketHeightPx} width="272" height={blanketHeightPx} rx="3" fill="url(#sludgeGradD)" opacity="0.9" />
          <text x="725" y={170 - blanketHeightPx - 5} fill="#8B4513" fontSize="10" textAnchor="middle" fontWeight="600">Blanket {fmt(safe.blanketThickness, 0)} mm | {fmt(safe.sludgeTotal, 1)} kg/d | {size.blanketDensity || 'Normal'}</text>

          {/* Scraper bridge */}
          <motion.g animate={{ x: [0, 50, 0] }} transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}>
            <title>Scraper Bridge: Traversing DAF surface to remove floated sludge</title>
            <rect x="600" y="44" width="70" height="7" rx="3" fill="#64748b" opacity="0.7" />
            <text x="635" y="49" fill="#e2e8f0" fontSize="10" textAnchor="middle" fontWeight="600">Scraper</text>
          </motion.g>
        </g>

        {/* Arrow: DAF → Effluent */}
        <g>
          <title>Treated Effluent to Discharge</title>
          <line x1="865" y1="90" x2="910" y2="90" stroke="#10b981" strokeWidth="2.5" strokeDasharray="6 4" markerEnd="url(#arrowEmerald)" />
        </g>

        {/* ═══ EFFLUENT BOX ═══ */}
        <g filter="url(#shadow)">
          <title>Effluent Data: TSS {fmt(safe.effluentTSS,0)} mg/L | O&G {fmt(safe.effluentOG,0)} mg/L | BOD {fmt(safe.effluentBOD,0)} mg/L ({fmt(safe.bodRemoval * 100,0)}%) | COD {fmt(safe.effluentCOD,0)} mg/L ({fmt(safe.codRemoval * 100,0)}%) | pH {fmt(safe.effluentPH,1)} | Removal {fmt(safe.removalEfficiency,0)}%</title>
          <rect x="915" y="55" width="140" height="110" rx="8" fill="url(#waterGradD)" stroke="#10b981" strokeWidth="2" />
          <rect x="919" y="59" width="132" height="22" rx="4" fill="#10b981" opacity="0.15" />
          <text x="985" y="74" fill="#10b981" fontSize="10" textAnchor="middle" fontWeight="700">Effluent</text>
          <text x="985" y="92" fill="#94a3b8" fontSize="10" textAnchor="middle">TSS {fmt(safe.effluentTSS, 0)} mg/L</text>
          <text x="985" y="106" fill="#10b981" fontSize="10" textAnchor="middle">Removal {fmt(safe.removalEfficiency, 0)}%</text>
          <text x="985" y="120" fill="#94a3b8" fontSize="10" textAnchor="middle">pH {fmt(safe.effluentPH, 1)} | O&G {fmt(safe.effluentOG, 0)}</text>
          <text x="985" y="134" fill="#f59e0b" fontSize="10" textAnchor="middle">BOD {fmt(safe.effluentBOD, 0)} ({fmt(safe.bodRemoval * 100, 0)}%)</text>
          <text x="985" y="148" fill="#f59e0b" fontSize="10" textAnchor="middle">COD {fmt(safe.effluentCOD, 0)} ({fmt(safe.codRemoval * 100, 0)}%)</text>
        </g>

        {/* ═══ AIR COMPRESSOR (top right) ═══ */}
        <g transform="translate(920, 185)" filter="url(#shadow)">
          <title>Air Compressor: {fmt(params.saturatorPressure,1)} bar | Power {fmt(safe.compressorPower,2)} kW</title>
          <rect x="0" y="5" width="45" height="35" rx="5" fill="#0f172a" stroke="#0288D1" strokeWidth="1.5" />
          <rect x="5" y="10" width="35" height="9" rx="2" fill="none" stroke="#0288D1" strokeWidth="0.5" />
          <text x="22" y="28" fill="#0288D1" fontSize="10" textAnchor="middle" fontWeight="600">{fmt(params.saturatorPressure, 1)} bar</text>
          <circle cx="22" cy="-3" r="8" fill="#0f172a" stroke="#0288D1" strokeWidth="1" />
          <line x1="22" y1="-3" x2="26" y2="-7" stroke="#0288D1" strokeWidth="0.8">
            <animateTransform attributeName="transform" type="rotate" from="0 22 -3" to={Math.min(120, params.saturatorPressure * 20)} dur="0.5s" fill="freeze" />
          </line>
          <line x1="-12" y1="22" x2="0" y2="22" stroke="#0288D1" strokeWidth="1.5" markerEnd="url(#arrowSky)" />
          <text x="22" y="48" fill="#0288D1" fontSize="10" textAnchor="middle" fontWeight="600">Compressor</text>
        </g>

        {/* Air line to DAF */}
        <g>
          <title>Air Line: Saturated air recycle to DAF cell</title>
          <line x1="910" y1="210" x2="750" y2="210" stroke="#0288D1" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#arrowSky)" />
          <line x1="750" y1="210" x2="750" y2="170" stroke="#0288D1" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#arrowSky)" />
        </g>

        {/* ═══ SATURATOR ═══ */}
        <g transform="translate(810, 185)" filter="url(#shadow)">
          <title>Saturator: {fmt(params.saturatorPressure,1)} bar | Efficiency {fmt(params.saturatorEfficiency,0)}%</title>
          <rect x="0" y="0" width="30" height="40" rx="4" fill="#0f172a" stroke="#0288D1" strokeWidth="1.5" />
          <line x1="5" y1="10" x2="25" y2="10" stroke="#0288D1" strokeWidth="0.5" />
          <line x1="5" y1="22" x2="25" y2="22" stroke="#0288D1" strokeWidth="0.5" />
          <text x="15" y="35" fill="#0288D1" fontSize="10" textAnchor="middle" fontWeight="600">Sat</text>
        </g>

        {/* ═══ RECYCLE PUMP — CENTRIFUGAL Volute ═══ */}
        <g transform="translate(160, 260)" filter="url(#shadow)">
          <title>Recycle Pump: {fmt(params.recycleRate,0)}% recycle | {fmt(safe.pumpPower,2)} kW</title>
          <path d="M 8,25 Q 0,25 0,12 Q 0,0 15,0 Q 30,0 35,10 Q 40,20 30,32 Q 20,40 8,35" fill="#0f172a" stroke="#0288D1" strokeWidth="1.5" />
          <rect x="-10" y="8" width="10" height="10" rx="2" fill="var(--rb-active)" stroke="#0288D1" strokeWidth="1" />
          <line x1="-15" y1="13" x2="-10" y2="13" stroke="#0288D1" strokeWidth="1.5" />
          <rect x="18" y="-8" width="8" height="8" rx="1" fill="var(--rb-active)" stroke="#0288D1" strokeWidth="1" />
          <line x1="22" y1="-8" x2="22" y2="-15" stroke="#0288D1" strokeWidth="1.5" />
          <motion.g animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }} style={{ transformOrigin: '18px 18px' }}>
            <circle cx="18" cy="18" r="4" fill="#0288D1" opacity="0.8" />
            <line x1="18" y1="10" x2="18" y2="26" stroke="#0288D1" strokeWidth="1.5" />
            <line x1="10" y1="18" x2="26" y2="18" stroke="#0288D1" strokeWidth="1.5" />
            <line x1="12" y1="12" x2="24" y2="24" stroke="#0288D1" strokeWidth="1.2" />
            <line x1="24" y1="12" x2="12" y2="24" stroke="#0288D1" strokeWidth="1.2" />
          </motion.g>
          <text x="18" y="52" fill="#0288D1" fontSize="10" textAnchor="middle" fontWeight="600">Recycle Pump</text>
          <text x="18" y="62" fill="#94a3b8" fontSize="10" textAnchor="middle">{fmt(params.recycleRate, 0)}% recycle | {fmt(safe.pumpPower, 2)} kW</text>
          {params.selectedPumpCode ? (
            <>
              <text x="18" y="73" fill="#facc15" fontSize="10" textAnchor="middle" fontWeight="600">🟡 {params.selectedPumpCode}</text>
              <text x="18" y="82" fill="#facc15" fontSize="10" textAnchor="middle">{params.selectedPumpMaxFlowM3h} m³/h | {params.selectedPumpMaxPressureBar} bar</text>
            </>
          ) : (
            <text x="18" y="73" fill="#ef4444" fontSize="10" textAnchor="middle" fontWeight="600">🔴 No pump selected</text>
          )}
        </g>

        {/* Recycle line from effluent → pump → saturator */}
        <g>
          <title>Recycle Line: Effluent → Recycle Pump → Saturator → DAF</title>
          <line x1="915" y1="165" x2="915" y2="290" stroke="#0288D1" strokeWidth="1.5" strokeDasharray="5 3" />
          <line x1="915" y1="290" x2="190" y2="290" stroke="#0288D1" strokeWidth="1.5" strokeDasharray="5 3" />
          <line x1="190" y1="290" x2="190" y2="105" stroke="#0288D1" strokeWidth="1.5" strokeDasharray="5 3" />
          <polygon points="187,107 190,100 193,107" fill="#0288D1" />
        </g>

        {/* ═══ SLUDGE LINE — routed at y=340 ═══ */}
        <g>
          <title>Sludge Line: Removed solids from DAF to thickening</title>
          <line x1="725" y1="170" x2="725" y2="340" stroke="#5D4037" strokeWidth="2.5" strokeDasharray="5 3" markerEnd="url(#arrowBrown)" />
          <line x1="725" y1="340" x2="380" y2="340" stroke="#5D4037" strokeWidth="2.5" strokeDasharray="5 3" markerEnd="url(#arrowBrown)" />
          <line x1="380" y1="340" x2="380" y2="380" stroke="#5D4037" strokeWidth="2.5" strokeDasharray="5 3" markerEnd="url(#arrowBrown)" />
        </g>

        {/* Sludge hopper */}
        <g filter="url(#shadow)">
          <title>Sludge Hopper: Collection cone for floated sludge</title>
          <polygon points="700,170 750,170 735,188 715,188" fill="#5D4037" opacity="0.4" stroke="#5D4037" strokeWidth="1" />
          <text x="725" y="182" fill="#5D4037" fontSize="10" textAnchor="middle" fontWeight="600">Hopper</text>
        </g>

        {/* Sludge pump */}
        <g transform="translate(365, 385)" filter="url(#shadow)">
          <title>Sludge Pump: Transfer to thickening tank</title>
          <path d="M 0,8 Q 0,0 8,0 Q 16,0 18,6 Q 20,12 14,16 Q 8,20 2,16" fill="#0f172a" stroke="#5D4037" strokeWidth="1.5" />
          <motion.g animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 3, ease: "linear" }} style={{ transformOrigin: '10px 10px' }}>
            <line x1="10" y1="3" x2="10" y2="17" stroke="#5D4037" strokeWidth="1.5" />
            <line x1="3" y1="10" x2="17" y2="10" stroke="#5D4037" strokeWidth="1.5" />
          </motion.g>
          <text x="10" y="28" fill="#5D4037" fontSize="10" textAnchor="middle" fontWeight="600">Sludge Pump</text>
        </g>

        {/* Sludge tank */}
        <g filter="url(#shadow)">
          <title>Sludge Thickening: {fmt(safe.sludgeTotal,1)} kg/d | {fmt(safe.sludgeVolume,1)} m³/d | DS 3% | pH {fmt(safe.effluentPH,1)} | Density {size.blanketDensity || 'Normal'}</title>
          <rect x="440" y="375" width="120" height="48" rx="5" fill="url(#sludgeGradD)" stroke="#5D4037" strokeWidth="1.5" />
          <text x="500" y="392" fill="#f59e0b" fontSize="10" textAnchor="middle" fontWeight="700">Sludge Thickening</text>
          <text x="500" y="406" fill="#94a3b8" fontSize="10" textAnchor="middle">{fmt(safe.sludgeTotal, 1)} kg/d | {fmt(safe.sludgeVolume, 1)} m³/d</text>
          <text x="500" y="418" fill="#94a3b8" fontSize="10" textAnchor="middle">DS 3% | pH {fmt(safe.effluentPH, 1)} | {size.blanketDensity || 'Normal'}</text>
        </g>

        {/* ═══ LEGEND OVERLAY (bottom) ═══ */}
        <g filter="url(#shadow)">
          <rect x="20" y="510" width="1060" height="26" rx="5" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
          <text x="35" y="527" fill="#2196F3" fontSize="10" fontWeight="600">— Influent + Chemicals</text>
          <text x="160" y="527" fill="#8b5cf6" fontSize="10" fontWeight="600">— Flocculated Water</text>
          <text x="290" y="527" fill="#10b981" fontSize="10" fontWeight="600">— Effluent + Bubbles</text>
          <text x="420" y="527" fill="#0288D1" fontSize="10" fontWeight="600">— Recycle + Air</text>
          <text x="540" y="527" fill="#5D4037" fontSize="10" fontWeight="600">— Sludge</text>
          <text x="660" y="527" fill="#94a3b8" fontSize="10" fontWeight="500">A/S={fmt(safe.airToSolids, 3)} | Ø={fmt(safe.bubbleSize, 0)}μm | v={fmt(safe.bubbleRise, 1)}m/h | G={fmt(safe.flocGValue, 0)}s⁻¹ | pH↓{fmt(safe.phDrop, 2)}</text>
          <circle cx="1010" cy="523" r="5" fill={asColor} />
          <text x="1020" y="527" fill={asColor} fontSize="10" fontWeight="600">A/S {safe.asStatus}</text>
        </g>
      </svg>
    </div>
  );
});

/* ─── Statutory Discharge Standards ──────────────────────────────────────────
   All limits in mg/L except pH (dimensionless range).
   Sources: EA EPR 2016, SEPA CAR 2011, NIEA Water Order 1999,
            AU state EPA policies, EU WFD 2000/60/EC + IED 2010/75/EU.
   ─────────────────────────────────────────────────────────────────────────── */

/** Maps standard code keys → REGULATORY_STANDARDS full-name keys */
const STANDARD_TO_REG_BODY: Record<string, string> = {
  UK_EA:    'Environment Agency (EA)',
  UK_NRW:   'Natural Resources Wales (NRW)',
  UK_SEPA:  'Scottish Environment Protection Agency (SEPA)',
  UK_NIEA:  'Northern Ireland Environment Agency (NIEA)',
  AU_EPA:   'EPA New South Wales',
  AU_VIC:   'EPA Victoria',
  AU_QLD:   'EPA Queensland',
  AU_SA:    'EPA South Australia',
  AU_WA:    'EPA Western Australia',
  EU_UWWTD: 'European Environment Agency (EEA)',
  US_EPA:   'US EPA / State Environmental Agency',
};

// ── Derive shared limits from ALL_STANDARDS (single source of truth) ─────────
const _UK = ALL_STANDARDS.find(s => s.code === 'UK_EA')!;
const _AU = ALL_STANDARDS.find(s => s.code === 'AU_EPA')!;
const _EU = ALL_STANDARDS.find(s => s.code === 'EU_UWWTD')!;
const _US = ALL_STANDARDS.find(s => s.code === 'US_EPA')!;

export const REGULATORY_STANDARDS: Record<string, {
  legislation: string[];
  tss: number; bod: number; cod: number | null; og: number;
  phMin: number; phMax: number; nh3n?: number; tp?: number;
}> = {
  'Environment Agency (EA)': {
    legislation: [
      'Environmental Permitting (England and Wales) Regulations 2016',
      'Urban Wastewater Treatment (England and Wales) Regulations 1994',
      'Water Framework Directive 2000/60/EC',
    ],
    tss: _UK.limits.tss, bod: _UK.limits.bod5, cod: _UK.limits.cod ?? 125,
    og: _UK.limits.oilGrease, phMin: _UK.limits.phMin ?? 6.0, phMax: _UK.limits.phMax ?? 9.0,
    nh3n: _UK.limits.ammoniaN, tp: _UK.limits.totalPhosphorus,
  },
  'Natural Resources Wales (NRW)': {
    legislation: [
      'Environmental Permitting (England and Wales) Regulations 2016',
      'Urban Wastewater Treatment (England and Wales) Regulations 1994',
      'Water Framework Directive 2000/60/EC',
    ],
    tss: _UK.limits.tss, bod: _UK.limits.bod5, cod: _UK.limits.cod ?? 125,
    og: _UK.limits.oilGrease, phMin: _UK.limits.phMin ?? 6.0, phMax: _UK.limits.phMax ?? 9.0,
    nh3n: _UK.limits.ammoniaN, tp: _UK.limits.totalPhosphorus,
  },
  'Scottish Environment Protection Agency (SEPA)': {
    legislation: [
      'Controlled Activities Regulations (Scotland) 2011 (CAR)',
      'Urban Wastewater Treatment (Scotland) Regulations 1994',
      'Water Framework Directive 2000/60/EC',
    ],
    tss: 25, bod: 15, cod: 100, og: 5, phMin: 6.0, phMax: 9.0,
  },
  'Northern Ireland Environment Agency (NIEA)': {
    legislation: [
      'Water (Northern Ireland) Order 1999',
      'Water Environment (Water Framework Directive) Regulations (NI) 2017',
    ],
    tss: 30, bod: 20, cod: null, og: 10, phMin: 6.0, phMax: 9.0,
  },
  'EPA New South Wales': {
    legislation: [
      'Protection of the Environment Operations Act 1997 (POEO Act)',
      'Protection of the Environment Operations (General) Regulation 2022',
    ],
    tss: _AU.limits.tss, bod: _AU.limits.bod5, cod: _AU.limits.cod ?? 100,
    og: _AU.limits.oilGrease, phMin: _AU.limits.phMin ?? 6.0, phMax: _AU.limits.phMax ?? 9.5,
  },
  'EPA Victoria': {
    legislation: [
      'Environment Protection Act 2017 (EP Act)',
      'State Environment Protection Policy (Waters) SEPP',
    ],
    tss: 30, bod: 20, cod: 90, og: 5, phMin: 6.0, phMax: 9.0,
  },
  'EPA Queensland': {
    legislation: [
      'Environmental Protection Act 1994 (QLD)',
      'Environmental Protection Policy (Water) 2009 (EPP Water)',
    ],
    tss: 50, bod: 30, cod: null, og: 15, phMin: 6.0, phMax: 8.5,
  },
  'EPA South Australia': {
    legislation: [
      'Environment Protection Act 1993 (SA)',
      'Environment Protection (Water Quality) Policy 2015',
    ],
    tss: 50, bod: 30, cod: null, og: 10, phMin: 6.0, phMax: 9.0,
  },
  'EPA Western Australia': {
    legislation: [
      'Environmental Protection Act 1986 (WA)',
      'Environmental Protection Regulations 1987',
    ],
    tss: 60, bod: 30, cod: null, og: 10, phMin: 6.0, phMax: 9.0,
  },
  'EPA Tasmania': {
    legislation: [
      'Environmental Management and Pollution Control Act 1994 (EMPCA)',
      'Water Management Act 1999 (TAS)',
    ],
    tss: 30, bod: 20, cod: null, og: 10, phMin: 6.0, phMax: 9.0,
  },
  'EPA Northern Territory': {
    legislation: [
      'Waste Management and Pollution Control Act 1998 (NT)',
      'Water Act 1992 (NT)',
    ],
    tss: 50, bod: 30, cod: null, og: 15, phMin: 6.0, phMax: 9.0,
  },
  'ACT Environment, Planning & Sustainable Development': {
    legislation: [
      'Environment Protection Act 1997 (ACT)',
      'Environment Protection Regulation 2005 (ACT)',
    ],
    tss: 30, bod: 20, cod: null, og: 10, phMin: 6.0, phMax: 9.0,
  },
  'European Environment Agency (EEA)': {
    legislation: [
      'Urban Wastewater Treatment Directive 91/271/EEC (as amended 2024/3019)',
      'Water Framework Directive 2000/60/EC',
      'Industrial Emissions Directive 2010/75/EU (IED)',
    ],
    tss: _EU.limits.tss, bod: _EU.limits.bod5, cod: _EU.limits.cod ?? 125,
    og: _EU.limits.oilGrease, phMin: _EU.limits.phMin ?? 6.0, phMax: _EU.limits.phMax ?? 9.0,
    nh3n: _EU.limits.ammoniaN, tp: _EU.limits.totalPhosphorus,
  },
  'US EPA / State Environmental Agency': {
    legislation: [
      'Clean Water Act (CWA) 33 U.S.C. §1251 et seq.',
      '40 CFR Part 133 — Secondary Treatment Standards',
      '40 CFR Parts 122–131 — NPDES Permit Program',
    ],
    tss: _US.limits.tss, bod: _US.limits.bod5, cod: _US.limits.cod ?? 150,
    og: _US.limits.oilGrease, phMin: _US.limits.phMin ?? 6.0, phMax: _US.limits.phMax ?? 9.0,
    nh3n: _US.limits.ammoniaN, tp: _US.limits.totalPhosphorus,
  },
};

/* ─── DAF Validation Report Builder ──────────────────────
   Exported so AdminDAFValidation can import the same function.
   Uses all new DAFSize fields: flocGt, flocNpUsed, flocRegime,
   flocReynolds, flocEffFactor, alkalinityConsumed, residualAlkalinity,
   polymerChargeMatch, polymerChargeNote.
   ─────────────────────────────────────────────────────── */
export function buildDAFReportSections(p: DAFParams, s: DAFSize, regulatoryBody?: string): ValidationSection[] {
  const f = (v: any, d = 2) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);
  const Q = p.flowRate / 24;
  const tempK = p.temp + 273.15;
  const mu = 0.001 * Math.exp(-1.5 * (p.temp - 20) / tempK);
  const rhoWater = 998 - 0.2 * (p.temp - 20);
  const N_rps = p.flocMotorSpeed / 60;
  const D_imp = p.flocImpellerDia;
  const surfaceArea = p.length * p.width;
  const IMP_LABELS: Record<string, string> = {
    paddle: 'Flat-blade paddle', hydrofoil: 'Hydrofoil (A310)',
    pitched: 'Pitched-blade 45°', rushton: 'Rushton disc turbine',
  };
  const impLabel = IMP_LABELS[p.flocImpellerType || 'paddle'] || 'Paddle';
  const cType = p.coagulantType;
  const phFactor = cType === 'ferric' ? '0.001' : cType === 'alum' ? '0.0006' : cType === 'pac' ? '0.0002' : '0';
  const precipRatio = cType === 'ferric' ? '2.7' : cType === 'alum' ? '2.9' : cType === 'pac' ? '1.5' : '0';
  const alkFactor = cType === 'ferric' ? '0.925' : cType === 'alum' ? '0.450' : cType === 'pac' ? '0.200' : '0';
  const C_sat_T = 25 * Math.exp(-1300 * (1 / tempK - 1 / 293.15));
  // Absolute pressure for Henry's law and ideal-gas air density
  const P_abs_rpt = p.saturatorPressure + 1.013;
  const rhoAir = 1.2 * P_abs_rpt * (293 / tempK);
  const fTotal = s.conductivityFactor * s.hardnessFactor;

  return [
    /* ── Step 1: Influent Characterisation ──────────────── */
    {
      title: 'Step 1 — Influent Characterisation',
      icon: Droplets,
      equations: [
        { label: 'Hourly flow rate', formula: 'Q = Q_daily / 24', substitution: `Q = ${f(p.flowRate, 0)} / 24 = ${f(Q, 3)} m³/h`, result: f(Q, 3), status: 'pass', unit: 'm³/h' },
        { label: 'BOD/COD ratio', formula: 'r = BOD / COD  (typical 0.3–0.8)', substitution: `r = ${f(p.bod, 0)} / ${f(p.cod, 0)} = ${f(p.bod / Math.max(p.cod, 1), 3)}`, result: f(p.bod / Math.max(p.cod, 1), 3), status: (p.bod / Math.max(p.cod, 1) > 0.3 && p.bod / Math.max(p.cod, 1) < 0.8) ? 'pass' : 'warn' },
        { label: 'Water density', formula: 'ρ_w = 998 − 0.2 × (T − 20)  kg/m³', substitution: `ρ_w = 998 − 0.2 × (${p.temp} − 20) = ${f(rhoWater, 1)} kg/m³`, result: f(rhoWater, 1), status: 'pass', unit: 'kg/m³' },
        { label: 'Dynamic viscosity', formula: 'μ = 0.001 × exp[−1.5(T−20)/(T+273.15)]  Pa·s', substitution: `μ = 0.001 × exp[−1.5 × ${p.temp - 20} / ${f(tempK, 1)}] = ${f(mu * 1000, 4)} mPa·s`, result: f(mu * 1000, 4), status: 'pass', unit: 'mPa·s' },
        { label: 'TSS loading rate', formula: 'L_TSS = TSS × Q / 1000  kg/h', substitution: `L_TSS = ${f(p.tss, 0)} × ${f(Q, 3)} / 1000 = ${f(p.tss * Q / 1000, 3)} kg/h`, result: f(p.tss * Q / 1000, 3), status: 'pass', unit: 'kg/h' },
        { label: 'Total organic loading', formula: 'L_org = (BOD + COD + O&G) × Q / 1000', substitution: `L_org = (${f(p.bod, 0)} + ${f(p.cod, 0)} + ${f(p.og, 0)}) × ${f(Q, 3)} / 1000 = ${f((p.bod + p.cod + p.og) * Q / 1000, 2)} kg/h`, result: f((p.bod + p.cod + p.og) * Q / 1000, 2), status: 'pass', unit: 'kg/h' },
        { label: 'Turbidity factor', formula: 'f_turb = 0.92 (>500 NTU), 0.96 (>300), 1.00 (<300)', substitution: `NTU = ${f(p.turbidity, 0)} → f_turb = ${p.turbidity > 500 ? '0.92' : p.turbidity > 300 ? '0.96' : '1.00'}`, result: p.turbidity > 500 ? '0.92' : p.turbidity > 300 ? '0.96' : '1.00', status: p.turbidity <= 300 ? 'pass' : 'warn' },
      ],
    },
    /* ── Step 2: Coagulation Chemistry ──────────────────── */
    {
      title: 'Step 2 — Coagulation Chemistry',
      icon: FlaskConical,
      equations: [
        { label: 'Hydrolysis reaction', formula: cType === 'ferric' ? 'FeCl₃ + 3H₂O → Fe(OH)₃↓ + 3HCl' : cType === 'alum' ? 'Al₂(SO₄)₃ + 6H₂O → 2Al(OH)₃↓ + 3H₂SO₄' : cType === 'pac' ? 'PAC → [Al_n(OH)_m]^(3n-m)+ ↓ (pre-hydrolysed)' : 'No coagulant dosed', substitution: `${cType === 'none' ? 'No coagulant' : `${cType.toUpperCase()} @ ${f(p.coagulantDose, 0)} mg/L`}`, result: cType === 'none' ? '—' : `${f(p.coagulantDose, 0)} mg/L`, status: 'pass' },
        { label: 'pH drop coefficient', formula: 'k = 0.001 (Fe) | 0.0006 (Al) | 0.0002 (PAC)', substitution: `k = ${phFactor}`, result: phFactor, status: 'pass' },
        { label: 'Buffered pH drop', formula: 'ΔpH = (dose × k) / (1 + alk/100)', substitution: `ΔpH = (${f(p.coagulantDose, 0)} × ${phFactor}) / (1 + ${f(p.alkalinity, 0)}/100) = ${f(s.phDrop, 4)}`, result: f(s.phDrop, 4), status: s.phDrop < 0.3 ? 'pass' : s.phDrop < 0.6 ? 'warn' : 'fail', unit: 'pH units' },
        { label: 'Effluent pH', formula: 'pH_eff = pH_in − ΔpH', substitution: `pH_eff = ${f(p.ph, 1)} − ${f(s.phDrop, 4)} = ${f(s.effluentPH, 2)}`, result: f(s.effluentPH, 2), status: s.effluentPH > 6.5 ? 'pass' : s.effluentPH > 6.0 ? 'warn' : 'fail' },
        { label: 'Precipitate formation ratio', formula: 'ratio = 2.7 (Fe) | 2.9 (Al) | 1.5 (PAC)  — M(OH)ₙ/coag', substitution: `ratio = ${precipRatio}`, result: precipRatio, status: 'pass' },
        { label: 'Precipitate mass (daily)', formula: 'm_precip = dose × ratio × Q_d / 1000', substitution: `m_precip = ${f(p.coagulantDose, 0)} × ${precipRatio} × ${f(p.flowRate, 0)} / 1000 = ${f(s.coagPrecipitate, 2)} kg/d`, result: f(s.coagPrecipitate, 2), status: 'pass', unit: 'kg/d' },
        { label: 'Coagulant consumption', formula: 'Demand = Q_d × dose / 1000', substitution: `Demand = ${f(p.flowRate, 0)} × ${f(p.coagulantDose, 0)} / 1000 = ${f(s.coagulantDemand, 2)} kg/d`, result: f(s.coagulantDemand, 2), status: 'pass', unit: 'kg/d' },
      ],
      warnings: s.effluentPH < 6.0 ? [`Effluent pH acidic (${f(s.effluentPH, 2)}). Insufficient alkalinity to buffer coagulant. Add lime or NaHCO₃.`] : undefined,
    },
    /* ── Step 3: Alkalinity & pH Balance ────────────────── */
    {
      title: 'Step 3 — Alkalinity & pH Balance',
      icon: Beaker,
      equations: [
        { label: 'Alkalinity consumption factor', formula: 'k_alk: FeCl₃=0.925 | Alum=0.450 | PAC=0.200  mg CaCO₃/mg coag', substitution: `k_alk = ${alkFactor} (stoichiometric — Bratby 2006)`, result: alkFactor, status: 'pass' },
        { label: 'Alkalinity consumed by coagulant', formula: 'ΔAlk = dose × k_alk', substitution: `ΔAlk = ${f(p.coagulantDose, 0)} × ${alkFactor} = ${f(s.alkalinityConsumed, 2)} mg/L as CaCO₃`, result: f(s.alkalinityConsumed, 2), status: 'pass', unit: 'mg/L CaCO₃' },
        { label: 'Residual alkalinity', formula: 'Alk_res = Alk_in − ΔAlk', substitution: `Alk_res = ${f(p.alkalinity, 0)} − ${f(s.alkalinityConsumed, 2)} = ${f(s.residualAlkalinity, 1)} mg/L CaCO₃`, result: f(s.residualAlkalinity, 1), status: s.residualAlkalinity >= 60 ? 'pass' : s.residualAlkalinity >= 30 ? 'warn' : 'fail', unit: 'mg/L CaCO₃' },
        { label: 'pH buffer capacity', formula: 'Safe: Alk_res > 60 mg/L | Marginal: 30–60 | Risk: <30', substitution: `Alk_res = ${f(s.residualAlkalinity, 0)} mg/L → ${s.residualAlkalinity >= 60 ? 'Good buffer — pH stable' : s.residualAlkalinity >= 30 ? 'Marginal — monitor pH closely' : 'RISK — pH crash likely, add alkali'}`, result: s.residualAlkalinity >= 60 ? 'Safe' : s.residualAlkalinity >= 30 ? 'Marginal' : 'Risk', status: s.residualAlkalinity >= 60 ? 'pass' : s.residualAlkalinity >= 30 ? 'warn' : 'fail' },
      ],
      warnings: s.residualAlkalinity < 30 && cType !== 'none' ? [`Residual alkalinity critically low: ${f(s.residualAlkalinity, 0)} mg/L CaCO₃. After coagulant consumes ${f(s.alkalinityConsumed, 0)} mg/L, pH crash imminent. Dose lime (Ca(OH)₂) or sodium bicarbonate.`] : undefined,
    },
    /* ── Step 4: Water Chemistry Factors ────────────────── */
    {
      title: 'Step 4 — Water Chemistry Factors',
      icon: Filter,
      equations: [
        { label: 'Conductivity modifier', formula: 'f_cond = max(0.80, 1 − 0.03 × log₁₀(EC/1000))', substitution: `f_cond = max(0.80, 1 − 0.03 × log₁₀(${f(p.conductivity, 0)}/1000)) = ${f(s.conductivityFactor, 4)}`, result: f(s.conductivityFactor, 4), status: s.conductivityFactor >= 0.95 ? 'pass' : s.conductivityFactor >= 0.90 ? 'warn' : 'fail' },
        { label: 'Hardness modifier', formula: 'f_hard = 1.0 + 0.05 × min((hard−300)/200, 1); 0.95 if hard<50', substitution: `f_hard = ${f(s.hardnessFactor, 4)} (hardness ${f(p.hardness, 0)} mg/L CaCO₃)`, result: f(s.hardnessFactor, 4), status: s.hardnessFactor >= 0.98 ? 'pass' : 'warn' },
        { label: 'Combined chemistry factor', formula: 'f_total = f_cond × f_hard', substitution: `f_total = ${f(s.conductivityFactor, 4)} × ${f(s.hardnessFactor, 4)} = ${f(fTotal, 4)}`, result: f(fTotal, 4), status: fTotal >= 0.90 ? 'pass' : fTotal >= 0.85 ? 'warn' : 'fail' },
        { label: 'Coagulation boost (adjusted)', formula: 'boost = base_coag × f_cond × f_hard', substitution: `boost = ${cType === 'ferric' ? '0.30' : cType === 'alum' ? '0.20' : cType === 'pac' ? '0.15' : '0.0'} × ${f(fTotal, 4)} = ${f((cType === 'ferric' ? 0.30 : cType === 'alum' ? 0.20 : cType === 'pac' ? 0.15 : 0) * fTotal, 4)}`, result: f((cType === 'ferric' ? 0.30 : cType === 'alum' ? 0.20 : cType === 'pac' ? 0.15 : 0) * fTotal, 4), status: 'pass' },
        { label: 'Adjusted TSS removal', formula: 'η_TSS (includes f_total, flocEffFactor)', substitution: `η_TSS = ${f(s.tssRemoval * 100, 1)}% (all correction factors applied)`, result: f(s.tssRemoval * 100, 1), status: s.tssRemoval >= 0.70 ? 'pass' : s.tssRemoval >= 0.55 ? 'warn' : 'fail', unit: '%' },
        { label: 'Adjusted O&G removal', formula: 'η_OG (includes f_total, flocEffFactor)', substitution: `η_OG = ${f(s.ogRemoval * 100, 1)}% (all correction factors applied)`, result: f(s.ogRemoval * 100, 1), status: s.ogRemoval >= 0.70 ? 'pass' : s.ogRemoval >= 0.55 ? 'warn' : 'fail', unit: '%' },
      ],
      warnings: p.conductivity > 6000 ? [`High conductivity (${f(p.conductivity, 0)} μS/cm) reduces coagulant efficiency by ${((1 - s.conductivityFactor) * 100).toFixed(0)}%. Consider increasing coagulant dose.`] : undefined,
    },
    /* ── Step 5: Flocculation Mixing Physics ─────────────── */
    {
      title: 'Step 5 — Flocculation Mixing Physics',
      icon: Wind,
      equations: [
        { label: 'Impeller geometry', formula: 'Determines Np_turb and Kp_lam constants', substitution: `Type: ${impLabel} (${p.flocImpellerType}) | D = ${f(D_imp, 2)} m | N = ${f(p.flocMotorSpeed, 0)} rpm`, result: p.flocImpellerType, status: 'pass' },
        { label: 'Impeller Reynolds number', formula: 'Re_imp = ρ × N × D² / μ', substitution: `Re_imp = ${f(rhoWater, 0)} × ${f(N_rps, 4)} × ${f(D_imp, 2)}² / ${f(mu, 7)} = ${f(s.flocReynolds, 0)}`, result: f(s.flocReynolds, 0), status: s.flocReynolds > 10000 ? 'pass' : s.flocReynolds > 1000 ? 'warn' : 'fail' },
        { label: 'Flow regime classification', formula: 'Turbulent: Re>10,000 | Trans: 10<Re≤10,000 | Laminar: Re≤10', substitution: `Re = ${f(s.flocReynolds, 0)} → ${s.flocRegime.charAt(0).toUpperCase() + s.flocRegime.slice(1)} regime`, result: s.flocRegime, status: s.flocRegime === 'turbulent' ? 'pass' : s.flocRegime === 'transitional' ? 'warn' : 'fail' },
        { label: 'Effective Power Number (Np)', formula: 'Turbulent: Np_turb | Trans: Np_turb×(10000/Re)^0.14 | Lam: Kp/Re', substitution: `Np_eff = ${f(s.flocNpUsed, 5)} for ${impLabel} in ${s.flocRegime} regime`, result: f(s.flocNpUsed, 5), status: 'pass' },
        { label: 'Shaft power dissipated', formula: 'P = Np_eff × ρ × N³ × D⁵  (W)', substitution: `P = ${f(s.flocNpUsed, 5)} × ${f(rhoWater, 0)} × ${f(N_rps, 5)}³ × ${f(D_imp, 2)}⁵ = ${f(s.flocPower * 1000, 2)} W = ${f(s.flocPower, 4)} kW`, result: f(s.flocPower, 4), status: 'pass', unit: 'kW' },
        { label: 'Camp-Stein G-value', formula: 'G = √(P_W / μV)  s⁻¹ — target 20–75 s⁻¹ for pre-DAF', substitution: `G = √(${f(s.flocPower * 1000, 2)} / (${f(mu, 7)} × ${f(p.flocTankVolume, 2)})) = ${f(s.flocGValue, 2)} s⁻¹`, result: f(s.flocGValue, 2), status: s.flocGValue >= 20 && s.flocGValue <= 75 ? 'pass' : s.flocGValue <= 100 ? 'warn' : 'fail', unit: 's⁻¹' },
        { label: 'Hydraulic retention time', formula: 't = V_floc / Q × 60  min', substitution: `t = ${f(p.flocTankVolume, 2)} / ${f(Q, 3)} × 60 = ${f(s.flocRetentionTime, 2)} min`, result: f(s.flocRetentionTime, 2), status: s.flocRetentionTime >= 5 && s.flocRetentionTime <= 30 ? 'pass' : 'warn', unit: 'min' },
        { label: 'Camp criterion Gt (dimensionless)', formula: 'Gt = G × t_s — optimal: 20,000–80,000', substitution: `Gt = ${f(s.flocGValue, 2)} × ${f(s.flocRetentionTime * 60, 1)} s = ${Math.round(s.flocGt).toLocaleString()}`, result: Math.round(s.flocGt).toLocaleString(), status: s.flocGt >= 20000 && s.flocGt <= 80000 ? 'pass' : s.flocGt > 5000 ? 'warn' : 'fail' },
        { label: 'Impeller tip speed', formula: 'v_tip = π × D × N  m/s — limit: ≤3 m/s (DAF floc)', substitution: `v_tip = π × ${f(D_imp, 2)} × ${f(N_rps, 4)} = ${f(s.flocTipSpeed, 4)} m/s`, result: f(s.flocTipSpeed, 4), status: s.flocTipSpeed <= 3 ? 'pass' : s.flocTipSpeed <= 5 ? 'warn' : 'fail', unit: 'm/s' },
        { label: 'Floc efficiency factor (f_eff)', formula: '1.00 (G 20–75, Gt 20k–80k) → 0.75 (under-mixed Gt<5k)', substitution: `G=${f(s.flocGValue, 1)} s⁻¹, Gt=${Math.round(s.flocGt).toLocaleString()} → f_eff = ${f(s.flocEffFactor, 3)} (applied to all removal fractions)`, result: f(s.flocEffFactor, 3), status: s.flocEffFactor >= 0.95 ? 'pass' : s.flocEffFactor >= 0.85 ? 'warn' : 'fail' },
      ],
    },
    /* ── Step 6: Polymer Charge Compatibility ───────────── */
    {
      title: 'Step 6 — Polymer Charge Compatibility',
      icon: Pipette,
      equations: [
        { label: 'Coagulant charge state', formula: 'Metal coagulants → positively-charged M(OH)ₙ floc', substitution: cType === 'none' ? 'No coagulant — raw colloid surface charge applies' : `${cType.toUpperCase()} hydrolysis → +ve charged Fe/Al hydroxide floc`, result: cType === 'none' ? 'N/A' : '+ve floc', status: 'pass' },
        { label: 'Polymer type & dose', formula: 'Anionic: -ve chain bridges +ve floc (optimal); Cationic: +ve chain with +ve floc (poor)', substitution: p.flocculantType === 'none' ? 'No polymer dosed' : `${p.flocculantType.toUpperCase()} @ ${f(p.flocculantDose, 2)} mg/L — ${p.flocculantType === 'anionic' ? 'negatively charged' : p.flocculantType === 'cationic' ? 'positively charged' : 'charge-neutral'} polyelectrolyte`, result: p.flocculantType, status: 'pass' },
        { label: 'Compatibility (Bratby 2006)', formula: 'Optimal: anionic+metal | Good: cationic alone | Acceptable: nonionic | Poor: cationic+metal', substitution: s.polymerChargeNote, result: s.polymerChargeMatch, status: s.polymerChargeMatch === 'optimal' || s.polymerChargeMatch === 'good' ? 'pass' : s.polymerChargeMatch === 'acceptable' ? 'warn' : 'fail' },
        { label: 'Polymer demand', formula: 'Demand = Q_d × dose / 1000  kg/d', substitution: `Demand = ${f(p.flowRate, 0)} × ${f(p.flocculantDose, 3)} / 1000 = ${f(s.flocculantDemand, 3)} kg/d`, result: f(s.flocculantDemand, 3), status: 'pass', unit: 'kg/d' },
      ],
      warnings: s.polymerChargeMatch === 'poor' ? [`Poor polymer–coagulant pairing: ${s.polymerChargeNote}`] : undefined,
    },
    /* ── Step 7: Air System — Henry's Law ────────────────── */
    {
      title: "Step 7 — Air System (Henry's Law)",
      icon: CircleDot,
      equations: [
        { label: "Air solubility at T (van't Hoff)", formula: 'C_sat(T) = C₂₀ × exp[−1300(1/T_K − 1/293.15)]', substitution: `C_sat(${f(p.temp, 0)}°C) = 25 × exp[−1300(1/${f(tempK, 2)} − 1/293.15)] = ${f(C_sat_T, 3)} mg/L`, result: f(C_sat_T, 3), status: 'pass', unit: 'mg/L' },
        { label: 'Saturator pressure correction (Henry)', formula: 'C_sat(P) = C_sat(T) × P_abs  where P_abs = P_gauge + 1.013 bar', substitution: `C_sat(P) = ${f(C_sat_T, 3)} × (${f(p.saturatorPressure, 1)} + 1.013) = ${f(C_sat_T * P_abs_rpt, 3)} mg/L  [gauge ${f(p.saturatorPressure,1)} bar → abs ${f(P_abs_rpt,3)} bar]`, result: f(C_sat_T * P_abs_rpt, 3), status: 'pass', unit: 'mg/L' },
        { label: 'Effective dissolved air (saturator η)', formula: 'C_air = C_sat(P) × η_sat', substitution: `C_air = ${f(s.airSolubility / (p.saturatorEfficiency / 100), 3)} × ${f(p.saturatorEfficiency / 100, 3)} = ${f(s.airSolubility, 3)} mg/L`, result: f(s.airSolubility, 3), status: 'pass', unit: 'mg/L' },
        { label: 'Recycle flow rate', formula: 'Q_R = Q × R/100  m³/h', substitution: `Q_R = ${f(Q, 3)} × ${f(p.recycleRate, 0)}/100 = ${f(Q * p.recycleRate / 100, 4)} m³/h`, result: f(Q * p.recycleRate / 100, 4), status: 'pass', unit: 'm³/h' },
        { label: 'Air mass flux', formula: 'm_air = C_air × Q_R / 1000  kg/h', substitution: `m_air = ${f(s.airSolubility, 3)} × ${f(Q * p.recycleRate / 100, 4)} / 1000 = ${f(s.airDemand / 24, 4)} kg/h`, result: f(s.airDemand / 24, 4), status: 'pass', unit: 'kg/h' },
        { label: 'Air-to-Solids ratio', formula: 'A/S = m_air / (TSS × Q / 1000) — target 0.03–0.08', substitution: `A/S = ${f(s.airDemand / 24, 4)} / ${f(p.tss * Q / 1000, 4)} = ${f(s.airToSolids, 5)}`, result: f(s.airToSolids, 5), status: s.airToSolids >= 0.03 && s.airToSolids <= 0.08 ? 'pass' : s.airToSolids >= 0.02 ? 'warn' : 'fail' },
        { label: 'Daily air demand', formula: 'm_day = m_air × 24', substitution: `m_day = ${f(s.airDemand / 24, 4)} × 24 = ${f(s.airDemand, 3)} kg/d`, result: f(s.airDemand, 3), status: 'pass', unit: 'kg/d' },
      ],
    },
    /* ── Step 8: Bubble Physics — Stokes' Law ───────────── */
    {
      title: "Step 8 — Bubble Physics (Stokes' Law)",
      icon: Waves,
      equations: [
        { label: 'Bubble diameter', formula: 'd_b = 80 − 8(P_sat − 4) μm, clamped 20–100', substitution: `d_b = 80 − 8(${f(p.saturatorPressure, 1)} − 4) = ${f(s.bubbleSize, 0)} μm`, result: f(s.bubbleSize, 0), status: s.bubbleSize >= 30 && s.bubbleSize <= 80 ? 'pass' : 'warn', unit: 'μm' },
        { label: 'Air bubble density (absolute P)', formula: 'ρ_a = 1.2 × P_abs × (293/T_K)  kg/m³  [ideal gas; P_abs = P_gauge+1.013]', substitution: `ρ_a = 1.2 × ${f(P_abs_rpt, 3)} × 293/${f(tempK, 1)} = ${f(rhoAir, 3)} kg/m³`, result: f(rhoAir, 3), status: 'pass', unit: 'kg/m³' },
        { label: 'Rise velocity (Stokes + H–R correction ×1.20)', formula: 'v_b = g(ρ_w−ρ_a)d² / 18μ × 1.20  (Hadamard–Rybczynski fluid-sphere; surfactant-reduced)', substitution: `v_Stokes = 9.81×(${f(rhoWater,1)}−${f(rhoAir,3)})×(${f(s.bubbleSize*1e-6,8)})² / (18×${f(mu,7)}); v_HR = v_Stokes × 1.20 = ${f(s.bubbleRise,3)} m/h`, result: f(s.bubbleRise, 3), status: s.bubbleRise >= 5 && s.bubbleRise <= 20 ? 'pass' : 'warn', unit: 'm/h' },
        { label: 'Bubble rise adequacy', formula: 'v_b should exceed HLR for separation', substitution: `v_b = ${f(s.bubbleRise, 1)} m/h vs HLR = ${f(s.hlr, 2)} m/h → ${s.bubbleRise > s.hlr ? 'bubble rise exceeds HLR ✓' : 'HLR exceeds bubble rise — reduce flow or increase pressure'}`, result: s.bubbleRise > s.hlr ? 'Pass' : 'Warn', status: s.bubbleRise > s.hlr ? 'pass' : 'warn' },
      ],
    },
    /* ── Step 9: Flotation Kinetics & Removal ───────────── */
    {
      title: 'Step 9 — Flotation Kinetics & Removal Efficiency',
      icon: BarChart3,
      equations: [
        { label: 'DAF cell surface area', formula: 'A = L × W', substitution: `A = ${f(p.length, 1)} × ${f(p.width, 1)} = ${f(surfaceArea, 1)} m²`, result: f(surfaceArea, 1), status: 'pass', unit: 'm²' },
        { label: 'Hydraulic loading rate', formula: 'HLR = Q / A — target ≤5 m³/m²·h', substitution: `HLR = ${f(Q, 3)} / ${f(surfaceArea, 1)} = ${f(s.hlr, 3)} m³/m²·h`, result: f(s.hlr, 3), status: s.hlr <= 5 ? 'pass' : s.hlr <= 8 ? 'warn' : 'fail', unit: 'm³/m²·h' },
        { label: 'Cell hydraulic retention', formula: 't_cell = V_cell / Q × 60', substitution: `t_cell = ${f(surfaceArea * p.depth, 2)} / ${f(Q, 3)} × 60 = ${f(s.actualRetention, 1)} min`, result: f(s.actualRetention, 1), status: s.actualRetention >= 10 ? 'pass' : 'warn', unit: 'min' },
        { label: 'TSS removal efficiency', formula: 'η_TSS = (45% base + coag_boost + poly_boost) × HLR × A/S × ret × chem × f_eff', substitution: `η_TSS = ${f(s.tssRemoval * 100, 2)}% (all factors applied, f_eff=${f(s.flocEffFactor, 3)})`, result: f(s.tssRemoval * 100, 2), status: s.tssRemoval >= 0.75 ? 'pass' : s.tssRemoval >= 0.55 ? 'warn' : 'fail', unit: '%' },
        { label: 'O&G removal efficiency', formula: 'η_OG = (65% base + poly_boost) × HLR × A/S × ret × chem × f_eff', substitution: `η_OG = ${f(s.ogRemoval * 100, 2)}% (base 65%)`, result: f(s.ogRemoval * 100, 2), status: s.ogRemoval >= 0.75 ? 'pass' : s.ogRemoval >= 0.55 ? 'warn' : 'fail', unit: '%' },
        { label: 'BOD removal efficiency', formula: 'η_BOD = (25% base + coag_BOD + poly_BOD) × factors', substitution: `η_BOD = ${f(s.bodRemoval * 100, 2)}% (base 25%, particle-bound only)`, result: f(s.bodRemoval * 100, 2), status: 'pass', unit: '%' },
        { label: 'COD removal efficiency', formula: 'η_COD = (35% base + coag_COD + poly_COD) × factors', substitution: `η_COD = ${f(s.codRemoval * 100, 2)}% (base 35%)`, result: f(s.codRemoval * 100, 2), status: 'pass', unit: '%' },
        { label: 'Overall removal efficiency', formula: 'η_avg = (η_TSS + η_OG + η_BOD + η_COD) / 4', substitution: `η_avg = (${f(s.tssRemoval * 100, 1)} + ${f(s.ogRemoval * 100, 1)} + ${f(s.bodRemoval * 100, 1)} + ${f(s.codRemoval * 100, 1)}) / 4 = ${f(s.removalEfficiency, 2)}%`, result: f(s.removalEfficiency, 2), status: s.removalEfficiency >= 70 ? 'pass' : 'warn', unit: '%' },
      ],
    },
    /* ── Step 10: Sludge Production & Blanket ───────────── */
    {
      title: 'Step 10 — Sludge Production & Blanket',
      icon: Cylinder,
      equations: [
        { label: 'Captured TSS (daily)', formula: 'm_TSS = TSS × η_TSS × Q_d / 1000', substitution: `m_TSS = ${f(p.tss, 0)} × ${f(s.tssRemoval, 5)} × ${f(p.flowRate, 0)} / 1000 = ${f(s.sludgeTSS, 2)} kg/d`, result: f(s.sludgeTSS, 2), status: 'pass', unit: 'kg/d' },
        { label: 'Captured O&G (daily)', formula: 'm_OG = OG × η_OG × Q_d / 1000', substitution: `m_OG = ${f(p.og, 0)} × ${f(s.ogRemoval, 5)} × ${f(p.flowRate, 0)} / 1000 = ${f(s.sludgeOG, 2)} kg/d`, result: f(s.sludgeOG, 2), status: 'pass', unit: 'kg/d' },
        { label: 'Chemical precipitate (daily)', formula: 'm_chem = coag_dose × ratio × Q_d / 1000', substitution: `m_chem = ${f(s.coagPrecipitate, 2)} kg/d (from coagulant hydrolysis)`, result: f(s.sludgeChem, 2), status: 'pass', unit: 'kg/d' },
        { label: 'Total floated dry solids', formula: 'm_total = m_TSS + m_OG + m_chem  (BOD/COD are O₂-demand, not physical solids)', substitution: `m_total = ${f(s.sludgeTSS, 2)} + ${f(s.sludgeOG, 2)} + ${f(s.sludgeChem, 2)} = ${f(s.sludgeTotal, 2)} kg/d`, result: f(s.sludgeTotal, 2), status: 'pass', unit: 'kg/d' },
        { label: 'Wet sludge volume (3% DS)', formula: 'V_sl = m_total / 30  (30 kg DS/m³ @ 3% DS)', substitution: `V_sl = ${f(s.sludgeTotal, 2)} / 30 = ${f(s.sludgeVolume, 3)} m³/d`, result: f(s.sludgeVolume, 3), status: 'pass', unit: 'm³/d' },
        { label: 'Float blanket accumulation', formula: 'h = (V_sl / A_DAF / 24) × 1000 × 2  mm', substitution: `h = (${f(s.sludgeVolume, 3)} / ${f(surfaceArea, 1)} / 24) × 1000 × 2 = ${f(s.blanketThickness, 0)} mm (${s.blanketDensity})`, result: f(s.blanketThickness, 0), status: s.blanketThickness < 150 ? 'pass' : s.blanketThickness < 300 ? 'warn' : 'fail', unit: 'mm' },
      ],
    },
    /* ── Step 11: Power & Energy Balance ────────────────── */
    {
      title: 'Step 11 — Power & Energy Balance',
      icon: Zap,
      equations: [
        { label: 'Recycle pump power', formula: 'P_pump = ρgQH / η_pump  (η=75%, H=P_sat×10 m)', substitution: `P_pump = ${f(rhoWater, 0)} × 9.81 × ${f(Q * p.recycleRate / 100 / 3600, 6)} × ${f(p.saturatorPressure * 10, 0)} / 0.75 / 1000 = ${f(s.pumpPower, 4)} kW`, result: f(s.pumpPower, 4), status: 'pass', unit: 'kW' },
        { label: 'Air compressor power (isothermal)', formula: 'P_comp = Q_R × P_atm × ln(P_sat/P_atm) / η_comp  (η=65%)', substitution: `P_comp = ${f(Q * p.recycleRate / 100 / 3600, 6)} × 101325 × ln(${p.saturatorPressure}/1.013) / 0.65 / 1000 = ${f(s.compressorPower, 4)} kW`, result: f(s.compressorPower, 4), status: 'pass', unit: 'kW' },
        { label: 'Flocculation mixer power', formula: 'P_mix = Np × ρ × N³ × D⁵  (from Step 5)', substitution: `P_mix = ${f(s.mixerPower, 5)} kW (regime: ${s.flocRegime}, Np=${f(s.flocNpUsed, 4)})`, result: f(s.mixerPower, 5), status: 'pass', unit: 'kW' },
        { label: 'Total installed power', formula: 'P_total = P_pump + P_comp + P_mix', substitution: `P_total = ${f(s.pumpPower, 4)} + ${f(s.compressorPower, 4)} + ${f(s.mixerPower, 5)} = ${f(s.power, 3)} kW`, result: f(s.power, 3), status: s.power < 15 ? 'pass' : 'warn', unit: 'kW' },
        { label: 'Specific energy consumption', formula: 'E_spec = P_total × 24 / Q_d  kWh/m³', substitution: `E_spec = ${f(s.power, 3)} × 24 / ${f(p.flowRate, 0)} = ${f(s.power * 24 / Math.max(p.flowRate, 0.001), 4)} kWh/m³`, result: f(s.power * 24 / Math.max(p.flowRate, 0.001), 4), status: s.power * 24 / Math.max(p.flowRate, 0.001) < 0.5 ? 'pass' : s.power * 24 / Math.max(p.flowRate, 0.001) < 1.0 ? 'warn' : 'fail', unit: 'kWh/m³' },
      ],
    },
    /* ── Step 12: Scraper Design & Blanket Management ──────── */
    {
      title: 'Step 12 — Scraper Design & Blanket Management',
      icon: Timer,
      equations: (() => {
        const Q_h2 = p.flowRate / 24;
        const mu2   = 0.001 * Math.exp(-1.5 * (p.temp - 20) / (p.temp + 273.15));
        const rho2  = 998 - 0.2 * (p.temp - 20);
        const hlr2  = Q_h2 / surfaceArea;
        const C_sat_T2  = 25 * Math.exp(-1300 * (1 / (p.temp + 273.15) - 1 / 293.15));
        // Must use absolute pressure for Henry's law (gauge + 1.013 bar)
        const C_air2    = C_sat_T2 * (p.saturatorPressure + 1.013) * (p.saturatorEfficiency / 100);
        const TSSmass2  = (p.tss * Q_h2) / 1000;
        const airMass2  = (C_air2 * Q_h2 * (p.recycleRate / 100)) / 1000;
        const AS2       = TSSmass2 > 0 ? airMass2 / TSSmass2 : 0;
        const phi_air2  = Math.min(0.15, 0.05 + AS2 * 0.5);
        const rhoAir2   = 1.2 * (p.saturatorPressure + 1.013) * (293 / (p.temp + 273.15)); // absolute P
        const floatDens = Math.round(rho2 * (1 - phi_air2) + rhoAir2 * phi_air2);
        const f_coag2   = 1 + Math.min(0.30, p.coagulantDose / 100);
        const f_poly2   = 1 + Math.min(0.40, p.flocculantDose * 0.08);
        const f_hlr2    = Math.max(0.80, 1 - (hlr2 - 4) / 20);
        const fDS       = Math.max(1.5, Math.min(8.0, 3.0 * f_coag2 * f_poly2 * f_hlr2));
        const capMass   = (s.sludgeTSS + s.sludgeOG + s.sludgeChem) / 24;
        const floatVol2 = capMass / (fDS / 100) / floatDens;
        const accumRate = (floatVol2 / surfaceArea) * 1000;
        const nFlights  = Math.max(2, Math.ceil(p.length / 1.5));
        const sbAS2     = Math.min(0.15, AS2 * 1.5);
        const sbAccum   = Math.min(0.20, accumRate / 30);
        const sbOG      = Math.min(0.10, p.og / 2000);
        const vScr      = Math.max(0.15, Math.min(0.60, 0.20 + sbAS2 + sbAccum + sbOG));
        const pph       = (vScr * 60) / Math.max(p.length, 0.1);
        const eta_sc    = 0.87;
        const opBlanket = accumRate / Math.max(pph * eta_sc, 0.001);
        const bladeDepth = Math.min(250, Math.max(50, opBlanket * 2 + 25));
        const vScrMs    = vScr / 60;
        const AB        = (bladeDepth / 1000) * p.width;
        const Fdrag     = 1.8 * floatDens * vScrMs * vScrMs * AB * nFlights;
        const motorKW   = Math.max(0.18, (Fdrag * 1.5 * vScrMs) / 0.65 / 1000);
        return [
          { label: 'Float dry solids content', formula: 'DS% = 3.0 × f_coag × f_poly × f_hlr  (Bratby 2006)', substitution: `DS% = 3.0 × ${f_coag2.toFixed(3)} × ${f_poly2.toFixed(3)} × ${f_hlr2.toFixed(3)} = ${fDS.toFixed(2)}%`, result: f(fDS, 2), status: (fDS >= 2 && fDS <= 6) ? 'pass' : 'warn', unit: '%' },
          { label: 'Float bulk density', formula: 'ρ_float = ρ_w(1−φ) + ρ_air×φ  φ = min(0.15, 0.05+A/S×0.5)', substitution: `φ = ${phi_air2.toFixed(3)}, ρ_float = ${rho2.toFixed(0)}×${(1-phi_air2).toFixed(3)} + ${rhoAir2.toFixed(2)}×${phi_air2.toFixed(3)} = ${floatDens} kg/m³`, result: f(floatDens, 0), status: 'pass', unit: 'kg/m³' },
          { label: 'Wet float volume rate', formula: 'V_float = m_captured / (DS%/100) / ρ_float  m³/h', substitution: `V_float = ${f(capMass, 4)} / ${(fDS/100).toFixed(4)} / ${floatDens} = ${f(floatVol2, 5)} m³/h`, result: f(floatVol2, 5), status: 'pass', unit: 'm³/h' },
          { label: 'Blanket accumulation rate', formula: 'h_accum = V_float / A_DAF × 1000  mm/h', substitution: `h_accum = ${f(floatVol2, 5)} / ${f(surfaceArea, 1)} × 1000 = ${f(accumRate, 2)} mm/h`, result: f(accumRate, 2), status: accumRate < 15 ? 'pass' : accumRate < 40 ? 'warn' : 'fail', unit: 'mm/h' },
          { label: 'Scraper flight count', formula: 'n = max(2, ⌈L / 1.5⌉)  — flights at 1.5 m spacing', substitution: `n = max(2, ⌈${f(p.length, 1)} / 1.5⌉) = ${nFlights} flights`, result: nFlights.toString(), status: 'pass', unit: 'flights' },
          { label: 'Scraper travel speed', formula: 'v = 0.20 + f(A/S) + f(accum) + f(OG)  m/min [0.15–0.60]', substitution: `v = 0.20 + ${sbAS2.toFixed(3)} + ${sbAccum.toFixed(3)} + ${sbOG.toFixed(3)} = ${f(vScr, 3)} m/min`, result: f(vScr, 3), status: (vScr >= 0.15 && vScr <= 0.50) ? 'pass' : 'warn', unit: 'm/min' },
          { label: 'Passes per hour', formula: 'pph = v × 60 / L', substitution: `pph = ${f(vScr, 3)} × 60 / ${f(p.length, 1)} = ${f(pph, 2)} passes/h`, result: f(pph, 2), status: pph >= 2 ? 'pass' : pph >= 1 ? 'warn' : 'fail', unit: '/h' },
          { label: 'Operating blanket (steady state)', formula: 'h_op = accum_rate / (pph × η_scrape)  η=0.87', substitution: `h_op = ${f(accumRate, 2)} / (${f(pph, 2)} × 0.87) = ${f(opBlanket, 1)} mm`, result: f(opBlanket, 1), status: opBlanket <= 80 ? 'pass' : opBlanket <= 150 ? 'warn' : 'fail', unit: 'mm' },
          { label: 'Blade immersion depth', formula: 'h_blade = h_op × 2 + 25 mm  [50–250 mm]', substitution: `h_blade = ${f(opBlanket, 1)} × 2 + 25 = ${f(bladeDepth, 0)} mm`, result: f(bladeDepth, 0), status: 'pass', unit: 'mm' },
          { label: 'Scraper motor power', formula: 'P = C_D(1.8)×ρ_float×v²×A_blade×n×1.5×v / η_chain(0.65)', substitution: `P = 1.8×${floatDens}×${f(vScrMs,5)}²×${f(AB,4)}×${nFlights}×1.5×${f(vScrMs,5)} / 0.65 = ${f(motorKW, 4)} kW`, result: f(motorKW, 4), status: motorKW < 2 ? 'pass' : 'warn', unit: 'kW' },
          { label: 'Scraper status', formula: 'Blanket ≤80 mm = optimal | ≤150 mm = adequate | ≤250 mm = marginal | >250 mm = overloaded', substitution: s.scraperStatusDetail, result: s.scraperStatus.toUpperCase(), status: s.scraperStatus === 'optimal' ? 'pass' : s.scraperStatus === 'adequate' ? 'pass' : s.scraperStatus === 'marginal' ? 'warn' : 'fail' },
        ];
      })(),
      warnings: s.scraperStatus === 'overloaded' ? [`Scraper system overloaded — operating blanket ${s.operatingBlanket_mm.toFixed(0)} mm exceeds safe limit. Float re-entrainment into effluent is likely. Increase scraper speed, add flights, or reduce loading.`] :
               s.scraperStatus === 'marginal'    ? [`Scraper marginal — blanket ${s.operatingBlanket_mm.toFixed(0)} mm. Recommend higher speed or additional flight.`] : undefined,
    },
    /* ── Step 13: Design Status & Compliance ─────────────── */
    {
      title: 'Step 13 — Design Status & Compliance Check',
      icon: ShieldAlert,
      equations: [
        { label: 'Overall design verdict', formula: 'Based on HLR, A/S, TSS removal, effluent pH, power', substitution: s.statusDetail, result: s.status.toUpperCase(), status: s.status === 'optimal' ? 'pass' : s.status === 'acceptable' ? 'warn' : 'fail' },
        { label: 'Effluent TSS', formula: 'TSS_eff = TSS_in × (1 − η_TSS)', substitution: `TSS_eff = ${f(p.tss, 0)} × (1 − ${f(s.tssRemoval, 4)}) = ${f(s.effluentTSS, 1)} mg/L`, result: f(s.effluentTSS, 1), status: s.effluentTSS < 50 ? 'pass' : s.effluentTSS < 150 ? 'warn' : 'fail', unit: 'mg/L' },
        { label: 'Effluent O&G', formula: 'OG_eff = OG_in × (1 − η_OG)', substitution: `OG_eff = ${f(p.og, 0)} × (1 − ${f(s.ogRemoval, 4)}) = ${f(s.effluentOG, 1)} mg/L`, result: f(s.effluentOG, 1), status: s.effluentOG < 15 ? 'pass' : s.effluentOG < 30 ? 'warn' : 'fail', unit: 'mg/L' },
        { label: 'Effluent BOD', formula: 'BOD_eff = BOD_in × (1 − η_BOD)', substitution: `BOD_eff = ${f(p.bod, 0)} × (1 − ${f(s.bodRemoval, 4)}) = ${f(s.effluentBOD, 1)} mg/L`, result: f(s.effluentBOD, 1), status: 'pass', unit: 'mg/L' },
        { label: 'Effluent pH', formula: 'pH_eff = pH_in − ΔpH_coag', substitution: `pH_eff = ${f(p.ph, 1)} − ${f(s.phDrop, 4)} = ${f(s.effluentPH, 2)}`, result: f(s.effluentPH, 2), status: s.effluentPH > 6.5 ? 'pass' : s.effluentPH > 6.0 ? 'warn' : 'fail' },
      ],
    },
    /* ── Step 14: Regulatory Compliance (conditional) ───── */
    ...(() => {
      if (!regulatoryBody) return [];
      const std = REGULATORY_STANDARDS[regulatoryBody];
      if (!std) return [];
      type Eq = { label: string; formula: string; substitution: string; result: string; unit?: string; status: 'pass' | 'warn' | 'fail' };
      const eqns: Eq[] = [];
      // Applicable legislation (informational row)
      eqns.push({
        label: 'Applicable legislation',
        formula: std.legislation[0],
        substitution: std.legislation.slice(1).join(' · ') || '—',
        result: regulatoryBody,
        status: 'pass',
      });
      // TSS
      const tssStatus: 'pass'|'warn'|'fail' = s.effluentTSS <= std.tss ? 'pass' : s.effluentTSS <= std.tss * 1.33 ? 'warn' : 'fail';
      eqns.push({ label: 'TSS — consent limit', formula: `TSS_eff ≤ ${std.tss} mg/L`, substitution: `${f(s.effluentTSS, 1)} mg/L vs. statutory limit ${std.tss} mg/L`, result: f(s.effluentTSS, 1), unit: 'mg/L', status: tssStatus });
      // BOD
      const bodStatus: 'pass'|'warn'|'fail' = s.effluentBOD <= std.bod ? 'pass' : s.effluentBOD <= std.bod * 1.33 ? 'warn' : 'fail';
      eqns.push({ label: 'BOD — consent limit', formula: `BOD_eff ≤ ${std.bod} mg/L`, substitution: `${f(s.effluentBOD, 1)} mg/L vs. statutory limit ${std.bod} mg/L`, result: f(s.effluentBOD, 1), unit: 'mg/L', status: bodStatus });
      // COD
      if (std.cod !== null) {
        const codStatus: 'pass'|'warn'|'fail' = s.effluentCOD <= std.cod ? 'pass' : s.effluentCOD <= std.cod * 1.33 ? 'warn' : 'fail';
        eqns.push({ label: 'COD — consent limit', formula: `COD_eff ≤ ${std.cod} mg/L`, substitution: `${f(s.effluentCOD, 1)} mg/L vs. statutory limit ${std.cod} mg/L`, result: f(s.effluentCOD, 1), unit: 'mg/L', status: codStatus });
      }
      // O&G
      const ogStatus: 'pass'|'warn'|'fail' = s.effluentOG <= std.og ? 'pass' : s.effluentOG <= std.og * 1.33 ? 'warn' : 'fail';
      eqns.push({ label: 'O&G — consent limit', formula: `OG_eff ≤ ${std.og} mg/L`, substitution: `${f(s.effluentOG, 1)} mg/L vs. statutory limit ${std.og} mg/L`, result: f(s.effluentOG, 1), unit: 'mg/L', status: ogStatus });
      // pH
      const phOk = s.effluentPH >= std.phMin && s.effluentPH <= std.phMax;
      const phClose = s.effluentPH >= std.phMin - 0.3 && s.effluentPH <= std.phMax + 0.3;
      eqns.push({ label: 'pH — permitted range', formula: `${std.phMin} ≤ pH_eff ≤ ${std.phMax}`, substitution: `pH_eff = ${f(s.effluentPH, 2)} (permitted: ${std.phMin}–${std.phMax})`, result: f(s.effluentPH, 2), status: phOk ? 'pass' : phClose ? 'warn' : 'fail' });
      // NH₃-N (advisory only — DAF does not biologically remove ammonia)
      if (std.nh3n !== undefined) {
        eqns.push({ label: `NH₃-N — consent limit (${std.nh3n} mg/L)`, formula: 'DAF does not directly remove ammonia', substitution: 'Further biological nitrification treatment required to meet NH₃-N consent', result: '⚠ N/A', status: 'warn' });
      }
      // TP (advisory only)
      if (std.tp !== undefined) {
        eqns.push({ label: `Total P — consent limit (${std.tp} mg/L)`, formula: 'Coagulant dosing removes ~40–70 % of TP via precipitation', substitution: 'Confirm phosphorus removal via jar testing; enhanced coagulation may be required', result: '⚠ Review', status: 'warn' });
      }
      // Overall verdict
      const anyFail = eqns.some(e => e.status === 'fail');
      const anyWarn = eqns.some(e => e.status === 'warn');
      const verdict = anyFail ? 'LIKELY NON-COMPLIANT' : anyWarn ? 'CONDITIONAL COMPLIANCE' : 'COMPLIANT';
      const verdictStatus: 'pass'|'warn'|'fail' = anyFail ? 'fail' : anyWarn ? 'warn' : 'pass';
      eqns.push({ label: 'Overall statutory compliance', formula: 'All key parameters within statutory consent limits', substitution: `${regulatoryBody} — assessed against primary discharge standards`, result: verdict, status: verdictStatus });
      return [{
        title: `Step 14 — Statutory Compliance: ${regulatoryBody}`,
        icon: Shield,
        equations: eqns,
        warnings: anyFail
          ? [`One or more effluent parameters exceed statutory discharge limits for ${regulatoryBody}. Consent breach risk — review DAF sizing, chemical dosing, and consider secondary treatment.`]
          : anyWarn
          ? [`Some effluent parameters are near statutory limits for ${regulatoryBody}. Verify with site-specific consent conditions and final jar test data.`]
          : undefined,
      }];
    })(),
  ];
}

/* ─── Validation Section Block (for inline modal) ─── */
const ValidationSectionBlock = memo(function ValidationSectionBlock({ section, idx }: { section: ValidationSection; idx: number }) {
  const [open, setOpen] = useState(idx < 3);
  const Icon = section.icon;
  const pass = section.equations.filter(e => e.status === 'pass').length;
  const warn = section.equations.filter(e => e.status === 'warn').length;
  const fail = section.equations.filter(e => e.status === 'fail').length;
  return (
    <div className="overflow-hidden rb-card">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between p-3 hover:bg-rb-elevated transition-colors">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
            <Icon className="w-3.5 h-3.5 text-sky-400" />
          </div>
          <span className="text-xs font-bold text-white text-left">{section.title}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          {pass > 0 && <span className="text-[10px] font-bold text-emerald-400">{pass}✓</span>}
          {warn > 0 && <span className="text-[10px] font-bold text-amber-400">{warn}!</span>}
          {fail > 0 && <span className="text-[10px] font-bold text-rose-400">{fail}✗</span>}
          <ChevronRight className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} />
        </div>
      </button>
      {open && (
        <div className="p-3 border-t border-slate-800 space-y-2">
          {section.warnings?.map((w, i) => (
            <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-400">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{w}</span>
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {section.equations.map((eq, i) => (
              <div key={i} className="p-2.5 space-y-1.5 rb-inner-card-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[11px] font-bold text-slate-300 leading-tight">{eq.label}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={`text-[11px] font-bold ${eq.status === 'pass' ? 'text-emerald-400' : eq.status === 'warn' ? 'text-amber-400' : 'text-rose-400'}`}>
                      {eq.result}{eq.unit ? ` ${eq.unit}` : ''}
                    </span>
                    {eq.status === 'pass'
                      ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                      : eq.status === 'warn'
                      ? <AlertCircle className="w-3 h-3 text-amber-400 shrink-0" />
                      : <AlertCircle className="w-3 h-3 text-rose-400 shrink-0" />}
                  </div>
                </div>
                <div className="font-mono text-[10px] text-slate-500 bg-rb-surface rounded px-2 py-1 leading-relaxed break-all">{eq.formula}</div>
                <div className="font-mono text-[10px] text-sky-400 bg-rb-surface rounded px-2 py-1 leading-relaxed break-all">{eq.substitution}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});


/* ─── DAF Validation Modal (opens from Save button) ──── */
function DAFValidationModal({ isOpen, onClose, savedParams }: {
  isOpen: boolean; onClose: () => void; savedParams: DAFParams | null;
}) {
  const [showPrint, setShowPrint] = useState(false);
  const [reportNumber, setReportNumber] = useState('');
  const { user } = useAuth();
  const { currentCustomer, activeStandard } = useSession();
  // All hooks MUST be called before any early return (React rules of hooks).
  // Memoize the expensive physics + report builder so re-renders (e.g. modal scroll,
  // print toggle) don't rerun the full calcDAF + buildDAFReportSections pipeline.
  const regCtx = useMemo<{ regulatoryBody?: string; customerName?: string; sampleName?: string; siteName?: string } | null>(() => {
    try { return JSON.parse(localStorage.getItem('daf-regulatory-context') || 'null'); } catch { return null; }
  }, []); // only read once on mount
  const modalStdCode = (currentCustomer as any)?.standard || activeStandard || 'UK_EA';
  const regBody = regCtx?.regulatoryBody || STANDARD_TO_REG_BODY[modalStdCode] || '';
  const regStd = regBody ? REGULATORY_STANDARDS[regBody] : null;
  const size = useMemo(() => savedParams ? calcDAF(savedParams) : null, [savedParams]);
  const sections = useMemo(() => (savedParams && size) ? buildDAFReportSections(savedParams, size, regBody || undefined) : [], [savedParams, size, regBody]);
  if (!isOpen || !savedParams || !size) return null;
  // Build prepared-by string from logged-in user
  const preparedBy = user
    ? (user.name || user.username)
    : 'Reynolds & Bauhm Ltd';
  const engineerRef = user ? `User: ${user.username}` : undefined;
  const passCount = sections.reduce((a, s) => a + s.equations.filter(e => e.status === 'pass').length, 0);
  const warnCount = sections.reduce((a, s) => a + s.equations.filter(e => e.status === 'warn').length, 0);
  const failCount = sections.reduce((a, s) => a + s.equations.filter(e => e.status === 'fail').length, 0);
  const total = passCount + warnCount + failCount;
  const statusColour = size.status === 'optimal' ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5'
    : size.status === 'acceptable' ? 'text-cyan-400 border-cyan-500/30 bg-cyan-500/5'
    : size.status === 'overloaded' ? 'text-rose-400 border-rose-500/30 bg-rose-500/5'
    : 'text-amber-400 border-amber-500/30 bg-amber-500/5';
  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4">
        <div className="relative w-full max-w-[1100px] bg-[#060f1e] rounded-2xl border border-slate-700 shadow-2xl my-8">
          {/* ── Modal header ── */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 border-b border-slate-800 bg-[#060f1e]/95 backdrop-blur-sm rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                <Microscope className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-white leading-tight">DAF Process Validation Report</h2>
                <p className="text-[10px] text-slate-500 leading-tight">
                  Reynolds &amp; Bauhm Ltd — Wastewater Treatment Engineering
                  {regCtx?.customerName ? ` · ${regCtx.customerName}` : ''}
                  {preparedBy !== 'Reynolds & Bauhm Ltd' ? ` · Prepared by: ${preparedBy}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-1.5 text-[10px]">
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold">{passCount}/{total} Pass</span>
                {warnCount > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 font-bold">{warnCount} Warn</span>}
                {failCount > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 font-bold">{failCount} Fail</span>}
              </div>
              <button
                onClick={() => {
                  setReportNumber(buildRegulatoryReportNumber('DAF', modalStdCode));
                  setShowPrint(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-700/80 text-white border border-slate-600 hover:bg-slate-600 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Print / PDF
              </button>
              <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* ── Content ── */}
          <div className="p-4 space-y-3">
            {/* Status banner */}
            <div className={`rounded-xl p-3.5 border flex items-center gap-3 ${statusColour}`}>
              <div className={`text-xs font-bold tracking-wider uppercase`}>{size.status}</div>
              <div className="w-px h-4 bg-current opacity-30" />
              <p className="text-xs opacity-80">{size.statusDetail}</p>
              <div className="ml-auto hidden md:flex items-center gap-4 text-xs shrink-0">
                <span>HLR: <strong>{fmt(size.hlr, 1)} m³/m²·h</strong></span>
                <span>A/S: <strong>{fmt(size.airToSolids, 4)}</strong></span>
                <span>TSS removal: <strong>{fmt(size.tssRemoval * 100, 0)}%</strong></span>
                <span>G-value: <strong>{fmt(size.flocGValue, 0)} s⁻¹</strong></span>
                <span>Gt: <strong>{Math.round(size.flocGt).toLocaleString()}</strong></span>
              </div>
            </div>

            {/* Effluent quality strip */}
            <div className="grid grid-cols-5 gap-2">
              {[
                ['TSS', `${fmt(savedParams.tss, 0)} → ${fmt(size.effluentTSS, 0)} mg/L`, size.effluentTSS < 50],
                ['O&G', `${fmt(savedParams.og, 0)} → ${fmt(size.effluentOG, 0)} mg/L`, size.effluentOG < 15],
                ['BOD', `${fmt(savedParams.bod, 0)} → ${fmt(size.effluentBOD, 0)} mg/L`, size.effluentBOD < 300],
                ['COD', `${fmt(savedParams.cod, 0)} → ${fmt(size.effluentCOD, 0)} mg/L`, size.effluentCOD < 600],
                ['pH', `${fmt(savedParams.ph, 1)} → ${fmt(size.effluentPH, 2)}`, size.effluentPH > 6.0],
              ].map(([label, val, pass]) => (
                <div key={label as string} className={`bg-rb-surface rounded-lg p-2 border text-center ${pass ? 'border-emerald-500/20' : 'border-rose-500/20'}`}>
                  <p className="text-[10px] text-slate-500 uppercase font-bold">{label}</p>
                  <p className={`text-[10px] font-bold mt-0.5 ${pass ? 'text-emerald-400' : 'text-rose-400'}`}>{val as string}</p>
                </div>
              ))}
            </div>

            {/* Step-by-step sections */}
            {sections.map((section, idx) => (
              <ValidationSectionBlock key={section.title} section={section} idx={idx} />
            ))}

            {/* Design notes */}
            <div className="p-4 space-y-2 rb-card">
              <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 uppercase tracking-wider">
                <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />Design Notes &amp; Warnings ({size.designNotes.length})
              </h3>
              <div className="space-y-1.5">
                {size.designNotes.map((note, i) => (
                  <div key={i} className={`text-xs p-2 rounded-lg leading-relaxed ${
                    note.startsWith('⚠️') || note.startsWith('❌')
                      ? 'bg-rose-500/5 border border-rose-500/20 text-rose-400'
                      : note.startsWith('ℹ️')
                      ? 'bg-sky-500/5 border border-sky-500/20 text-sky-400'
                      : 'bg-emerald-500/5 border border-emerald-500/20 text-emerald-400'
                  }`}>{note}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print/PDF modal */}
      <ValidationPrintReport
        isOpen={showPrint}
        onClose={() => setShowPrint(false)}
        equipmentName="Dissolved Air Flotation (DAF)"
        sampleName={regCtx?.sampleName || undefined}
        siteName={regCtx?.siteName || undefined}
        customerName={regCtx?.customerName || undefined}
        date={new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        reportNumber={reportNumber}
        sections={sections}
        passCount={passCount}
        warnCount={warnCount}
        failCount={failCount}
        regulatoryBody={regBody || undefined}
        regulatoryLegislation={regStd?.legislation}
        preparedBy={preparedBy}
        engineerRef={engineerRef}
        rawParams={{
          flowRate: savedParams.flowRate, tss: savedParams.tss, og: savedParams.og,
          bod: savedParams.bod, cod: savedParams.cod, ph: savedParams.ph, temp: savedParams.temp,
          alkalinity: savedParams.alkalinity, turbidity: savedParams.turbidity,
          conductivity: savedParams.conductivity, hardness: savedParams.hardness,
          nh3n: savedParams.nh3n, tp: savedParams.tp,
        }}
      />
    </>
  );
}

/* ─── Science Validation Accordion ───────────────────── */
const ScienceAccordion = memo(function ScienceAccordion({ params, size }: { params: DAFParams; size: DAFSize }) {
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (id: string) => setOpen(open === id ? null : id);

  const Item = ({ id, title, icon: Icon, color, children }: any) => (
    <div className="rounded-xl border border-slate-800 overflow-hidden">
      <button onClick={() => toggle(id)} className="w-full flex items-center justify-between p-3 bg-rb-elevated hover:bg-rb-surface transition-colors">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color }} />
          <span className="text-xs font-bold text-slate-300">{title}</span>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${open === id ? 'rotate-90' : ''}`} />
      </button>
      <AnimatePresence>
        {open === id && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="p-3 bg-rb-surface space-y-2 text-xs text-slate-400">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const rhoWater = 998 - 0.2 * (params.temp - 20);
  const N_rps = params.flocMotorSpeed / 60;
  // Issue fix: use regime-dependent Np from calcDAF result (not hardcoded 0.35)
  // size.flocNpUsed is the actual Np applied in calcDAF, accounting for impeller type and Re
  const Np_display = size.flocNpUsed;
  const P_calc = Np_display * rhoWater * Math.pow(N_rps, 3) * Math.pow(params.flocImpellerDia, 5);

  return (
    <div className="p-4 space-y-3 rb-card">
      <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-sky-400" />Science Validation — Click to Expand</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Item id="coag" title="1. Coagulation Chemistry" icon={FlaskConical} color="#D32F2F">
          <div className="font-mono bg-rb-elevated rounded p-2 text-[10px]">
            {params.coagulantType === 'ferric' ? 'FeCl₃ + 3H₂O → Fe(OH)₃↓ + 3HCl' : params.coagulantType === 'alum' ? 'Al³⁺ + 3H₂O → Al(OH)₃↓ + 3H⁺' : 'PAC → Al(OH)₃↓ (pre-hydrolysed)'}
          </div>
          <p>pH drop = dose × {params.coagulantType === 'ferric' ? '0.001' : params.coagulantType === 'alum' ? '0.0006' : '0.0002'} / (1 + alkalinity/100) = {fmt(size.phDrop, 4)}</p>
          <p>Precipitate ratio = {params.coagulantType === 'ferric' ? '2.7' : params.coagulantType === 'alum' ? '2.9' : '1.5'} kg/kg coagulant</p>
          <p>Precipitate mass = {fmt(params.coagulantDose, 0)} mg/L × {params.coagulantType === 'ferric' ? '2.7' : params.coagulantType === 'alum' ? '2.9' : '1.5'} × {fmt(params.flowRate, 0)} m³/d ÷ 1000 = <span className="text-amber-400 font-bold">{fmt(size.coagPrecipitate, 1)} kg/d</span></p>
          <p>Effluent pH = {fmt(params.ph, 1)} − {fmt(size.phDrop, 4)} = <span className="text-emerald-400 font-bold">{fmt(size.effluentPH, 2)}</span></p>
        </Item>

        <Item id="waterchem" title="2. Water Chemistry Effects" icon={Beaker} color="#3b82f6">
          <p className="font-mono bg-rb-elevated rounded p-2 text-[10px]">f_cond = max(0.80, 1 − 0.03 × log₁₀(cond/1000))</p>
          <p>Conductivity = {fmt(params.conductivity, 0)} μS/cm</p>
          <p>f_cond = {params.conductivity > 1000 ? `max(0.80, 1 − 0.03 × log₁₀(${fmt(params.conductivity, 0)}/1000)) = ${fmt(size.conductivityFactor, 3)}` : '1.000 (conductivity ≤ 1000, no effect)'}</p>
          <p>Status: <span className={size.conductivityFactor >= 0.95 ? 'text-emerald-400 font-bold' : size.conductivityFactor >= 0.90 ? 'text-amber-400 font-bold' : 'text-rose-400 font-bold'}>{size.conductivityFactor >= 0.95 ? 'Minimal effect' : size.conductivityFactor >= 0.90 ? 'Moderate shielding' : 'Strong shielding — increase coagulant dose'}</span></p>
          <p className="font-mono bg-rb-elevated rounded p-2 text-[10px]">f_hard = 0.95 + 0.05 × min((hardness−300)/200, 1)</p>
          <p>Hardness = {fmt(params.hardness, 0)} mg/L CaCO₃</p>
          <p>f_hard = {params.hardness < 50 ? `0.95 (low hardness — poor sweep floc)` : params.hardness > 300 ? `1.0 + 0.05 × min((${fmt(params.hardness, 0)}−300)/200, 1) = ${fmt(size.hardnessFactor, 3)}` : '1.000 (optimal range)'}</p>
          <p>Combined coagulation modifier = <span className="text-sky-400 font-bold">{fmt(size.conductivityFactor * size.hardnessFactor, 3)}</span></p>
          <p>Adjusted coag boost = {fmt(params.coagulantType === 'ferric' ? 0.30 : params.coagulantType === 'alum' ? 0.20 : 0.15, 2)} × {fmt(size.conductivityFactor, 3)} × {fmt(size.hardnessFactor, 3)} = {fmt((params.coagulantType === 'ferric' ? 0.30 : params.coagulantType === 'alum' ? 0.20 : 0.15) * size.conductivityFactor * size.hardnessFactor, 3)}</p>
        </Item>

        <Item id="floc" title="3. Flocculation Physics" icon={Wind} color="#8b5cf6">
          <p className="font-mono bg-rb-elevated rounded p-2 text-[10px]">P = Np × ρ × N³ × D⁵</p>
          <p>P = {fmt(Np_display, 4)} (Np, {size.flocRegime}) × {fmt(rhoWater, 0)} kg/m³ × ({fmt(N_rps, 3)} s⁻¹)³ × ({fmt(params.flocImpellerDia, 2)} m)⁵</p>
          <p>Power input = <span className="text-sky-400 font-bold">{fmt(P_calc, 1)} W = {fmt(P_calc / 1000, 3)} kW</span></p>
          <p className="font-mono bg-rb-elevated rounded p-2 text-[10px]">G = √(P / μV)</p>
          <p>G = √({fmt(P_calc, 1)} W / ({fmt(size.flocGValue > 0 ? P_calc / Math.pow(size.flocGValue, 2) : 0.001, 5)} Pa·s × {fmt(params.flocTankVolume, 1)} m³))</p>
          <p>G-value = <span className="text-emerald-400 font-bold">{fmt(size.flocGValue, 0)} s⁻¹</span> (optimal: 20–100 s⁻¹)</p>
          <p>Tip speed = π × {fmt(params.flocImpellerDia, 2)} × {fmt(N_rps, 3)} = <span className="text-emerald-400 font-bold">{fmt(size.flocTipSpeed, 2)} m/s</span> (max: 5 m/s)</p>
        </Item>

        <Item id="bubble" title="4. Bubble Generation" icon={CircleDot} color="#0288D1">
          <p className="font-mono bg-rb-elevated rounded p-2 text-[10px]">C_sat(T) = C₂₀ × exp[−1300(1/T − 1/293.15)]</p>
          <p>C_sat @ {fmt(params.temp, 0)}°C = 25 × exp[−1300(1/{fmt(params.temp + 273.15, 1)} − 1/293.15)] = {fmt(size.airSolubility / ((params.saturatorPressure + 1.013) * params.saturatorEfficiency / 100), 1)} mg/L</p>
          <p>P_abs = {fmt(params.saturatorPressure, 1)} + 1.013 = {fmt(params.saturatorPressure + 1.013, 3)} bar abs × {fmt(params.saturatorEfficiency, 0)}% η: <span className="text-sky-400 font-bold">{fmt(size.airSolubility, 1)} mg/L</span></p>
          <p className="font-mono bg-rb-elevated rounded p-2 text-[10px]">d_bubble = 80 − 8(P_sat − 4)</p>
          <p>d_bubble = 80 − 8({fmt(params.saturatorPressure, 1)} − 4) = <span className="text-sky-400 font-bold">{fmt(size.bubbleSize, 0)} μm</span></p>
          <p className="font-mono bg-rb-elevated rounded p-2 text-[10px]">v = g(ρ_w − ρ_a)d² / 18μ</p>
          <p>Rise velocity = <span className="text-sky-400 font-bold">{fmt(size.bubbleRise, 1)} m/h</span></p>
        </Item>

        <Item id="flotation" title="5. Flotation Kinetics" icon={Waves} color="#10b981">
          <p className="font-mono bg-rb-elevated rounded p-2 text-[10px]">A/S = m_air / m_solids</p>
          <p>Air mass = {fmt(size.airSolubility, 1)} mg/L × {fmt(params.flowRate * params.recycleRate / 100 / 24, 1)} m³/h ÷ 1000 = {fmt(size.airDemand / 24, 2)} kg/h</p>
          <p>Solids mass = {fmt(params.tss, 0)} mg/L × {fmt(params.flowRate / 24, 1)} m³/h ÷ 1000 = {fmt(params.tss * params.flowRate / 24 / 1000, 2)} kg/h</p>
          <p>A/S ratio = <span className={size.asStatus === 'optimal' ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}>{fmt(size.airToSolids, 4)}</span> (optimal: 0.03–0.08)</p>
          <p>HLR = {fmt(params.flowRate / 24, 1)} m³/h ÷ {fmt(params.length * params.width, 1)} m² = <span className="text-sky-400 font-bold">{fmt(size.hlr, 1)} m³/m²·h</span></p>
          <p>Retention = {fmt(params.length * params.width * params.depth, 1)} m³ ÷ {fmt(params.flowRate / 24, 1)} m³/h × 60 = <span className="text-sky-400 font-bold">{fmt(size.actualRetention, 0)} min</span></p>
        </Item>

        <Item id="sludge" title="6. Sludge Production" icon={Cylinder} color="#f59e0b">
          <p className="font-mono bg-rb-elevated rounded p-2 text-[10px]">m_sludge = Σ(C_i × Q × η_i) + m_precip</p>
          <p>TSS captured = {fmt(params.tss, 0)} × {fmt(params.flowRate, 0)} × {fmt(size.tssRemoval, 2)} ÷ 1000 = {fmt(size.sludgeTSS, 1)} kg/d</p>
          <p>O&G captured = {fmt(params.og, 0)} × {fmt(params.flowRate, 0)} × {fmt(size.ogRemoval, 2)} ÷ 1000 = {fmt(size.sludgeOG, 1)} kg/d</p>
          <p>Chemical precip = {fmt(size.sludgeChem, 1)} kg/d</p>
          <p>Total dry solids = <span className="text-amber-400 font-bold">{fmt(size.sludgeTotal, 1)} kg/d</span></p>
          <p>Wet sludge @ 3% DS = {fmt(size.sludgeTotal, 1)} ÷ 30 = <span className="text-emerald-400 font-bold">{fmt(size.sludgeVolume, 1)} m³/d</span></p>
          <p>Blanket = ({fmt(size.sludgeVolume, 2)} m³/d ÷ {fmt(params.length * params.width, 1)} m² ÷ 24 h) × 1000 × 2 = <span className="text-amber-400 font-bold">{fmt(size.blanketThickness, 0)} mm</span></p>
        </Item>
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════ */
/*  MAIN PAGE COMPONENT                                    */
/* ═══════════════════════════════════════════════════════ */
export interface DbSample {
  id: number;
  sample_name: string;
  customer_name?: string;
  customer_id?: number | null;
  site_id?: number | null;
  params?: Record<string, number>;
  created_at?: string;
  /** 'influent' | 'effluent' — only influent samples are permitted in DAF assessment */
  sampleType?: string;
  sample_type?: string;
  /** 'own' | 'shared' — 'shared' means assigned by an admin */
  _source?: string;
}

// ─── Drift-detection key lists ────────────────────────────────────────────────
// Used to tell the "Recommended Settings" button whether the user has modified
// the DAF design parameters (design drift) or changed the influent quality
// (influent drift — recommendations become outdated).
const DAF_DESIGN_KEYS: (keyof DAFParams)[] = [
  'length', 'width', 'depth', 'freeboard',
  'airPressure', 'recycleRate', 'saturatorPressure', 'saturatorEfficiency',
  'flocTankVolume', 'flocNumCompartments', 'flocRetentionTime', 'flocMotorPower',
  'flocMotorSpeed', 'flocImpellerDia', 'flocImpellerType',
  'coagulantType', 'coagulantDose', 'flocculantType', 'flocculantDose',
  'targetRemovalTSS', 'targetRemovalOG',
];
const DAF_INFLUENT_KEYS: (keyof DAFParams)[] = [
  'flowRate', 'bod', 'cod', 'tss', 'og', 'ph', 'temp',
  'alkalinity', 'turbidity', 'conductivity', 'hardness', 'nh3n', 'tp',
];

export default function DAFSpecPage() {
  const { currentCustomer } = useSession();
  const { user } = useAuth();
  const [params, setParams] = useState<DAFParams>(PROFILES[0].defaults);

  // ─── Save / dirty tracking ─────────────────────────────────────────────────
  // `lastSavedParams` is the snapshot of params at the last explicit save.
  // isDirty = params have changed since last save.  Initialise from localStorage
  // so on first load: if a config is already stored, lastSavedParams = that config
  // and isDirty = false (nothing changed yet).
  const [lastSavedParams, setLastSavedParams] = useState<DAFParams | null>(() => {
    try {
      const raw = localStorage.getItem('daf-params-v2');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const hasSavedConfig = lastSavedParams !== null;
  // Dirty = have params drifted from the last-saved snapshot?
  // Use key-by-key comparison — avoids two JSON.stringify calls on every slider tick.
  const isDirty = useMemo(() => {
    if (!lastSavedParams) return false;
    const keys = Object.keys(params) as (keyof DAFParams)[];
    return keys.some(k => (params as any)[k] !== (lastSavedParams as any)[k]);
  }, [params, lastSavedParams]);

  const [showValidationReport, setShowValidationReport] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false); // 2s flash on save
  const [showSavePrompt, setShowSavePrompt] = useState(false); // prompt before validate when dirty
  const [showGoToEffluent, setShowGoToEffluent] = useState(false);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'flow' | 'chemistry' | 'air' | 'hydraulics' | 'pumps' | 'scraper'>('flow');
  const [showSampleLoader, setShowSampleLoader] = useState(false);
  const [showPumpImpact, setShowPumpImpact] = useState(false);
  const [profileIndex, setProfileIndex] = useState(0);
  const [isOptimalActive, setIsOptimalActive] = useState(false);

  // ─── Recommended-settings tracking ────────────────────────────────────────
  // `recommendedParams` is a snapshot of params at the moment "Recommended
  // Settings" was last applied. Used to detect drift (design params changed
  // by the user) or influent changes (quality sliders moved after apply).
  // Both snapshots are persisted to localStorage so the state survives reload.
  const [recommendedParams, setRecommendedParams] = useState<DAFParams | null>(() => {
    try { return JSON.parse(localStorage.getItem('daf-recommended-v2') || 'null'); } catch { return null; }
  });
  const [recommendedForSample, setRecommendedForSample] = useState<string>(() => {
    return localStorage.getItem('daf-recommended-sample') || '';
  });

  // Design drift — DAF sizing / air / mixing / chemistry changed after apply
  const designDrift = useMemo(() => {
    if (!recommendedParams || !isOptimalActive) return false;
    return DAF_DESIGN_KEYS.some(k => params[k] !== recommendedParams[k]);
  }, [params, recommendedParams, isOptimalActive]);

  // Influent drift — quality sliders moved after apply (recommendations may be stale)
  const influentChanged = useMemo(() => {
    if (!recommendedParams || !isOptimalActive) return false;
    return DAF_INFLUENT_KEYS.some(k => {
      const a = params[k], b = recommendedParams[k];
      if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) > 0.001;
      return a !== b;
    });
  }, [params, recommendedParams, isOptimalActive]);

  // ─── Database Samples ───
  const [dbSamples, setDbSamples] = useState<DbSample[]>([]);
  const [selectedSampleId, setSelectedSampleId] = useState<number | ''>('');
  const [dilutionFactor, setDilutionFactor] = useState(1);
  const [baseSampleParams, setBaseSampleParams] = useState<Partial<DAFParams> | null>(null);

  // ─── Pump Recommendation Engine ───
  const [allPumpModels, setAllPumpModels] = useState<RawPumpModel[]>([]);
  const [recommendedPumps, setRecommendedPumps] = useState<ScoredPump[]>([]);
  const [recsLoading, setRecsLoading] = useState(false);

  const size = useMemo(() => calcDAF(params), [params]);
  const pumpImpact = useMemo(() => calcWhiteWaterImpact(params, size), [params, size]);

  // Load samples — local storage first, then merge with database if available
  // ALWAYS scoped to the active session customer (currentCustomer) if one is set.
  const loadDbSamples = useCallback(async () => {
    // Resolve the DB integer ID — serverId takes priority over local timestamp id
    const sessionCustId = currentCustomer
      ? ((currentCustomer as any).serverId
          ?? (currentCustomer.id.length < 13 && Number(currentCustomer.id) > 0 ? Number(currentCustomer.id) : 0))
      : 0;

    const currentUserId = (() => {
      try {
        const raw = localStorage.getItem('rb_session_v2');
        if (!raw) return 0;
        const sess = JSON.parse(raw);
        return sess?.userProfile?.id ? Number(sess.userProfile.id) : 0;
      } catch { return 0; }
    })();
    const localCustomers: Array<{ id: number; name: string; company?: string | null }> = (() => {
      try { return JSON.parse(localStorage.getItem('rb_customers') || '[]'); } catch { return []; }
    })();
    const custName = (id: number | null) => {
      if (!id) return undefined;
      const c = localCustomers.find(x => x.id === id);
      return c ? (c.company || c.name) : undefined;
    };

    // If no session customer is active, show nothing (confidentiality gate)
    if (!sessionCustId) {
      setDbSamples([]);
      return;
    }

    const localSamples = getAllLocalSamples()
      .filter(s => (s.sampleType || 'influent') === 'influent')
      .filter(s => !currentUserId || !s.userId || s.userId === currentUserId)
      // Gate to session customer
      .filter(s => s.customerId != null ? Number(s.customerId) === sessionCustId : false)
      .map(s => ({
        id: s.id,
        sample_name: s.sampleName,
        customer_name: custName(s.customerId),
        customer_id: s.customerId ?? null,
        params: s.params,
        created_at: s.createdAt,
        _local: true,
      } as DbSample & { _local?: boolean }));

    // Merge with backend samples if available — influent only (server already filters sampleType='effluent')
    if (hasPhpBackend()) {
      try {
        // Pass customerId to server so it only returns samples for this customer
        const serverSamples = await apiListInfluentSamples(sessionCustId);
        if (Array.isArray(serverSamples) && serverSamples.length > 0) {
          // Server returns camelCase fields from influent_samples table.
          // Map to DbSample (snake_case) and apply a secondary name-based guard
          // to catch any records whose name implies effluent (e.g. mislabelled legacy rows).
          const EFFLUENT_WORDS = /final\s+effluent|treated\s+effluent|post.treatment|post.treat|treated\s+water|discharge\s+effluent|outlet\s+from|outlet\s+water|daf\s+outlet|daf\s+effluent|clarifier\s+outlet|post\s+daf|after\s+treatment|plant\s+outlet|plant\s+effluent|secondary\s+effluent|tertiary\s+effluent/i;
          const influentOnly = (serverSamples as Array<Record<string, unknown>>)
            .filter(s => {
              const type = (s.sampleType ?? s.sample_type ?? 'influent') as string;
              if (type === 'effluent') return false;
              const name = ((s.sampleName ?? s.sample_name ?? '') as string);
              if (EFFLUENT_WORDS.test(name)) return false;
              // Strict customer gate — only samples belonging to the active session customer
              const cid = (s.customerId ?? s.customer_id) != null ? Number(s.customerId ?? s.customer_id) : null;
              if (sessionCustId && cid !== null && cid !== sessionCustId) return false;
              return true;
            })
            .map(s => ({
              id:            s.id as number,
              sample_name:   (s.sampleName ?? s.sample_name ?? '') as string,
              customer_name: (s.customerName ?? s.customer_name ?? undefined) as string | undefined,
              customer_id:   (s.customerId ?? s.customer_id ?? null) as number | null,
              site_id:       (s.siteId ?? s.site_id ?? null) as number | null,
              params:        s.params as Record<string, number> | undefined,
              created_at:    (s.createdAt ?? s.created_at ?? undefined) as string | undefined,
              sampleType:    (s.sampleType ?? s.sample_type ?? 'influent') as string,
              _source:       (s._source ?? 'own') as string,
            } as DbSample));

          const serverIds = new Set(influentOnly.map(s => s.id));
          const merged = [
            ...influentOnly,
            ...localSamples.filter(s => !serverIds.has(s.id)),
          // Final gate — strict influent-only regardless of any upstream gap
          ].filter(s => (s.sampleType ?? 'influent') !== 'effluent');
          setDbSamples(merged);
          return;
        }
      } catch (err) {
        console.warn('Failed to load influent samples from backend:', err);
      }
    }
    setDbSamples(localSamples);
  }, [currentCustomer]);

  useEffect(() => {
    loadDbSamples();
  }, [loadDbSamples]);

  // Load all pump models for recommendation engine
  const loadPumpModels = useCallback(async () => {
    if (!hasPhpBackend()) return;
    setRecsLoading(true);
    try {
      const res = await apiPumpModels();
      const models: RawPumpModel[] = (res.models || []).map((m: any) => ({
        id: m.id,
        code: m.code,
        manufacturerName: m.manufacturerName,
        seriesName: m.seriesName,
        motorKw: m.motorKw ?? null,
        motorHp: m.motorHp ?? null,
        maxFlowM3h: m.maxFlowM3h ?? null,
        maxPressureBar: m.maxPressureBar ?? null,
      }));
      setAllPumpModels(models);
    } catch (err) {
      console.warn('Failed to load pump models:', err);
    } finally {
      setRecsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPumpModels();
  }, [loadPumpModels]);

  const handleLoadSample = (sampleId: number | '') => {
    setSelectedSampleId(sampleId);
    if (!sampleId) {
      setBaseSampleParams(null);
      setDilutionFactor(1);
      return;
    }
    const sample = dbSamples.find(s => s.id === sampleId);
    if (!sample?.params) {
      toast.error('Sample has no parameter data');
      return;
    }
    // Guard: reject effluent samples — they are treated output, not DAF feed
    const st = sample.sampleType || sample.sample_type || 'influent';
    if (st === 'effluent') {
      toast.error('Effluent samples cannot be used as DAF feed data. Please select an influent sample.');
      setSelectedSampleId('');
      return;
    }
    const p = sample.params;
    const mapped: Partial<DAFParams> = {
      flowRate: Number(p.flowRate ?? p.flow_rate ?? 250) || 250,
      tss: Number(p.tss ?? 250) || 250,
      og: Number(p.oilGrease ?? p.oil_grease ?? p.og ?? 80) || 80,
      bod: Number(p.bod5 ?? p.bod ?? 1500) || 1500,
      cod: Number(p.cod ?? 3000) || 3000,
      ph: Number(p.ph ?? 7.0) || 7.0,
      temp: Number(p.temperature ?? 20) || 20,
      alkalinity: Number(p.alkalinity ?? p.alk ?? 100) || 100,
      turbidity: Number(p.turbidity ?? p.turb ?? p.ntu ?? 200) || 200,
      conductivity: Number(p.conductivity ?? p.cond ?? p.ec ?? 1500) || 1500,
      hardness: Number(p.hardness ?? p.hard ?? p.total_hardness ?? 200) || 200,
      nh3n: Number(p.nh3n ?? p.nh3_n ?? p.ammonia ?? p.nh3 ?? 25) || 25,
      tp: Number(p.tp ?? p.totalPhosphorus ?? p.total_phosphorus ?? p.phosphorus ?? 5) || 5,
    };
    setBaseSampleParams(mapped);
    setDilutionFactor(1);
    setParams(prev => ({ ...prev, ...mapped }));
    setIsOptimalActive(false);
    // Clear any previous recommendation — it was for a different influent
    clearRecommended();
    // Remember the sample name so "Recommended Settings" button can show it
    setRecommendedForSample(sample.sample_name || '');
    localStorage.setItem('daf-recommended-sample', sample.sample_name || '');
    // Save regulatory context — look up customer's regulatory body from rb_customers
    try {
      const customers: Array<{ id: number; regulatoryBody?: string; name?: string; company?: string }> =
        JSON.parse(localStorage.getItem('rb_customers') || '[]');
      const customerId = sample.customer_id;
      const customer = customerId ? customers.find(c => c.id === customerId) : null;
      // Also look up site name from local sites
      const sites: Array<{ id: string | number; name: string; customerId?: string | number }> =
        JSON.parse(localStorage.getItem('rb_customer_sites') || '[]');
      const siteMatch = sample.site_id
        ? sites.find(s => String(s.id) === String(sample.site_id))
        : null;
      localStorage.setItem('daf-regulatory-context', JSON.stringify({
        regulatoryBody: customer?.regulatoryBody || STANDARD_TO_REG_BODY[(customer as any)?.standard] || '',
        customerName: sample.customer_name || '',
        sampleName: sample.sample_name || '',
        siteName: siteMatch?.name || '',
      }));
    } catch {
      localStorage.removeItem('daf-regulatory-context');
    }
    toast.success(`Loaded sample: ${sample.sample_name}`);
  };

  const applyDilution = (d: number) => {
    const safeD = Math.max(1, d);
    setDilutionFactor(safeD);
    if (!baseSampleParams) return;
    setParams(prev => ({
      ...prev,
      flowRate: Number(baseSampleParams.flowRate || prev.flowRate) * safeD,
      tss: Number(baseSampleParams.tss || prev.tss) / safeD,
      og: Number(baseSampleParams.og || prev.og) / safeD,
      bod: Number(baseSampleParams.bod || prev.bod) / safeD,
      cod: Number(baseSampleParams.cod || prev.cod) / safeD,
      ph: Number(baseSampleParams.ph || prev.ph),
      temp: Number(baseSampleParams.temp || prev.temp),
      alkalinity: Number(baseSampleParams.alkalinity || prev.alkalinity),
      turbidity: Number(baseSampleParams.turbidity || prev.turbidity) / safeD,
      conductivity: Number(baseSampleParams.conductivity || prev.conductivity),
      hardness: Number(baseSampleParams.hardness || prev.hardness),
      nh3n: Number(baseSampleParams.nh3n || prev.nh3n) / safeD,
      tp: Number(baseSampleParams.tp || prev.tp) / safeD,
    }));
  };

  const clearRecommended = () => {
    setRecommendedParams(null);
    setRecommendedForSample('');
    localStorage.removeItem('daf-recommended-v2');
    localStorage.removeItem('daf-recommended-sample');
  };

  const handleReset = () => {
    setParams(PROFILES[0].defaults); setProfileIndex(0); setSelectedSampleId('');
    setBaseSampleParams(null); setDilutionFactor(1); setIsOptimalActive(false);
    clearRecommended();
    toast.info('Parameters reset to defaults');
  };

  const handleApplyOptimal = () => {
    const optimised = calcOptimalDAFSettings(params);
    setParams(optimised);
    setRecommendedParams(optimised);
    setIsOptimalActive(true);
    setProfileIndex(-1); // deselect profile cards
    // Persist snapshot so state survives reload
    localStorage.setItem('daf-recommended-v2', JSON.stringify(optimised));
    localStorage.setItem('daf-recommended-sample', recommendedForSample);
    const label = recommendedForSample || 'current influent';
    toast.success(`Optimal DAF settings applied for ${label} — dimensions, air, mixing and chemistry calculated from influent data`);
  };
  const handleSave = async () => {
    localStorage.setItem('daf-params-v2', JSON.stringify(params));
    // Also persist computed performance so ProcessTrain/Dashboard can use physics-based removal
    // instead of fixed library defaults (resolves process-train DAF removal mismatch).
    const savedSize = calcDAF(params);
    localStorage.setItem('daf-size-v2', JSON.stringify({
      tssRemoval: savedSize.tssRemoval,
      ogRemoval: savedSize.ogRemoval,
      bodRemoval: savedSize.bodRemoval,
      codRemoval: savedSize.codRemoval,
      status: savedSize.status,
      hlr: savedSize.hlr,
      airToSolids: savedSize.airToSolids,
    }));
    setLastSavedParams(params);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
    // Stamp current user into regulatory context so PDF always has up-to-date preparer
    try {
      const existing = JSON.parse(localStorage.getItem('daf-regulatory-context') || '{}');
      localStorage.setItem('daf-regulatory-context', JSON.stringify({
        ...existing,
        preparedBy: user ? (user.name || user.username) : undefined,
        engineerRef: user ? `User: ${user.username}` : undefined,
        savedAt: new Date().toISOString(),
      }));
    } catch { /* ignore */ }

    // ── Save DAF effluent result against the active session customer ──────────
    // The calculated output (post-DAF concentrations) is saved as an effluent
    // sample in influent_samples so the customer page can display it in the
    // Effluent tab alongside their influent data.
    if (hasPhpBackend() && currentCustomer) {
      try {
        const customerId = parseInt(currentCustomer.id, 10);

        // Inherit siteId from the loaded influent sample if one was selected
        const loadedSample = selectedSampleId
          ? dbSamples.find(s => s.id === selectedSampleId)
          : null;
        const siteId = loadedSample?.site_id ?? null;

        // Compute estimated post-DAF effluent quality
        const effluentParams = {
          flowRate:      Math.round(params.flowRate),
          tss:           Math.round(params.tss    * (1 - savedSize.tssRemoval)),
          og:            Math.round(params.og     * (1 - savedSize.ogRemoval)),
          bod:           Math.round(params.bod    * (1 - savedSize.bodRemoval)),
          cod:           Math.round(params.cod    * (1 - savedSize.codRemoval)),
          ph:            parseFloat(params.ph.toFixed(2)),
          temperature:   params.temp,
          nh3n:          params.nh3n,   // DAF does not significantly remove NH₃-N
          conductivity:  params.conductivity,
          // Store key DAF design metrics for traceability
          daf_hlr:       parseFloat(savedSize.hlr.toFixed(3)),
          daf_asr:       parseFloat(savedSize.airToSolids.toFixed(3)),
        };

        const dateStr = new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
        const refSuffix = Date.now().toString().slice(-5);
        const customerLabel = currentCustomer.company || currentCustomer.name;

        await apiSaveInfluentSample({
          customerId,
          siteId,
          sampleName:     `DAF Effluent — ${customerLabel} — ${dateStr}`,
          reportNumber:   buildRegulatoryReportNumber('DAF', currentCustomer.standard ?? 'UK_EA'),
          sampleType:     'effluent',
          params:         effluentParams,
          standard:       currentCustomer.standard ?? 'UK_EA',
          linkedSampleId: typeof selectedSampleId === 'number' ? selectedSampleId : null,
          notes:          `Auto-saved from DAF Calculator.\n` +
                          `Influent source: ${loadedSample?.sample_name ?? 'Manual input'}.\n` +
                          `TSS removal: ${(savedSize.tssRemoval * 100).toFixed(1)}% | ` +
                          `BOD removal: ${(savedSize.bodRemoval * 100).toFixed(1)}% | ` +
                          `COD removal: ${(savedSize.codRemoval * 100).toFixed(1)}% | ` +
                          `O&G removal: ${(savedSize.ogRemoval * 100).toFixed(1)}%.`,
        });

        toast.success(`Configuration saved · Effluent sample recorded for ${customerLabel}`);
        setShowGoToEffluent(true);
      } catch (err) {
        console.warn('Effluent sample save failed (non-critical):', err);
        toast.success('Configuration saved — Validation button is now active');
        setShowGoToEffluent(true);
      }
    } else {
      toast.success('Configuration saved — Validation button is now active');
      setShowGoToEffluent(true);
    }
  };

  // ─── Generate DAF Regulatory Report ───────────────────────────────────────
  const handleGenerateDAFReport = async () => {
    if (!hasSavedConfig) {
      toast.info('Save your DAF configuration first before generating a regulatory report');
      return;
    }
    try {
      const regCtx = JSON.parse(localStorage.getItem('daf-regulatory-context') || '{}');
      const size = calcDAF(params);
      const customerLabel = currentCustomer ? (currentCustomer.company || currentCustomer.name) : 'Unknown Customer';
      const stdCode = currentCustomer?.standard ?? regCtx.regulatoryBody ?? 'UK_EA';
      const reportNumber = buildRegulatoryReportNumber('DAF', stdCode);

      const pdfParams: DAFReportParams = {
        flowRate: params.flowRate, tss: params.tss, og: params.og,
        bod: params.bod, cod: params.cod, ph: params.ph, temp: params.temp,
        alkalinity: params.alkalinity, turbidity: params.turbidity,
        conductivity: params.conductivity, hardness: params.hardness,
        nh3n: params.nh3n, tp: params.tp,
        length: params.length, width: params.width, depth: params.depth,
        airPressure: params.airPressure, recycleRate: params.recycleRate,
        saturatorPressure: params.saturatorPressure,
      };

      const pdfResult: DAFReportResult = {
        tssRemoval: size.tssRemoval, ogRemoval: size.ogRemoval,
        bodRemoval: size.bodRemoval, codRemoval: size.codRemoval,
        effluentTSS: size.effluentTSS, effluentOG: size.effluentOG,
        effluentBOD: size.effluentBOD, effluentCOD: size.effluentCOD,
        effluentPH: size.effluentPH,
        hlr: size.hlr, slr: size.slr, actualRetention: size.actualRetention,
        airToSolids: size.airToSolids,
        sludgeTotal: size.sludgeTotal, sludgeVolume: size.sludgeVolume,
        power: size.power,
        status: size.status, statusDetail: size.statusDetail,
        designNotes: size.designNotes,
      };

      // Loaded influent sample (for traceability)
      const loadedInfluentSample = selectedSampleId
        ? dbSamples.find(s => s.id === selectedSampleId)
        : null;
      const siteId = loadedInfluentSample?.site_id ?? null;

      const pdfOpts: DAFReportOptions = {
        reportNumber,
        customerName: customerLabel,
        siteName: regCtx.siteName || currentCustomer?.siteName,
        siteAddress: regCtx.siteAddress || currentCustomer?.address,
        permitRef: regCtx.permitRef || currentCustomer?.permitRef,
        regulatoryAuthority: regCtx.regulatoryBody || STANDARD_TO_REG_BODY[stdCode] || stdCode,
        preparedBy: user ? (user.name || user.username) : undefined,
        standard: stdCode,
        influentSampleRef: loadedInfluentSample
          ? `${loadedInfluentSample.sample_name} (ID: ${loadedInfluentSample.id})`
          : undefined,
        createdAt: new Date(),
      };

      // Generate & download the PDF
      generateDAFReportPDF(pdfParams, pdfResult, pdfOpts, 'save');

      // Save report metadata to DB for retrieval in Reports page
      if (hasPhpBackend()) {
        try {
          await apiSaveReport({
            reportType: 'daf',
            reportNumber,
            reportTitle: `DAF Effluent Report — ${customerLabel}`,
            customerName: customerLabel,
            customerId: currentCustomer ? parseInt(currentCustomer.id, 10) : null,
            siteId: siteId,
            siteName: pdfOpts.siteName ?? null,
            siteAddress: pdfOpts.siteAddress ?? null,
            permitRef: pdfOpts.permitRef ?? null,
            regulatoryAuthority: pdfOpts.regulatoryAuthority ?? null,
            standard: pdfOpts.standard ?? 'UK_EA',
            preparedBy: pdfOpts.preparedBy ?? null,
            influentSampleRef: pdfOpts.influentSampleRef ?? null,
            createdAt: new Date().toISOString(),
            // Store key performance data as JSON for display in Reports page
            reportData: JSON.stringify({
              tssRemoval: +(size.tssRemoval * 100).toFixed(1),
              ogRemoval: +(size.ogRemoval * 100).toFixed(1),
              bodRemoval: +(size.bodRemoval * 100).toFixed(1),
              codRemoval: +(size.codRemoval * 100).toFixed(1),
              effluentTSS: +size.effluentTSS.toFixed(1),
              effluentOG: +size.effluentOG.toFixed(1),
              effluentBOD: +size.effluentBOD.toFixed(1),
              effluentCOD: +size.effluentCOD.toFixed(1),
              hlr: +size.hlr.toFixed(3),
              airToSolids: +size.airToSolids.toFixed(3),
              status: size.status,
              flowRate: params.flowRate,
              params: pdfParams,
            }),
          });
          toast.success(`DAF regulatory report generated · Saved to database as ${reportNumber}`);
        } catch (err) {
          console.warn('Report DB save failed (non-critical):', err);
          toast.success(`DAF regulatory report downloaded as ${reportNumber}`);
        }
      } else {
        toast.success(`DAF regulatory report downloaded as ${reportNumber}`);
      }
    } catch (err) {
      console.error('DAF report generation error:', err);
      toast.error('Report generation failed — see console for details');
    }
  };

  useEffect(() => {
    const savedParams = localStorage.getItem('daf-params-v2');
    if (savedParams) { try { setParams(JSON.parse(savedParams)); } catch {} }
  }, []);

  const statusColors = {
    optimal: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: Info },
    undersized: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: AlertCircle },
    overloaded: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: ShieldAlert },
    oversized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Info },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  const asColor = size.asStatus === 'poor' ? '#ef4444' : size.asStatus === 'marginal' ? '#f59e0b' : size.asStatus === 'optimal' ? '#10b981' : '#0ea5e9';
  const q_m3h = params.flowRate / 24;
  const requiredRecycleFlowM3h = q_m3h * (params.recycleRate / 100);
  const requiredPressureBar = params.saturatorPressure;

  // ─── Pump Recommendation Handlers ───
  const handleSelectPump = (pump: ScoredPump) => {
    const newParams = {
      ...params,
      selectedPumpId: pump.id,
      selectedPumpCode: pump.code,
      selectedPumpMaxFlowM3h: pump.maxFlowM3h ?? undefined,
      selectedPumpMaxPressureBar: pump.maxPressureBar ?? undefined,
      selectedPumpMotorKw: pump.motorKw ?? undefined,
    };
    setParams(newParams);
    localStorage.setItem('daf-params-v2', JSON.stringify(newParams));
    toast.success(`Selected ${pump.code} — ${pump.verdictLabel}`);
  };

  const handleAutoSelect = () => {
    const best = getBestPump(allPumpModels, requiredRecycleFlowM3h, requiredPressureBar);
    if (best && best.verdict !== 'undersized' && best.verdict !== 'unknown') {
      handleSelectPump(best);
    } else {
      toast.error('No suitable pump found for current requirements');
    }
  };

  // Compute recommendations whenever requirements change
  useEffect(() => {
    if (allPumpModels.length === 0 || !requiredRecycleFlowM3h || !requiredPressureBar) {
      setRecommendedPumps([]);
      return;
    }
    const recs = getRecommendedPumps(allPumpModels, requiredRecycleFlowM3h, requiredPressureBar, 5);
    setRecommendedPumps(recs);
  }, [allPumpModels, requiredRecycleFlowM3h, requiredPressureBar]);

  // ─── Pump validation warnings ───
  useEffect(() => {
    if (!params.selectedPumpId) return;
    const maxFlow = params.selectedPumpMaxFlowM3h;
    const maxPressure = params.selectedPumpMaxPressureBar;
    if (!maxFlow) return;

    if (requiredRecycleFlowM3h > maxFlow * 1.1) {
      toast.warning(`Selected pump ${params.selectedPumpCode} may be too small for ${requiredRecycleFlowM3h.toFixed(1)} m³/h. Choose a larger pump.`);
    } else if (requiredRecycleFlowM3h < maxFlow * 0.35) {
      toast.warning(`Selected pump ${params.selectedPumpCode} may be oversized for ${requiredRecycleFlowM3h.toFixed(1)} m³/h. Consider a smaller pump.`);
    }
    if (maxPressure && requiredPressureBar > maxPressure * 1.05) {
      toast.warning(`Selected pump ${params.selectedPumpCode} may not provide enough pressure (${requiredPressureBar} bar required, ${maxPressure} bar max).`);
    }
  }, [params.flowRate, params.recycleRate, params.saturatorPressure, params.selectedPumpId, params.selectedPumpCode, params.selectedPumpMaxFlowM3h, params.selectedPumpMaxPressureBar]);

  // Auto-open pump impact panel when a pump is selected
  useEffect(() => {
    if (params.selectedPumpId) setShowPumpImpact(true);
  }, [params.selectedPumpId]);

  // Auto-calculate floc motor power from RPM + diameter (power number)
  const updateFlocMixer = (field: 'flocMotorSpeed' | 'flocImpellerDia' | 'flocImpellerType', value: number | string) => {
    const newParams = { ...params, [field]: value } as typeof params;
    const N_rps  = (field === 'flocMotorSpeed'  ? Number(value) : params.flocMotorSpeed)  / 60;
    const D_imp  =  field === 'flocImpellerDia' ? Number(value) : params.flocImpellerDia;
    const impType = (field === 'flocImpellerType' ? String(value) : params.flocImpellerType) as 'paddle' | 'hydrofoil' | 'pitched' | 'rushton';
    const rhoW   = 998 - 0.2 * (params.temp - 20);
    const mu_    = 0.001 * Math.exp(-1.5 * (params.temp - 20) / (params.temp + 273.15));
    // Turbulent Np lookup
    const NP_TABLE: Record<string, number> = { paddle: 1.70, hydrofoil: 0.31, pitched: 1.27, rushton: 5.90 };
    const NP_KP:    Record<string, number> = { paddle: 36.5, hydrofoil: 41.0, pitched: 44.0, rushton: 71.3 };
    const Np_turb = NP_TABLE[impType] ?? 1.70;
    const Kp_lam  = NP_KP[impType]   ?? 36.5;
    const Re_     = N_rps > 0 ? (rhoW * N_rps * D_imp * D_imp) / mu_ : 0;
    let Np_eff: number;
    if (Re_ > 10000)        Np_eff = Np_turb;
    else if (Re_ > 10)      Np_eff = Np_turb * Math.pow(10000 / Re_, 0.14);
    else if (Re_ > 0)       Np_eff = Kp_lam / Re_;
    else                    Np_eff = Np_turb;
    const P_W = Np_eff * rhoW * Math.pow(N_rps, 3) * Math.pow(D_imp, 5);
    newParams.flocMotorPower = Math.max(0.05, P_W / 1000);
    setParams(newParams);
  };

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      {/* ═════ HEADER ═════ */}
      <PageHeader
        icon={Waves}
        iconColor="text-emerald-400"
        title="DAF Specification"
        subtitle="Physics-based DAF design · Henry's law air solubility · Stokes' bubble dynamics · Camp-Stein G-value · Live chemistry"
        actions={<div className="flex items-center flex-wrap gap-2 shrink-0">
          <button onClick={handleReset} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-300 text-xs font-medium hover:bg-slate-700 hover:border-slate-600 transition-all">
            <RotateCcw className="w-3.5 h-3.5" />Reset
          </button>
          <button onClick={handleSave} className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border text-xs font-medium transition-all ${
            savedFlash
              ? 'bg-emerald-500/25 border-emerald-400/60 text-emerald-300'
              : isDirty
              ? 'bg-amber-500/15 border-amber-500/40 text-amber-400 hover:bg-amber-500/25 animate-pulse'
              : hasSavedConfig
              ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/25'
              : 'bg-slate-700/60 border-slate-600/60 text-slate-300 hover:bg-slate-700'
          }`}
          title={isDirty ? 'You have unsaved changes — click to save' : hasSavedConfig ? 'Configuration saved' : 'Save current configuration'}>
            <Save className="w-3.5 h-3.5" />
            {savedFlash ? '✓ Saved!' : isDirty ? '● Unsaved changes' : hasSavedConfig ? '✓ Saved' : 'Save'}
          </button>
          <button
            onClick={() => setShowPumpImpact(v => !v)}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border text-xs font-medium transition-all ${
              pumpImpact
                ? showPumpImpact
                  ? 'bg-sky-500/20 border-sky-500/40 text-sky-300'
                  : 'bg-sky-500/10 border-sky-500/30 text-sky-400 hover:bg-sky-500/15'
                : 'bg-slate-800/60 border-slate-800/50 text-slate-500 hover:text-slate-400'
            }`}
            title={pumpImpact ? `White water impact: ${pumpImpact.pumpCode}` : 'Select a pump in the Pumps tab'}
          >
            <Droplets className="w-3.5 h-3.5" />
            {pumpImpact ? `${pumpImpact.pumpCode}` : 'Pump Impact'}
            {pumpImpact && (
              <span className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{
                background: pumpImpact.overallStatus === 'optimal' ? '#10b981' : pumpImpact.overallStatus === 'oversized' ? '#f59e0b' : pumpImpact.overallStatus === 'limited' ? '#f97316' : '#ef4444'
              }} />
            )}
          </button>
          <button
            onClick={() => {
              if (!hasSavedConfig && !isDirty) {
                toast.info('Enter parameters and save first to generate a validation report');
                return;
              }
              if (isDirty) {
                // Unsaved changes — show inline save-prompt instead of blocking
                setShowSavePrompt(true);
              } else {
                setShowValidationReport(true);
              }
            }}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border text-xs font-medium transition-all ${
              isDirty
                ? 'bg-amber-500/10 border-amber-500/40 text-amber-400 hover:bg-amber-500/20'
                : hasSavedConfig
                ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-400/60'
                : 'bg-slate-800/50 border-slate-800/40 text-slate-500 hover:text-slate-400 cursor-default'
            }`}
            title={isDirty ? 'Unsaved changes — save first for accurate validation' : hasSavedConfig ? 'View full validation report' : 'Save configuration to enable validation report'}
          >
            <Microscope className="w-3.5 h-3.5" />
            {isDirty ? 'Save & Validate' : 'Validation'}
            {hasSavedConfig && !isDirty && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />}
            {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />}
          </button>
          <button
            onClick={handleGenerateDAFReport}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border text-xs font-medium transition-all ${
              hasSavedConfig && !isDirty
                ? 'bg-rb-active/60 border-rb-brand/50 text-rb-brand hover:bg-rb-active/80 hover:border-rb-brand/70'
                : 'bg-slate-800/50 border-slate-800/40 text-slate-500 hover:text-slate-400 cursor-default'
            }`}
            title={hasSavedConfig && !isDirty ? 'Generate DAF regulatory PDF report and save to database' : 'Save configuration first to generate regulatory report'}
          >
            <FileText className="w-3.5 h-3.5" />
            Regulatory Report
            {hasSavedConfig && !isDirty && <span className="w-1.5 h-1.5 rounded-full bg-rb-brand shrink-0" />}
          </button>
        </div>}
      />

      {/* ═════ GO TO EFFLUENT ENTRY BANNER ═════ */}
      {showGoToEffluent && (
        <div className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold animate-fade-in">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>DAF config saved</span>
          <button
            onClick={() => { navigate('/effluent-entry'); }}
            className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 text-[11px] font-bold transition-all"
          >
            Record Actual Effluent →
          </button>
          <button onClick={() => setShowGoToEffluent(false)} className="p-0.5 text-emerald-600 hover:text-emerald-400">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ═════ UNSAVED CHANGES PROMPT ═════ */}
      <AnimatePresence>
        {showSavePrompt && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="rounded-xl border border-amber-500/40 bg-amber-500/8 p-4 flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
              <Save className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-400">Unsaved changes detected</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Your current design differs from the last saved configuration. Save now to ensure the validation report and PDF reflect your latest parameters.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={async () => {
                  await handleSave();
                  setShowSavePrompt(false);
                  setShowValidationReport(true);
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 transition-all"
              >
                <Save className="w-3.5 h-3.5" /> Save & Validate
              </button>
              <button
                onClick={() => {
                  setShowSavePrompt(false);
                  setShowValidationReport(true);
                }}
                className="px-3 py-2 rounded-lg text-xs text-slate-400 border border-slate-700 hover:bg-slate-800 transition-all"
              >
                Validate without saving
              </button>
              <button
                onClick={() => setShowSavePrompt(false)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═════ STATUS BANNER ═════ */}
      {(() => {
        const loadedSampleName = selectedSampleId !== ''
          ? dbSamples.find(s => s.id === selectedSampleId)?.sample_name || null
          : null;
        return (
          <motion.div
            layout
            className={`rounded-xl border p-4 ${sc.bg} ${sc.border} rb-status-${size.status}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${sc.bg} border ${sc.border}`}>
                <StatusIcon className={`w-6 h-6 ${sc.text}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm font-bold tracking-wider uppercase ${sc.text}`}>{size.status}</p>
                  <div className={`w-1.5 h-1.5 rounded-full ${sc.text.replace('text-', 'bg-')} rb-pulse-dot`} />
                  {/* ── Loaded sample chip — always sky-blue regardless of status colour ── */}
                  {loadedSampleName && (
                    <div
                      className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-sky-500/15 border border-sky-400/35 animate-pulse"
                      style={{ animationDuration: '3s' }}
                    >
                      <Database className="w-2.5 h-2.5 text-sky-400 shrink-0" />
                      <span className="text-[10px] font-bold text-sky-300 max-w-[200px] truncate leading-tight">
                        {loadedSampleName}
                      </span>
                    </div>
                  )}
                </div>
                <p className={`text-xs mt-0.5 leading-relaxed ${sc.text} opacity-75`}>{size.statusDetail}</p>
              </div>
              <div className="hidden md:flex items-center gap-4 text-xs shrink-0">
                <div className="text-center">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wider">HLR</p>
                  <p className="font-bold text-white">{fmt(size.hlr, 1)} <span className="text-slate-500 font-normal text-[10px]">m³/m²·h</span></p>
                </div>
                <div className="text-center">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wider">A/S Ratio</p>
                  <p className="font-bold" style={{ color: asColor }}>{fmt(size.airToSolids, 4)}</p>
                </div>
                <div className="text-center">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wider">Removal</p>
                  <p className="font-bold text-emerald-400">{fmt(size.removalEfficiency, 0)}%</p>
                </div>
                <div className="text-center">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wider">Power</p>
                  <p className="font-bold text-sky-400">{fmt(size.power, 1)} <span className="text-slate-500 font-normal text-[10px]">kW</span></p>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })()}

      {/* ═════ PUMP WHITE WATER IMPACT PANEL ═════ */}
      <AnimatePresence>
        {showPumpImpact && pumpImpact && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <PumpImpactPanel impact={pumpImpact} onClose={() => setShowPumpImpact(false)} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═════ PERFORMANCE GAUGES — full-width at top ═════ */}
      <div className="p-4 rb-card">
        {/* Pump Impact inline summary strip (when panel is hidden but pump selected) */}
        {pumpImpact && !showPumpImpact && (
          <button
            onClick={() => setShowPumpImpact(true)}
            className="w-full mb-3 flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-medium border transition-all hover:opacity-80"
            style={{
              background: pumpImpact.overallStatus === 'optimal' ? 'rgba(16,185,129,0.06)' : pumpImpact.overallStatus === 'oversized' ? 'rgba(245,158,11,0.06)' : 'rgba(239,68,68,0.06)',
              borderColor: pumpImpact.overallStatus === 'optimal' ? 'rgba(16,185,129,0.25)' : pumpImpact.overallStatus === 'oversized' ? 'rgba(245,158,11,0.25)' : 'rgba(239,68,68,0.25)',
              color: pumpImpact.overallStatus === 'optimal' ? '#10b981' : pumpImpact.overallStatus === 'oversized' ? '#f59e0b' : '#f97316',
            }}
          >
            <Droplets className="w-3 h-3 shrink-0" />
            <span className="font-bold">{pumpImpact.pumpCode}</span>
            <span className="text-slate-500 mx-1">·</span>
            <span>TSS {pumpImpact.targetTSSRemoval.toFixed(0)}%{pumpImpact.tssRemovalDelta !== 0 ? ` → ${pumpImpact.actualTSSRemoval.toFixed(0)}% (${pumpImpact.tssRemovalDelta > 0 ? '+' : ''}${pumpImpact.tssRemovalDelta.toFixed(1)}pp)` : ''}</span>
            <span className="text-slate-500 mx-1">·</span>
            <span>Bubble {pumpImpact.targetBubbleSize} → {pumpImpact.actualBubbleSize} μm</span>
            <span className="text-slate-500 mx-1">·</span>
            <span>A/S {pumpImpact.actualAS.toFixed(4)}</span>
            <span className="ml-auto uppercase tracking-wider text-[10px] opacity-70">Click to expand ↓</span>
          </button>
        )}
        <h3 className="text-xs font-bold text-slate-300 mb-3 flex items-center gap-2 uppercase tracking-wider">
          <Gauge className="w-3.5 h-3.5 text-emerald-400" />Performance Gauges
          <span className="ml-auto flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 rb-pulse-dot" />
            <span className="text-[10px] text-emerald-500 font-normal normal-case tracking-normal">Live</span>
          </span>
        </h3>
        <div className="grid grid-cols-3 sm:grid-cols-5 xl:grid-cols-10 gap-2">
          <G value={size.tssRemoval * 100} max={100} label="TSS Removal" unit="%" color="#10b981" />
          <G value={size.ogRemoval * 100} max={100} label="O&G Removal" unit="%" color="#06b6d4" />
          <G value={size.bodRemoval * 100} max={100} label="BOD Removal" unit="%" color="#f97316" />
          <G value={size.codRemoval * 100} max={100} label="COD Removal" unit="%" color="#f97316" />
          <G value={size.airToSolids} max={0.12} label="A/S Ratio" unit="" color={asColor} />
          <G value={size.bubbleRise} max={15} label="Bubble Rise" unit="m/h" color="#f59e0b" />
          <G value={size.pumpPower + size.compressorPower} max={10} label="Pump+Comp" unit="kW" color="#ef4444" />
          <G value={size.mixerPower} max={5} label="Mixer Power" unit="kW" color="#8b5cf6" />
          <G value={size.power} max={15} label="Total Power" unit="kW" color="#f97316" />
          <G value={size.flocGValue} max={120} label="G-Value" unit="s⁻¹" color="#a855f7" />
        </div>
      </div>

      <RegulatoryNotice standard={currentCustomer?.standard} context="daf" compact />

      {/* ═════ PROFILE SELECTOR + DB SAMPLE CARD ═════ */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {/* ── Recommended DAF Settings ─────────────────────────────────────────
             Three visual states:
             1. Not active      → ghost emerald — "Apply Recommended Settings"
             2. Active, no drift → bright emerald — "✓ Recommended for [sample]"
             3. Active, drifted → amber/orange  — "⚠ Modified from Recommended"
                                   (design params changed or influent changed)
          ────────────────────────────────────────────────────────────────── */}
        {(() => {
          const isDrift   = isOptimalActive && (designDrift || influentChanged);
          const isExact   = isOptimalActive && !designDrift && !influentChanged;
          const sampleLabel = recommendedForSample || 'this influent';

          // Styling per state
          const cardCls = isDrift
            ? 'bg-gradient-to-br from-amber-500/15 to-orange-600/5 border-amber-500/40 shadow-md shadow-amber-900/15 hover:from-amber-500/20 hover:to-orange-600/10 hover:border-amber-400/50'
            : isExact
            ? 'bg-gradient-to-br from-emerald-500/20 to-teal-600/10 border-emerald-500/50 shadow-lg shadow-emerald-900/20'
            : 'bg-gradient-to-br from-emerald-500/8 to-teal-600/4 border-emerald-700/40 hover:border-emerald-500/50 hover:from-emerald-500/14 hover:to-teal-600/8';

          const iconBgCls = isDrift
            ? 'bg-amber-500/20 shadow-inner shadow-amber-900/30'
            : isExact
            ? 'bg-emerald-500/25 shadow-inner shadow-emerald-900/30'
            : 'bg-emerald-900/40';

          const IconEl = isDrift ? AlertCircle : isExact ? CheckCircle2 : Zap;
          const iconCls = isDrift ? 'text-amber-400' : isExact ? 'text-emerald-300' : 'text-emerald-500';

          const titleCls = isDrift ? 'text-amber-300' : isExact ? 'text-emerald-300' : 'text-emerald-400';
          const title    = isDrift
            ? (designDrift && influentChanged ? 'Settings & Influent Changed' : designDrift ? 'Settings Modified' : 'Influent Changed')
            : isExact
            ? 'Recommended Settings'
            : 'Recommended Settings';

          const subtitleCls = isDrift ? 'text-amber-600/80' : isExact ? 'text-emerald-600' : 'text-emerald-400/90';
          const subtitle = isDrift
            ? 'Click to re-apply for current influent'
            : isExact
            ? `Applied for: ${sampleLabel.length > 18 ? sampleLabel.slice(0, 16) + '…' : sampleLabel}`
            : 'Physics-optimised for this influent';

          const badgeCls = isDrift
            ? 'bg-amber-500/20'
            : 'bg-emerald-500/20';
          const badgeIconCls = isDrift ? 'text-amber-400' : 'text-emerald-400';

          const footerText = isDrift
            ? (designDrift && influentChanged
                ? '⚠ DAF settings and influent quality both differ from when recommendations were applied'
                : designDrift
                ? '⚠ DAF design parameters have been manually adjusted — click to restore optimal settings'
                : '↻ Influent quality has changed — click to recalculate optimal settings for new influent')
            : isExact
            ? '✓ All DAF dimensions, air system, mixing and chemistry match calculated optimum'
            : 'Auto-sizes cell, air system, mixing & chemistry from current influent data';
          const footerCls = isDrift ? 'text-amber-600/70' : isExact ? 'text-emerald-600' : 'text-emerald-400/70';

          return (
            <button
              onClick={handleApplyOptimal}
              className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${cardCls}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${iconBgCls}`}>
                  <IconEl className={`w-5 h-5 transition-colors ${iconCls}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold truncate transition-colors ${titleCls}`}>{title}</p>
                  <p className={`text-[10px] leading-tight mt-0.5 truncate ${subtitleCls}`}>{subtitle}</p>
                </div>
                {(isExact || isDrift) && (
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${badgeCls}`}>
                    <IconEl className={`w-3 h-3 ${badgeIconCls}`} />
                  </div>
                )}
              </div>
              {/* Drift indicator bar */}
              {isDrift && (
                <div className="flex items-center gap-1.5 mt-2 px-2 py-1 rounded-lg bg-amber-900/20 border border-amber-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />
                  <p className={`text-[10px] leading-snug font-medium ${footerCls}`}>{footerText}</p>
                </div>
              )}
              {!isDrift && (
                <p className={`text-[10px] mt-2 leading-snug ${footerCls}`}>{footerText}</p>
              )}
            </button>
          );
        })()}
        <InfluentIntelligencePanel params={params} />
        <button
          onClick={() => setShowSampleLoader(!showSampleLoader)}
          className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
            showSampleLoader || selectedSampleId
              ? 'bg-gradient-to-br from-sky-500/15 to-sky-600/5 border-sky-500/40'
              : 'bg-rb-elevated/60 border-slate-800/80 hover:border-slate-700 hover:bg-rb-elevated'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${showSampleLoader || selectedSampleId ? 'bg-sky-500/20' : 'bg-slate-800'}`}>
              <Database className={`w-5 h-5 transition-colors ${showSampleLoader || selectedSampleId ? 'text-sky-400' : 'text-slate-500'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-bold truncate transition-colors ${showSampleLoader || selectedSampleId ? 'text-sky-400' : 'text-slate-300'}`}>
                {selectedSampleId ? (dbSamples.find(s => s.id === selectedSampleId)?.sample_name || 'Recorded Sample') : 'Load Influent Sample'}
              </p>
              <p className="text-[10px] text-slate-500 leading-tight mt-0.5 truncate">
                {selectedSampleId ? 'Sample loaded — click to adjust' : `${dbSamples.length > 0 ? dbSamples.length + ' sample' + (dbSamples.length !== 1 ? 's' : '') + ' available' : 'Save influent samples to load here'}`}
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* ═════ DB SAMPLE LOADER (collapsible) ═════ */}
      <AnimatePresence>
        {showSampleLoader && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className={`rounded-xl border p-4 space-y-4 ${selectedSampleId ? 'bg-sky-500/5 border-sky-500/30' : 'bg-rb-surface border-slate-800'}`}>
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-sky-400" />
                <h3 className="text-sm font-bold text-white">Load Influent Sample into DAF Model</h3>
                {dbSamples.length === 0
                  ? <span className="text-[10px] text-slate-500 ml-auto">No influent samples found — add them via Influent Samples</span>
                  : <span className="text-[10px] text-sky-500/70 ml-auto">{dbSamples.length} sample{dbSamples.length !== 1 ? 's' : ''} available</span>
                }
              </div>
              {dbSamples.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                  <div className="md:col-span-2 space-y-1.5">
                    <label className="text-xs font-medium text-slate-400">Recorded Sample</label>
                    <select
                      value={selectedSampleId}
                      onChange={(e) => handleLoadSample(e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500 rb-inner-card-sm"
                    >
                      <option value="">— Select a sample —</option>
                      {dbSamples.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s._source === 'shared' ? '⇄ ' : ''}{s.sample_name}{s.customer_name ? ` (${s.customer_name})` : ''}{s._source === 'shared' ? ' — Shared' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-400 flex items-center gap-1">
                      <Blend className="w-3 h-3" /> Dilution Factor
                    </label>
                    <input
                      type="range" min={1} max={5} step={0.1}
                      value={dilutionFactor}
                      onChange={(e) => applyDilution(parseFloat(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                      style={{ accentColor: '#06b6d4' }}
                    />
                    <div className="flex justify-between text-[10px] text-slate-500">
                      <span>1x (raw)</span>
                      <span className="text-sky-400 font-bold">{dilutionFactor.toFixed(1)}x</span>
                      <span>5x</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[10px] text-slate-500">Dilution effect:</div>
                    <div className="text-xs text-slate-300">
                      Flow <span className="text-sky-400 font-bold">x{dilutionFactor.toFixed(1)}</span>
                      {' / '}
                      Conc <span className="text-amber-400 font-bold">/{dilutionFactor.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              )}
              {selectedSampleId && baseSampleParams && (
                <div className="space-y-2 pt-2 border-t border-slate-800">
                  <p className="text-[10px] text-sky-400 font-bold uppercase tracking-wider">
                    {dbSamples.find(s => s.id === selectedSampleId)?.sample_name}
                    {dbSamples.find(s => s.id === selectedSampleId)?.customer_name ? ` / ${dbSamples.find(s => s.id === selectedSampleId)?.customer_name}` : ''}
                  </p>
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                    {[
                      { label: 'Flow', value: fmt(params.flowRate, 0), unit: 'm³/d' },
                      { label: 'TSS', value: fmt(params.tss, 0), unit: 'mg/L' },
                      { label: 'O&G', value: fmt(params.og, 0), unit: 'mg/L' },
                      { label: 'BOD', value: fmt(params.bod, 0), unit: 'mg/L' },
                      { label: 'COD', value: fmt(params.cod, 0), unit: 'mg/L' },
                      { label: 'pH', value: fmt(params.ph, 1), unit: '' },
                      { label: 'Temp', value: fmt(params.temp, 0), unit: 'C' },
                      { label: 'Alk', value: fmt(params.alkalinity, 0), unit: 'mg/L' },
                      { label: 'Turb', value: fmt(params.turbidity, 0), unit: 'NTU' },
                      { label: 'Cond', value: fmt(params.conductivity, 0), unit: 'µS/cm' },
                      { label: 'Hard', value: fmt(params.hardness, 0), unit: 'mg/L' },
                      { label: 'NH₃-N', value: fmt(params.nh3n, 0), unit: 'mg/L' },
                      { label: 'TP', value: fmt(params.tp, 1), unit: 'mg/L' },
                    ].map((item) => (
                      <div key={item.label} className="p-2 rb-inner-card-sm">
                        <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                        <p className="text-xs font-bold text-white">{item.value} <span className="text-[10px] text-slate-500 font-normal">{item.unit}</span></p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═════ FLOW RATE — always visible, above all tabs ═════ */}
      <div className="bg-rb-surface rounded-xl border border-cyan-500/20 p-4 xl:hidden">
        {/* Mobile: compact horizontal display */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-cyan-500/15">
            <Waves className="w-3.5 h-3.5 text-cyan-400" />
          </div>
          <span className="text-sm font-medium text-slate-200">Flow Rate</span>
          <div className="ml-auto flex items-center gap-1.5 bg-slate-800/60 rounded-lg px-2.5 py-1 border border-slate-800/40">
            <span className="text-sm font-bold text-white tabular-nums">{Math.round(params.flowRate)}</span>
            <span className="text-xs text-slate-500">m³/day</span>
            <span className="text-[10px] text-cyan-400 font-bold ml-1">({(params.flowRate / 24).toFixed(1)} m³/h)</span>
          </div>
        </div>
        <S label="Flow Rate" value={params.flowRate} min={24} max={2400} step={12} unit="m³/day" icon={Waves} color="#06b6d4"
          onChange={(v: number) => setParams({ ...params, flowRate: v })}
          info="Total influent flow rate to DAF unit. Always visible — adjust at any time regardless of active tab." />
      </div>

      {/* ═════ MAIN GRID: LEFT = Input Tabs, RIGHT = Diagram + Physics ═════ */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* ─── LEFT: INPUT TABS ─── */}
        <div className="xl:col-span-5 space-y-4">

          {/* Flow Rate — always visible above tabs on desktop */}
          <div className="hidden xl:block bg-rb-surface rounded-xl border border-cyan-500/25 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Waves className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
              <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">Flow Rate — always visible</span>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[10px] text-slate-500">m³/h equivalent:</span>
                <span className="text-sm font-black text-cyan-300 tabular-nums">{(params.flowRate / 24).toFixed(1)}</span>
                <span className="text-[10px] text-slate-500">m³/h</span>
              </div>
            </div>
            <S label="Flow Rate" value={params.flowRate} min={24} max={2400} step={12} unit="m³/day" icon={Waves} color="#06b6d4"
              onChange={(v: number) => setParams({ ...params, flowRate: v })}
              info="Total influent flow rate to DAF unit. Adjust here at any time — change is immediately reflected in all live performance calculations." />
          </div>

          <div className="overflow-hidden rb-card">
            <div className="grid grid-cols-6 border-b border-slate-800/80">
              {([
                { id: 'flow', label: 'Flow', icon: Waves },
                { id: 'chemistry', label: 'Chemistry', icon: FlaskConical },
                { id: 'air', label: 'Air', icon: Wind },
                { id: 'hydraulics', label: 'Hydraulics', icon: Cylinder },
                { id: 'pumps', label: 'Pumps', icon: Cog },
                { id: 'scraper', label: 'Scraper', icon: Timer },
              ] as const).map(({ id, label, icon: TabIcon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex flex-col items-center gap-1 px-1 py-2.5 text-[9px] font-bold uppercase tracking-wider transition-all relative ${
                    activeTab === id
                      ? 'text-emerald-400 bg-emerald-500/5'
                      : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/30'
                  }`}
                >
                  <TabIcon className="w-3.5 h-3.5 shrink-0" />
                  <span className="leading-none">{label}</span>
                  {activeTab === id && (
                    <motion.div
                      layoutId="daf-tab-indicator"
                      className="absolute bottom-0 inset-x-0 h-0.5 bg-emerald-400"
                      style={{ boxShadow: '0 0 8px rgba(16,185,129,0.6)' }}
                    />
                  )}
                </button>
              ))}
            </div>
            <div className="p-4 space-y-4">
              {activeTab === 'flow' && (
                <div className="space-y-4">
                  <div className="hidden xl:flex items-center gap-2 p-2.5 rounded-lg bg-cyan-500/5 border border-cyan-500/15 text-[10px] text-cyan-400/70">
                    <Waves className="w-3 h-3 shrink-0" />
                    Flow Rate is pinned above — adjust it there to keep it visible while browsing other tabs.
                  </div>
                  <S label="Influent TSS" value={params.tss} min={50} max={2000} step={10} unit="mg/L" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, tss: v })} />
                  <S label="Influent O&G" value={params.og} min={10} max={500} step={5} unit="mg/L" icon={Droplets} color="#f59e0b" onChange={(v: number) => setParams({ ...params, og: v })} />
                  <S label="Influent BOD" value={params.bod} min={100} max={5000} step={50} unit="mg/L" icon={BarChart3} color="#f97316" onChange={(v: number) => setParams({ ...params, bod: v })} info="Biochemical Oxygen Demand" />
                  <S label="Influent COD" value={params.cod} min={200} max={8000} step={50} unit="mg/L" icon={BarChart3} color="#f97316" onChange={(v: number) => setParams({ ...params, cod: v })} info="Chemical Oxygen Demand" />
                  <S label="Influent pH" value={params.ph} min={4} max={9.5} step={0.1} unit="" icon={Beaker} color="#a855f7" onChange={(v: number) => setParams({ ...params, ph: v })} />
                  <S label="Temperature" value={params.temp} min={5} max={35} step={1} unit="C" icon={Thermometer} color="#ef4444" onChange={(v: number) => setParams({ ...params, temp: v })} />
                  <S label="Alkalinity" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO3" icon={FlaskConical} color="#10b981" onChange={(v: number) => setParams({ ...params, alkalinity: v })} info="Buffers pH drop from coagulant hydrolysis" />
                  <S label="Turbidity" value={params.turbidity} min={10} max={2000} step={10} unit="NTU" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, turbidity: v })} info="Correlates with TSS; high turbidity reduces removal efficiency" />
                  <S label="Conductivity" value={params.conductivity} min={100} max={10000} step={50} unit="µS/cm" icon={Zap} color="#fbbf24" onChange={(v: number) => setParams({ ...params, conductivity: v })} info="Ionic strength affects coagulant efficiency" />
                  <S label="Hardness" value={params.hardness} min={10} max={1000} step={10} unit="mg/L CaCO3" icon={Droplets} color="#3b82f6" onChange={(v: number) => setParams({ ...params, hardness: v })} info="Total hardness affects scaling potential and coagulant demand" />
                  <S label="NH₃-N" value={params.nh3n} min={1} max={200} step={1} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, nh3n: v })} info="Ammonia nitrogen nutrient loading indicator" />
                  <S label="Total Phosphorus" value={params.tp} min={0.5} max={50} step={0.5} unit="mg/L" icon={FlaskConical} color="#ec4899" onChange={(v: number) => setParams({ ...params, tp: v })} info="Total phosphorus nutrient loading" />
                </div>
              )}
              {activeTab === 'chemistry' && (
                <div className="space-y-4">
                  <div className="p-3 rb-inner-card-sm">
                    <p className="text-[10px] text-slate-500 uppercase font-bold mb-2">Coagulant Type</p>
                    <div className="flex gap-2 flex-wrap">
                      {(['ferric', 'alum', 'pac', 'none'] as const).map((t) => (
                        <button key={t} onClick={() => setParams({ ...params, coagulantType: t })} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${params.coagulantType === t ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'}`}>
                          {t === 'ferric' ? 'FeCl3' : t === 'alum' ? 'Al2(SO4)3' : t === 'pac' ? 'PAC' : 'None'}
                        </button>
                      ))}
                    </div>
                    {params.coagulantType !== 'none' && (
                      <div className="mt-2 text-[10px] text-slate-400 font-mono bg-rb-surface rounded p-2">
                        {params.coagulantType === 'ferric' ? 'FeCl3 + 3H2O = Fe(OH)3 + 3HCl' : params.coagulantType === 'alum' ? 'Al3+ + 3H2O = Al(OH)3 + 3H+' : 'PAC = Al(OH)3 (pre-hydrolysed)'}
                      </div>
                    )}
                  </div>
                  <S label="Coagulant Dose" value={params.coagulantDose} min={0} max={100} step={1} unit="mg/L" icon={FlaskConical} color="#D32F2F" onChange={(v: number) => setParams({ ...params, coagulantDose: v })} />
                  <S label="Polymer Dose" value={params.flocculantDose} min={0} max={5} step={0.1} unit="mg/L" icon={FlaskConicalOff} color="#7B1FA2" onChange={(v: number) => setParams({ ...params, flocculantDose: v })} />
                  <div className="p-3 space-y-2 rb-inner-card-sm">
                    <p className="text-[10px] text-slate-500 uppercase font-bold">Live Chemistry Readout</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">pH drop:</span> <span className="text-amber-400 font-bold ml-1">{fmt(size.phDrop, 3)}</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Effluent pH:</span> <span className="text-emerald-400 font-bold ml-1">{fmt(size.effluentPH, 2)}</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Coag demand:</span> <span className="text-amber-400 font-bold ml-1">{fmt(size.coagulantDemand, 1)} kg/d</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Precipitate:</span> <span className="text-amber-400 font-bold ml-1">{fmt(size.coagPrecipitate, 1)} kg/d</span></div>
                      <div className={`bg-rb-surface rounded p-2 ${size.alkalinityConsumed > params.alkalinity * 0.7 ? 'border border-amber-500/30' : ''}`}>
                        <span className="text-slate-500">Alk consumed:</span>
                        <span className="text-amber-400 font-bold ml-1">{fmt(size.alkalinityConsumed, 0)} mg/L</span>
                      </div>
                      <div className={`bg-rb-surface rounded p-2 ${size.residualAlkalinity < 50 ? 'border border-rose-500/30' : ''}`}>
                        <span className="text-slate-500">Residual alk:</span>
                        <span className={`font-bold ml-1 ${size.residualAlkalinity < 30 ? 'text-rose-400' : size.residualAlkalinity < 60 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          {fmt(size.residualAlkalinity, 0)} mg/L
                        </span>
                      </div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">BOD/COD:</span> <span className="text-sky-400 font-bold ml-1">{fmt(params.bod / Math.max(params.cod, 1), 2)}</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Conductivity:</span> <span className="text-sky-400 font-bold ml-1">{fmt(params.conductivity, 0)} µS/cm</span></div>
                    </div>
                    {/* Polymer charge compatibility banner */}
                    <div className={`rounded-lg p-2.5 border text-[10px] leading-relaxed ${
                      size.polymerChargeMatch === 'optimal'    ? 'bg-emerald-500/8 border-emerald-500/25 text-emerald-400' :
                      size.polymerChargeMatch === 'good'       ? 'bg-sky-500/8 border-sky-500/25 text-sky-400' :
                      size.polymerChargeMatch === 'acceptable' ? 'bg-amber-500/8 border-amber-500/25 text-amber-400' :
                      'bg-rose-500/8 border-rose-500/25 text-rose-400'
                    }`}>
                      <p className="font-bold uppercase text-[10px] tracking-wider mb-0.5">
                        Polymer compatibility: {size.polymerChargeMatch}
                      </p>
                      <p className="opacity-80">{size.polymerChargeNote}</p>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'air' && (
                <div className="space-y-4">
                  <S label="Recycle Ratio" value={params.recycleRate} min={10} max={50} step={1} unit="%" icon={ArrowRightLeft} color="#0288D1" onChange={(v: number) => setParams({ ...params, recycleRate: v })} />
                  <S label="Saturator Pressure" value={params.saturatorPressure} min={3} max={7} step={0.1} unit="bar" icon={Gauge} color="#0288D1" onChange={(v: number) => setParams({ ...params, saturatorPressure: v })} />
                  <S label="Saturator Efficiency" value={params.saturatorEfficiency} min={70} max={95} step={1} unit="%" icon={CheckCircle2} color="#0288D1" onChange={(v: number) => setParams({ ...params, saturatorEfficiency: v })} />
                  <div className="p-3 space-y-2 rb-inner-card-sm">
                    <p className="text-[10px] text-slate-500 uppercase font-bold">Live Air Readout</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Air solubility:</span> <span className="text-sky-400 font-bold">{fmt(size.airSolubility, 1)} mg/L</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Air mass:</span> <span className="text-sky-400 font-bold">{fmt(size.airDemand / 24, 2)} kg/h</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">A/S ratio:</span> <span className="font-bold" style={{ color: asColor }}>{fmt(size.airToSolids, 4)}</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">A/S status:</span> <span className="font-bold" style={{ color: asColor }}>{size.asStatus}</span></div>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'hydraulics' && (
                <div className="space-y-4">
                  <S label="Tank Length" value={params.length} min={2} max={15} step={0.1} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, length: v })} />
                  <S label="Tank Width" value={params.width} min={1} max={8} step={0.1} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, width: v })} />
                  <S label="Tank Depth" value={params.depth} min={1} max={5} step={0.1} unit="m" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, depth: v })} />
                  <S label="Freeboard" value={params.freeboard} min={0.3} max={1.5} step={0.1} unit="m" icon={Volume2} color="#06b6d4" onChange={(v: number) => setParams({ ...params, freeboard: v })} />
                  <S label="Floc Tank Volume" value={params.flocTankVolume} min={2} max={30} step={0.5} unit="m³" icon={Cylinder} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, flocTankVolume: v })} />
                  <S label="Floc Compartments" value={params.flocNumCompartments} min={2} max={4} step={1} unit="" icon={Beaker} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, flocNumCompartments: v })} />
                  <S label="Floc Retention" value={params.flocRetentionTime} min={5} max={30} step={1} unit="min" icon={Timer} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, flocRetentionTime: v })} />
                  {/* Impeller Type Selector */}
                  <div className="p-3 rb-inner-card-sm">
                    <p className="text-[10px] text-slate-500 uppercase font-bold mb-2 flex items-center gap-1.5">
                      <CircleDot className="w-3 h-3" />Impeller Type
                      <span className="text-[10px] text-slate-600 normal-case font-normal ml-1">→ sets Power Number (Np) for P = Np·ρ·N³·D⁵</span>
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {([
                        { key: 'paddle',    label: 'Flat Paddle',    np: '1.70', tip: 'Most common for slow flocculation. Low RPM (20–60). Gentle mixing, wide blades.' },
                        { key: 'hydrofoil', label: 'Hydrofoil',      np: '0.31', tip: 'Low-drag aerofoil blade. High flow, low shear. Excellent for gentle floc building.' },
                        { key: 'pitched',   label: 'Pitched 45°',    np: '1.27', tip: 'Pitched-blade turbine. Axial flow, good top-to-bottom mixing in deep tanks.' },
                        { key: 'rushton',   label: 'Rushton Disc',   np: '5.90', tip: 'High-shear radial flow turbine. Typically used for gas dispersion, NOT flocculation.' },
                      ] as const).map(({ key, label, np, tip }) => (
                        <button
                          key={key}
                          title={tip}
                          onClick={() => updateFlocMixer('flocImpellerType', key)}
                          className={`px-2.5 py-2 rounded-lg text-left transition-all border ${
                            params.flocImpellerType === key
                              ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                              : 'bg-slate-800/60 border-slate-800/40 text-slate-400 hover:border-slate-600'
                          }`}
                        >
                          <p className="text-[10px] font-bold">{label}</p>
                          <p className="text-[10px] text-slate-500">Np = {np}</p>
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-600 mt-2 leading-tight">
                      Np is regime-dependent — actual value adjusts with Re. Turbulent: constant. Transitional: Np × (10000/Re)^0.14. Laminar: Kp/Re.
                    </p>
                  </div>
                  <S label="Mixer Speed (RPM)" value={params.flocMotorSpeed} min={5} max={150} step={1} unit="rpm" icon={Wind} color="#8b5cf6" onChange={(v: number) => updateFlocMixer('flocMotorSpeed', v)} info="Shaft speed. Power ∝ N³ — halving RPM reduces power 8×. Target G = 20–75 s⁻¹ for pre-DAF flocculation." />
                  <S label="Impeller Diameter" value={params.flocImpellerDia} min={0.2} max={2.5} step={0.05} unit="m" icon={CircleDot} color="#8b5cf6" onChange={(v: number) => updateFlocMixer('flocImpellerDia', v)} info="Diameter has 5th-power effect on power. Doubling D increases power 32×. Ensure D_imp/D_tank = 0.5–0.8." />
                  <div className="p-3 space-y-2 rb-inner-card-sm">
                    <p className="text-[10px] text-slate-500 uppercase font-bold flex items-center gap-1.5">
                      Live Flocculation Physics
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-auto ${
                        size.flocRegime === 'turbulent' ? 'bg-emerald-500/15 text-emerald-400' :
                        size.flocRegime === 'transitional' ? 'bg-amber-500/15 text-amber-400' :
                        'bg-rose-500/15 text-rose-400'
                      }`}>{size.flocRegime}</span>
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-rb-surface rounded p-2">
                        <span className="text-slate-500">G-value:</span>
                        <span className={`font-bold ml-1 ${size.flocGValue > 100 ? 'text-rose-400' : size.flocGValue < 20 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          {fmt(size.flocGValue, 0)} s⁻¹
                        </span>
                        <span className="text-[10px] text-slate-600 ml-1">(20–75)</span>
                      </div>
                      <div className="bg-rb-surface rounded p-2">
                        <span className="text-slate-500">G×t:</span>
                        <span className={`font-bold ml-1 ${size.flocGt < 20000 ? 'text-amber-400' : size.flocGt > 150000 ? 'text-rose-400' : 'text-emerald-400'}`}>
                          {Math.round(size.flocGt).toLocaleString()}
                        </span>
                        <span className="text-[10px] text-slate-600 ml-1">(20k–80k)</span>
                      </div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Tip speed:</span> <span className={`font-bold ml-1 ${size.flocTipSpeed > 5 ? 'text-rose-400' : 'text-emerald-400'}`}>{fmt(size.flocTipSpeed, 2)} m/s</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Re_imp:</span> <span className={`font-bold ml-1 ${size.flocReynolds < 10000 ? 'text-amber-400' : 'text-emerald-400'}`}>{Math.round(size.flocReynolds).toLocaleString()}</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Np used:</span> <span className="text-sky-400 font-bold ml-1">{fmt(size.flocNpUsed, 3)}</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Power:</span> <span className="text-sky-400 font-bold ml-1">{fmt(size.flocPower, 2)} kW</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Torque:</span> <span className="text-sky-400 font-bold ml-1">{fmt(size.flocMotorTorque, 1)} Nm</span></div>
                      <div className="bg-rb-surface rounded p-2"><span className="text-slate-500">Retention:</span> <span className="text-sky-400 font-bold ml-1">{fmt(size.flocRetentionTime, 1)} min</span></div>
                      <div className={`bg-rb-surface rounded p-2 col-span-2 border ${
                        size.flocEffFactor >= 1.0 ? 'border-emerald-500/20' :
                        size.flocEffFactor >= 0.90 ? 'border-amber-500/20' : 'border-rose-500/20'
                      }`}>
                        <span className="text-slate-500">Floc efficiency factor:</span>
                        <span className={`font-bold ml-1 ${size.flocEffFactor >= 1.0 ? 'text-emerald-400' : size.flocEffFactor >= 0.90 ? 'text-amber-400' : 'text-rose-400'}`}>
                          {(size.flocEffFactor * 100).toFixed(0)}%
                        </span>
                        <span className="text-[10px] text-slate-600 ml-1">applied to all removal fractions</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'pumps' && (
                <div className="space-y-4">
                  <div className="p-4 rb-inner-card-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-bold text-white flex items-center gap-2">
                          <Cog className="w-4 h-4 text-sky-400" />DAF Pump Selection
                        </h3>
                        <p className="text-xs text-slate-400 mt-1">Select a DAF white water pump based on your system requirements.</p>
                      </div>
                      <button onClick={() => navigate('/pumps')} className="px-4 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-medium border border-sky-500/20 hover:bg-sky-500/20 transition-colors flex items-center gap-2">
                        Browse Pump Database <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    {params.selectedPumpId ? (
                      <div className={`rounded-lg p-3 border mb-4 ${
                        requiredRecycleFlowM3h > (params.selectedPumpMaxFlowM3h || 0) * 1.1 || requiredPressureBar > (params.selectedPumpMaxPressureBar || 0) * 1.05
                          ? 'bg-rose-500/5 border-rose-500/20'
                          : requiredRecycleFlowM3h < (params.selectedPumpMaxFlowM3h || 0) * 0.35
                          ? 'bg-amber-500/5 border-amber-500/20'
                          : 'bg-emerald-500/5 border-emerald-500/20'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                              requiredRecycleFlowM3h > (params.selectedPumpMaxFlowM3h || 0) * 1.1 || requiredPressureBar > (params.selectedPumpMaxPressureBar || 0) * 1.05
                                ? 'bg-rose-400' : requiredRecycleFlowM3h < (params.selectedPumpMaxFlowM3h || 0) * 0.35 ? 'bg-amber-400' : 'bg-emerald-400'
                            }`} />
                            <span className="text-sm font-bold text-white">{params.selectedPumpCode}</span>
                            <span className="text-[10px] text-slate-400">{params.selectedPumpMaxFlowM3h} m³/h / {params.selectedPumpMaxPressureBar} bar / {params.selectedPumpMotorKw} kW</span>
                          </div>
                          <button onClick={() => navigate('/pumps')} className="text-[10px] text-sky-400 hover:underline">Change Pump</button>
                        </div>
                        <p className={`text-[10px] mt-1 ${
                          requiredRecycleFlowM3h > (params.selectedPumpMaxFlowM3h || 0) * 1.1 || requiredPressureBar > (params.selectedPumpMaxPressureBar || 0) * 1.05
                            ? 'text-rose-400' : requiredRecycleFlowM3h < (params.selectedPumpMaxFlowM3h || 0) * 0.35 ? 'text-amber-400' : 'text-emerald-400'
                        }`}>
                          {requiredRecycleFlowM3h > (params.selectedPumpMaxFlowM3h || 0) * 1.1
                            ? `Pump too small - required ${fmt(requiredRecycleFlowM3h, 1)} m³/h exceeds max ${params.selectedPumpMaxFlowM3h} m³/h`
                            : requiredPressureBar > (params.selectedPumpMaxPressureBar || 0) * 1.05
                            ? `Pressure insufficient - required ${requiredPressureBar} bar exceeds max ${params.selectedPumpMaxPressureBar} bar`
                            : requiredRecycleFlowM3h < (params.selectedPumpMaxFlowM3h || 0) * 0.35
                            ? `Pump may be oversized - required ${fmt(requiredRecycleFlowM3h, 1)} m³/h is well below max ${params.selectedPumpMaxFlowM3h} m³/h`
                            : 'Pump matches current requirements'}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-3 mb-4 flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-rose-400" />
                        <span className="text-sm text-rose-400">No pump selected. Browse the database to choose a pump.</span>
                      </div>
                    )}
                    {requiredRecycleFlowM3h > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-bold text-white flex items-center gap-2">
                            <TrendingUp className="w-3.5 h-3.5 text-sky-400" />Recommended Pumps
                          </h4>
                          <div className="flex items-center gap-2">
                            {!params.selectedPumpId && (
                              <button onClick={handleAutoSelect} disabled={recsLoading || recommendedPumps.length === 0} className="px-2.5 py-1 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                                Auto-select best
                              </button>
                            )}
                            <button onClick={() => navigate('/pumps')} className="text-[10px] text-sky-400 hover:underline">View all pumps</button>
                          </div>
                        </div>
                        {recsLoading ? (
                          <div className="flex gap-2">{Array.from({ length: 3 }).map((_, i) => (<div key={i} className="flex-1 h-20 bg-slate-800/40 rounded-lg animate-pulse" />))}</div>
                        ) : recommendedPumps.length === 0 ? (
                          <p className="text-[10px] text-slate-500">Enter flow and recycle rate to see recommendations.</p>
                        ) : (
                          <div className="grid grid-cols-1 gap-2">
                            {recommendedPumps.map((pump) => {
                              const colors = verdictColors[pump.verdict];
                              const isSel = params.selectedPumpId === pump.id;
                              return (
                                <div key={pump.id} className={`rounded-lg border p-2.5 transition-all ${isSel ? 'bg-yellow-500/5 border-yellow-500/30 ring-1 ring-yellow-500/20' : 'bg-slate-800/40 border-slate-800/50 hover:border-sky-500/30 hover:bg-slate-800/60'}`}>
                                  <div className="flex items-start justify-between">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                                        <span className="text-xs font-bold text-white truncate">{pump.code}</span>
                                      </div>
                                      <p className="text-[10px] text-slate-400 truncate">{pump.manufacturerName} / {pump.seriesName}</p>
                                    </div>
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>{pump.score}</span>
                                  </div>
                                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
                                    <span>{pump.maxFlowM3h} m³/h</span>
                                    <span>{pump.maxPressureBar} bar</span>
                                    {pump.motorKw && <span>{pump.motorKw} kW</span>}
                                  </div>
                                  <p className="text-[10px] text-slate-500 mt-1 leading-tight">{pump.notes}</p>
                                  <button onClick={() => handleSelectPump(pump)} disabled={isSel} className={`mt-1.5 w-full py-1 rounded text-[10px] font-medium transition-colors ${isSel ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 cursor-default' : 'bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20'}`}>
                                    {isSel ? 'Selected' : 'Select'}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="p-3 rb-card-sm">
                        <p className="text-[10px] text-slate-500 uppercase font-bold mb-2">Recycle Flow Required</p>
                        <p className="text-lg font-bold text-white">{fmt(requiredRecycleFlowM3h, 1)} <span className="text-sm text-slate-400">m³/h</span></p>
                        <p className="text-xs text-slate-500">{fmt((params.flowRate * params.recycleRate / 100) * 4.403, 0)} GPM @ {params.recycleRate}% recycle</p>
                      </div>
                      <div className="p-3 rb-card-sm">
                        <p className="text-[10px] text-slate-500 uppercase font-bold mb-2">Pressure Required</p>
                        <p className="text-lg font-bold text-white">{params.saturatorPressure} <span className="text-sm text-slate-400">bar</span></p>
                        <p className="text-xs text-slate-500">{fmt(params.saturatorPressure * 14.504, 1)} psi saturator pressure</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'scraper' && (
                <div className="space-y-4">
                  {/* Status banner */}
                  <div className={`rounded-lg p-3 border flex items-start gap-3 ${
                    size.scraperStatus === 'optimal' ? 'bg-emerald-500/5 border-emerald-500/20' :
                    size.scraperStatus === 'adequate' ? 'bg-sky-500/5 border-sky-500/20' :
                    size.scraperStatus === 'marginal' ? 'bg-amber-500/5 border-amber-500/20' :
                    'bg-rose-500/5 border-rose-500/20'
                  }`}>
                    <div className={`w-2.5 h-2.5 rounded-full mt-0.5 shrink-0 ${
                      size.scraperStatus === 'optimal' ? 'bg-emerald-400' :
                      size.scraperStatus === 'adequate' ? 'bg-sky-400' :
                      size.scraperStatus === 'marginal' ? 'bg-amber-400' : 'bg-rose-400'
                    }`} />
                    <div>
                      <p className={`text-xs font-bold uppercase tracking-wider ${
                        size.scraperStatus === 'optimal' ? 'text-emerald-400' :
                        size.scraperStatus === 'adequate' ? 'text-sky-400' :
                        size.scraperStatus === 'marginal' ? 'text-amber-400' : 'text-rose-400'
                      }`}>{size.scraperStatus.toUpperCase()} — Chain-&-Flight Scraper System</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{size.scraperStatusDetail}</p>
                    </div>
                  </div>

                  {/* Key metrics grid */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Scraper Flights', value: size.scraperCount.toString(), unit: 'flights', sub: `1 per 1.5 m (L=${fmt(params.length,1)} m)`, color: '#06b6d4' },
                      { label: 'Travel Speed', value: fmt(size.scraperSpeedMpm, 2), unit: 'm/min', sub: 'physics-constrained 0.15–0.60', color: '#10b981' },
                      { label: 'Passes per Hour', value: fmt(size.scraperPassesPerHour, 1), unit: '/h', sub: `speed × 60 / L`, color: '#10b981' },
                      { label: 'Pass Interval', value: fmt(size.scraperFrequencyMin, 1), unit: 'min', sub: '60 / passes_per_hour', color: '#f59e0b' },
                      { label: 'Operating Blanket', value: fmt(size.operatingBlanket_mm, 0), unit: 'mm', sub: 'steady-state balance depth', color: size.operatingBlanket_mm > 150 ? '#ef4444' : size.operatingBlanket_mm > 80 ? '#f59e0b' : '#10b981' },
                      { label: 'Blade Depth', value: fmt(size.scraperBladeDepth_mm, 0), unit: 'mm', sub: 'blanket × 2 + 25 mm', color: '#06b6d4' },
                    ].map((m) => (
                      <div key={m.label} className="p-3 rb-inner-card-sm">
                        <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">{m.label}</p>
                        <p className="text-lg font-bold leading-tight" style={{ color: m.color }}>
                          {m.value} <span className="text-[10px] font-normal text-slate-500">{m.unit}</span>
                        </p>
                        <p className="text-[10px] text-slate-600 font-mono mt-0.5">{m.sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* Float characteristics */}
                  <div className="p-3 space-y-2 rb-inner-card-sm">
                    <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Float Characterisation</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: 'Float DS', value: fmt(size.floatDS_pct, 1), unit: '%', info: 'coag/poly/HLR factors' },
                        { label: 'Float Density', value: fmt(size.floatDensity_kg_m3, 0), unit: 'kg/m³', info: `${(((1 - size.floatDensity_kg_m3 / 998) * 100).toFixed(1))}% air void` },
                        { label: 'Float Volume', value: fmt(size.floatVol_m3_h, 3), unit: 'm³/h', info: 'wet float produced' },
                        { label: 'Accum Rate', value: fmt(size.blanketAccumRate_mm_h, 1), unit: 'mm/h', info: 'blanket build-up rate' },
                      ].map((m) => (
                        <div key={m.label} className="bg-rb-surface rounded p-2 border border-slate-800/60">
                          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{m.label}</p>
                          <p className="text-sm font-bold text-white">{m.value} <span className="text-[10px] font-normal text-slate-400">{m.unit}</span></p>
                          <p className="text-[10px] text-slate-600 font-mono">{m.info}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Motor power */}
                  <div className="p-3 rb-inner-card-sm">
                    <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-2">Scraper Drive Motor</p>
                    <div className="flex items-end gap-3">
                      <div>
                        <p className="text-2xl font-bold text-sky-400">{fmt(size.scraperMotorPower_kW, 2)} <span className="text-sm text-slate-400">kW</span></p>
                        <p className="text-[10px] text-slate-500 font-mono">F_drag × C_D=1.8 × ρ_float × v² × A_blade × n / η_chain(0.65)</p>
                      </div>
                      <div className="ml-auto text-right">
                        <p className="text-[10px] text-slate-400">At {fmt(size.scraperSpeedMpm, 2)} m/min</p>
                        <p className="text-[10px] text-slate-400">{size.scraperCount} × {fmt(size.scraperBladeDepth_mm / 1000, 3)} m blade</p>
                        <p className="text-[10px] text-slate-400">ρ_float = {fmt(size.floatDensity_kg_m3, 0)} kg/m³</p>
                      </div>
                    </div>
                    <div className="mt-3 font-mono text-[10px] text-slate-600 bg-rb-surface rounded p-2 leading-relaxed">
                      F = 1.8 × {fmt(size.floatDensity_kg_m3, 0)} × {fmt(size.scraperSpeedMpm/60, 5)}² × ({fmt(size.scraperBladeDepth_mm/1000,3)}×{fmt(params.width,1)}) × {size.scraperCount} × 1.5
                      {' '}&nbsp;&nbsp;P = F × v / 0.65
                    </div>
                  </div>

                  {/* Physics notes */}
                  <div className="p-3 space-y-2 rb-inner-card-sm">
                    <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Scraper Physics Notes</p>
                    <div className="space-y-1.5 text-[11px] text-slate-400 leading-relaxed">
                      <p>• <span className="text-slate-300 font-semibold">Chain-and-flight</span> system spans full DAF width; flights spaced 1.5 m along the chain.</p>
                      <p>• <span className="text-slate-300 font-semibold">Speed</span> based on float accumulation rate, A/S ratio, and O&G content — higher values drive faster clearing.</p>
                      <p>• <span className="text-slate-300 font-semibold">Operating blanket</span> = steady-state depth where removal rate (η=87%) equals accumulation. Target ≤80 mm.</p>
                      <p>• <span className="text-slate-300 font-semibold">Blade depth</span> = 2 × operating blanket + 25 mm clearance. Clamped 50–250 mm.</p>
                      <p>• <span className="text-slate-300 font-semibold">Motor power</span>: flat-plate drag model (C_D=1.8, Hoerner 1965) × 1.5 friction/inertia × η_chain=0.65.</p>
                      <p>• <span className="text-slate-300 font-semibold">Float DS%</span>: base 3% adjusted for coagulant precipitate, polymer gel network, and HLR compaction time.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ─── RIGHT: FLOW DIAGRAM + LIVE PHYSICS ─── */}
        <div className="xl:col-span-7 space-y-4">
          <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 uppercase tracking-wider">
            <Microscope className="w-3.5 h-3.5 text-emerald-400" />P&ID Process Flow Diagram
          </h3>
          <FlowDiagram params={params} size={size} />

          {/* LIVE PROCESS PHYSICS */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 uppercase tracking-wider">
              <FlaskIcon className="w-3.5 h-3.5 text-sky-400" />Live Process Physics
              <span className="ml-auto flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 rb-pulse-dot" />
                <span className="text-[10px] text-sky-500 font-normal normal-case tracking-normal">Live</span>
              </span>
            </h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {[
                { label: 'Bubble Count', value: Math.round(size.bubbleCount).toString(), unit: 'bubbles', sub: 'A/S ratio', color: '#06b6d4' },
                { label: 'Bubble Diameter', value: fmt(size.bubbleSize, 0), unit: 'um', sub: "Stokes law", color: '#06b6d4' },
                { label: 'Rise Velocity', value: fmt(size.bubbleRise, 1), unit: 'm/h', sub: 'v=g(rw-ra)d2/18u', color: '#06b6d4' },
                { label: 'Blanket Thickness', value: fmt(size.blanketThickness, 0), unit: 'mm', sub: size.blanketDensity, color: '#f59e0b' },
                { label: 'Coag Demand', value: fmt(size.coagulantDemand, 1), unit: 'kg/d', sub: params.coagulantType !== 'none' ? `${params.coagulantType} @ ${params.coagulantDose} mg/L` : 'No coagulant', color: '#f43f5e' },
                { label: 'Air Demand', value: fmt(size.airDemand, 1), unit: 'kg/d', sub: "Henry law + van t Hoff", color: '#22d3ee' },
              ].map((item) => (
                <div key={item.label} className="rb-metric-card p-2.5 space-y-0.5">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">{item.label}</p>
                  <p className="text-base font-bold leading-tight" style={{ color: item.color }}>
                    {item.value} <span className="text-[10px] font-normal text-slate-500">{item.unit}</span>
                  </p>
                  <p className="text-[10px] text-slate-600 leading-tight font-mono">{item.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═════ RESULTS & PERFORMANCE (full-width) ═════ */}
      <div className="p-4 space-y-4 rb-card">
        <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 uppercase tracking-wider">
          <BarChart3 className="w-3.5 h-3.5 text-emerald-400" />Results & Performance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-gradient-to-br from-emerald-950/40 to-rb-elevated rounded-xl p-3 border border-emerald-900/30 space-y-2">
            <p className="text-[10px] text-emerald-600 uppercase font-bold tracking-wider">Effluent Quality</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">TSS</span><span className="text-white font-bold">{fmt(params.tss, 0)} to {fmt(size.effluentTSS, 0)} mg/L</span></div>
              <div className="flex justify-between"><span className="text-slate-400">TSS removal</span><span className="text-emerald-400 font-bold">{fmt(size.tssRemoval * 100, 0)}%</span></div>
              <div className="flex justify-between"><span className="text-slate-400">O&G</span><span className="text-white font-bold">{fmt(params.og, 0)} to {fmt(size.effluentOG, 0)} mg/L</span></div>
              <div className="flex justify-between"><span className="text-slate-400">O&G removal</span><span className="text-emerald-400 font-bold">{fmt(size.ogRemoval * 100, 0)}%</span></div>
              <div className="flex justify-between"><span className="text-slate-400">BOD</span><span className="text-white font-bold">{fmt(params.bod, 0)} to {fmt(size.effluentBOD, 0)} mg/L</span></div>
              <div className="flex justify-between"><span className="text-slate-400">BOD removal</span><span className="text-emerald-400 font-bold">{fmt(size.bodRemoval * 100, 0)}%</span></div>
              <div className="flex justify-between"><span className="text-slate-400">COD</span><span className="text-white font-bold">{fmt(params.cod, 0)} to {fmt(size.effluentCOD, 0)} mg/L</span></div>
              <div className="flex justify-between"><span className="text-slate-400">COD removal</span><span className="text-emerald-400 font-bold">{fmt(size.codRemoval * 100, 0)}%</span></div>
              <div className="flex justify-between"><span className="text-slate-400">pH</span><span className="text-white font-bold">{fmt(params.ph, 1)} to {fmt(size.effluentPH, 1)}</span></div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-amber-950/30 to-rb-elevated rounded-xl p-3 border border-amber-900/20 space-y-2">
            <p className="text-[10px] text-amber-600 uppercase font-bold tracking-wider">Sludge Production</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">Captured TSS</span><span className="text-amber-400 font-bold">{fmt(size.sludgeTSS, 1)} kg/d</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Captured O&G</span><span className="text-amber-400 font-bold">{fmt(size.sludgeOG, 1)} kg/d</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Captured BOD</span><span className="text-amber-400 font-bold">{fmt(size.sludgeBOD, 1)} kg/d</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Captured COD</span><span className="text-amber-400 font-bold">{fmt(size.sludgeCOD, 1)} kg/d</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Chemical precip</span><span className="text-amber-400 font-bold">{fmt(size.sludgeChem, 1)} kg/d</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Total sludge</span><span className="text-emerald-400 font-bold">{fmt(size.sludgeTotal, 1)} kg/d</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Wet volume</span><span className="text-emerald-400 font-bold">{fmt(size.sludgeVolume, 1)} m³/d</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Blanket</span><span className="text-amber-400 font-bold">{fmt(size.blanketThickness, 0)} mm ({size.blanketDensity})</span></div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-sky-950/30 to-rb-elevated rounded-xl p-3 border border-sky-900/20 space-y-2">
            <p className="text-[10px] text-sky-600 uppercase font-bold tracking-wider">Power Summary</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">Recycle pump</span><span className="text-sky-400 font-bold">{fmt(size.pumpPower, 2)} kW</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Air compressor</span><span className="text-sky-400 font-bold">{fmt(size.compressorPower, 2)} kW</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Floc mixer</span><span className="text-sky-400 font-bold">{fmt(size.mixerPower, 2)} kW</span></div>
              <div className="border-t border-slate-700 pt-1 flex justify-between"><span className="text-slate-300 font-bold">Total</span><span className="text-emerald-400 font-bold">{fmt(size.power, 2)} kW</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* ═════ DESIGN NOTES & WARNINGS (full-width) ═════ */}
      <div className="p-4 space-y-3 rb-card">
        <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 uppercase tracking-wider">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />Design Notes & Warnings
        </h3>
        <div className="space-y-1.5">
          {size.designNotes.map((note, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`text-xs p-2.5 rounded-lg leading-relaxed ${
                note.startsWith('⚠️')
                  ? 'bg-rose-500/5 border border-rose-500/20 text-rose-400'
                  : note.startsWith('ℹ️')
                  ? 'bg-sky-500/5 border border-sky-500/20 text-sky-400'
                  : 'bg-emerald-500/5 border border-emerald-500/20 text-emerald-400'
              }`}
            >
              {note}
            </motion.div>
          ))}
        </div>
      </div>

      {/* ═════ SCIENCE VALIDATION ACCORDION ═════ */}
      <ScienceAccordion params={params} size={size} />

      {/* ═════ INLINE VALIDATION REPORT MODAL ═════ */}
      <DAFValidationModal
        isOpen={showValidationReport}
        onClose={() => { setShowValidationReport(false); setShowSavePrompt(false); }}
        savedParams={
          // If we validated without saving, use live params; otherwise use localStorage snapshot
          showValidationReport
            ? (hasSavedConfig
                ? (() => { try { return JSON.parse(localStorage.getItem('daf-params-v2') || 'null'); } catch { return null; } })() ?? params
                : params)
            : null
        }
      />

    </div>
  );
}
